import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { VehicleRowList } from './components/VehicleRowList'
import { MarcheVehiclePanel } from './components/MarcheVehiclePanel'
import { StockVehiclePanel, formatVehicleDisplayTitle } from './components/StockVehiclePanel'
import { ConcurrenceDetailPanel } from './components/ConcurrenceDetailPanel'
import { DashboardToday } from './components/DashboardToday'
import { ActionQueue } from './components/ActionQueue'
import { LeasingPage } from './components/LeasingPage'
import { AnalyseTab } from './components/AnalyseTab'
import { PublicLeasingProposalPage } from './components/PublicLeasingProposalPage'
import { LandingPage } from './components/LandingPage'
import { computeStockHealthMetrics } from './utils/stockHealthMetrics'
import { buildStockHealthActions } from './utils/stockHealthActions'
import { cotationReferenceLabel } from './utils/cotationReference'
import { fetchWithTimeout } from './utils/fetchWithTimeout'
import { formatRecentDropLine, hasRecentPriceDropChf, recentDropChfFromHistoriqueRows } from './utils/recentPriceDrop'
import { applyClusterJitter } from './utils/scatterJitter'
import { filterScatterMarketPoints } from './utils/scatterDedup'
import { getAnnonceCompletenessIssues, shortIncompleteLabel } from './utils/incompleteListing'
import { useBackToClose } from './hooks/useBackToClose'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts'
import {
  BookmarkPlus,
  Calculator,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  FileDown,
  Flame,
  History,
  Hourglass,
  Link2,
  LineChart,
  Lock,
  Mail,
  Menu,
  Package,
  Settings,
  SlidersHorizontal,
  Store,
  X,
  Zap,
} from 'lucide-react'

import { BACKEND_URL as BACKEND } from './config/backendUrl'
const STOCK_DEV_LIMIT = Math.max(0, parseInt(String(process.env.REACT_APP_STOCK_DEV_LIMIT || '150'), 10) || 0)
// PERF FIX 2026-04 : par défaut on désactive le mode fast pour garder ecart_pct calculé.
const STOCK_DEV_FAST_MODE = String(process.env.REACT_APP_STOCK_DEV_FAST_MODE || '0') === '1'
const COTATION_HISTORY_LS_KEY = 'radar_cotation_history_v1'
// Bump à chaque changement de format ou de logique côté backend qui peut
// rendre obsolètes les snapshots stockés (ex. nouvelle logique concurrence).
const PAGE_CACHE_VERSION = 'v6'
/** Onglets visibles mais non accessibles (bêta / en développement). */
const LOCKED_CHAT_TABS = new Set(['leasing', 'analyse'])

function buildPageCacheKey(page, token, scope = 'default', userId = '') {
  const tokenPart = String(token || '').slice(-16) || 'anon'
  const userPart = String(userId || 'nouser')
  return `radar_page_cache_${PAGE_CACHE_VERSION}_${page}_${scope}_${userPart}_${tokenPart}`
}

function readPageCache(cacheKey, ttlMs) {
  try {
    const raw = localStorage.getItem(cacheKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const ts = Number(parsed?.ts)
    if (!Number.isFinite(ts)) return null
    if (Date.now() - ts > ttlMs) return null
    return parsed?.payload ?? null
  } catch {
    return null
  }
}

function writePageCache(cacheKey, payload) {
  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        ts: Date.now(),
        payload,
      })
    )
  } catch {}
}

/** Vide les caches persistantes des listes (marché, stock, dashboard, etc.) pour forcer un rechargement frais. */
function invalidateRadarPageCaches() {
  try {
    const toRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('radar_page_cache_')) toRemove.push(key)
    }
    for (const key of toRemove) localStorage.removeItem(key)
  } catch {}
}

function loadCotationHistory() {
  try {
    const raw = localStorage.getItem(COTATION_HISTORY_LS_KEY)
    const arr = JSON.parse(raw || '[]')
    return Array.isArray(arr) ? arr : []
  } catch {
    return []
  }
}
const DEFAULT_PREFS = {
  frais_recond_bas: 200,
  frais_recond_moyen: 400,
  frais_recond_haut: 800,
  marge_cible_pct: 23,
  marge_min_pct: 18,
  seuil_stock_long: 90,
  rayon_concurrence_km: 120,
  leasing_taux_defaut: 4.9,
  leasing_plafond_lcc_pct: 12,
  leasing_plafond_lcc_updated_at: '2026-01-01',
  leasing_duree_defaut_mois: 36,
  leasing_km_annuels_defaut: 10000,
  leasing_acompte_defaut_pct: 20,
  leasing_risk_ai_enabled: true,
  leasing_commission_pct: 0,
  leasing_depreciation_fallback_table: [
    { category: 'Roadster', coef_year: -0.06, coef_km: -0.00004 },
    { category: 'Sport', coef_year: -0.07, coef_km: -0.00005 },
    { category: 'Trail', coef_year: -0.055, coef_km: -0.000035 },
    { category: 'Scooter', coef_year: -0.05, coef_km: -0.00003 },
    { category: 'Touring', coef_year: -0.058, coef_km: -0.000038 },
  ],
  leasing_partner: {
    name: 'Partenaire leasing (v2)',
    logo_url: '',
    conditions: 'Placeholder v2',
  },
}

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #121416;
    --bg-elevated: #171a1f;
    --surface: #1a1d21;
    --surface-alt: #20242a;
    --surface-contrast: #2a2f36;
    --border: rgba(226,232,240,0.12);
    --border-strong: rgba(226,232,240,0.2);
    --text: #f8fafc;
    --text-secondary: rgba(226,232,240,0.68);
    --text-tertiary: rgba(226,232,240,0.46);
    --muted: rgba(203,213,225,0.74);
    --accent: #001e50;
    --accent-soft: rgba(0,30,80,0.22);
    /* Chargements : bleu plus lisible que le VW plein sur fond sombre */
    --loading-spin: #95b8ec;
    --loading-dot: rgba(149, 184, 236, 0.9);
    --loading-ring-static: rgba(149, 184, 236, 0.52);
    --particle: #171a1f;
    --radius-sm: 4px;
    --radius-md: 6px;
  }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'Inter', 'Roboto', system-ui, sans-serif;
    min-height: 100vh;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  /* Fond landing : calque au-dessus des particules (sinon points noirs masquent tout) */
  .landing {
    position: relative;
    min-height: 100vh;
  }

  .landing-fx {
    position: fixed;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    overflow: hidden;
    background: var(--bg);
  }

  /* Variables pilotées par le curseur : --parallax-x/y px, --tilt-z */
  .landing-fx-dots {
    position: absolute;
    inset: -5%;
    background-image: radial-gradient(circle at center, rgba(255,255,255,0.2) 0.55px, transparent 1.35px);
    background-size: 28px 28px;
    opacity: 1;
    transform: translate3d(var(--parallax-x, 0px), var(--parallax-y, 0px), 0);
    will-change: transform;
  }

  .landing-fx-beam {
    position: absolute;
    top: -25%;
    left: 50%;
    width: 150%;
    max-width: 1600px;
    height: 70%;
    transform: translateX(-50%);
    background:
      radial-gradient(ellipse 75% 60% at 50% 0%, rgba(255,255,255,0.38), transparent 62%),
      radial-gradient(ellipse 55% 45% at 80% 8%, rgba(255,255,255,0.14), transparent 55%);
    animation: landing-beam 12s ease-in-out infinite alternate;
    opacity: 0.95;
  }

  @keyframes landing-beam {
    0% { opacity: 0.88; transform: translateX(-50%) scale(1); }
    100% { opacity: 1; transform: translateX(-50%) scale(1.04); }
  }

  .landing-fx-grid {
    position: absolute;
    left: 50%;
    bottom: -28%;
    width: 200%;
    height: 75%;
    margin-left: -100%;
    background-image:
      linear-gradient(rgba(255,255,255,0.16) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.16) 1px, transparent 1px);
    background-size: 44px 44px;
    transform-origin: 50% 100%;
    transform: perspective(360px) rotateX(58deg) translate3d(var(--parallax-x, 0px), 0, 0) rotateZ(var(--tilt-z, 0deg));
    mask-image: linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 55%, transparent 85%);
    -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 55%, transparent 85%);
    animation: landing-grid 18s linear infinite;
    opacity: 1;
    will-change: transform;
  }

  @keyframes landing-grid {
    0% { background-position: 0 0, 0 0; }
    100% { background-position: 0 44px, 44px 0; }
  }

  .landing-fx-vignette {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 85% 75% at 50% 35%, transparent 0%, rgba(0,0,0,0.42) 100%);
    pointer-events: none;
  }

  .landing-fx-noise {
    position: absolute;
    inset: 0;
    opacity: 0.12;
    mix-blend-mode: overlay;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 180px 180px;
  }

  .particles-layer {
    position: fixed;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    overflow: hidden;
    background: transparent;
  }

  .particles-layer.particles-under-fx {
    z-index: 0;
  }

  .particle {
    position: absolute;
    border-radius: 50%;
    background: var(--particle);
    box-shadow: 0 0 0 1px rgba(255,255,255,0.02);
    animation-name: particle-rise;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    will-change: transform, opacity;
  }

  @keyframes particle-rise {
    0% {
      transform: translate3d(0, 105vh, 0) scale(1);
      opacity: 0;
    }
    4% { opacity: var(--p-opacity, 0.2); }
    96% { opacity: var(--p-opacity, 0.2); }
    100% {
      transform: translate3d(var(--p-dx, 0), -8vh, 0) scale(0.6);
      opacity: 0;
    }
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes pulse-soft {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
  }

  @keyframes bounce-dot {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
    30% { transform: translateY(-5px); opacity: 0.9; }
  }

  @keyframes nav-in {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 2.5rem;
    background: rgba(5,5,5,0.85);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid var(--border);
    animation: nav-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .nav-logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
  }

  .nav-logo-banner {
    height: 22px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: -0.02em;
    color: var(--text);
  }

  .logo-icon {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    background: var(--surface);
    border: 1px solid var(--border-strong);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  .logo-icon-img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    display: block;
  }

  .nav-cta {
    padding: 9px 20px;
    border-radius: 6px;
    border: 1px solid var(--border-strong);
    background: var(--text);
    color: var(--bg);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
  }

  .nav-cta:hover {
    background: transparent;
    color: var(--text);
    border-color: var(--text);
  }

  .hero {
    position: relative;
    z-index: 3;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0;
    text-align: center;
  }
  .hero-main {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 6.25rem 2rem 1.5rem;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
    min-height: 0;
  }
  .hero-float-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 2;
  }
  .hero-float-dot {
    position: absolute;
    width: var(--dot-size, 4px);
    height: var(--dot-size, 4px);
    border-radius: 999px;
    background: rgba(255,255,255,0.26);
    box-shadow: 0 0 10px rgba(255,255,255,0.12);
    animation: hero-float var(--dot-duration, 10s) ease-in-out infinite;
    animation-delay: var(--dot-delay, 0s);
  }
  @keyframes hero-float {
    0%, 100% { transform: translate3d(0, 0, 0); opacity: 0.45; }
    50% { transform: translate3d(var(--dot-dx, 8px), var(--dot-dy, -16px), 0); opacity: 0.95; }
  }
  .hero-text {
    max-width: 640px;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .hero-text .hero-badge {
    margin-bottom: 1.5rem;
    margin-left: auto;
    margin-right: auto;
  }
  .hero-text .hero-sub {
    max-width: 34rem;
    margin-bottom: 1.75rem;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  .hero-text .hero-btns {
    justify-content: center;
  }
  .landing-marquee-strap {
    flex-shrink: 0;
    width: 100%;
    margin-top: auto;
    padding-top: clamp(2.5rem, 5vh, 4rem);
  }
  @media (max-width: 960px) {
    .hero-main {
      text-align: center;
      padding: 5.5rem 1.5rem 1.25rem;
      align-items: center;
    }
    .hero-text {
      max-width: none;
    }
    .hero-text .hero-badge {
      margin-left: auto;
      margin-right: auto;
    }
    .hero-text .hero-sub {
      margin-left: auto;
      margin-right: auto;
    }
    .hero-text .hero-btns {
      justify-content: center;
    }
    .hero-metrics {
      flex-direction: column;
      align-items: stretch;
      max-width: 20rem;
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    }
    .hero-metric {
      justify-content: center;
      white-space: normal;
      text-align: center;
    }
    .hero-metric-label {
      white-space: normal;
      line-height: 1.35;
    }
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid var(--border);
    background: var(--surface);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-secondary);
    margin-bottom: 1.75rem;
    animation: fadeUp 0.7s ease both;
  }

  .badge-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--text);
    animation: pulse-soft 2.2s ease-in-out infinite;
  }

  .hero-title-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 1.25rem;
    width: 100%;
    animation: fadeUp 0.7s 0.06s ease both;
  }
  .hero-alpha-badge {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.9);
    background: linear-gradient(
      135deg,
      rgba(139, 92, 246, 0.38),
      rgba(59, 130, 246, 0.3)
    );
    border: 1px solid rgba(167, 139, 250, 0.5);
    box-shadow: 0 0 20px rgba(139, 92, 246, 0.15);
  }
  .hero-title {
    font-size: clamp(2.75rem, 8vw, 5.5rem);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.04em;
    color: var(--text);
    margin: 0;
    text-align: center;
    max-width: 100%;
  }

  .grad {
    font-weight: 600;
    background: linear-gradient(
      100deg,
      rgba(245,245,245,0.42) 0%,
      rgba(245,245,245,0.95) 45%,
      rgba(245,245,245,0.42) 90%
    );
    background-size: 220% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: landing-grad 7s ease-in-out infinite;
  }

  @keyframes landing-grad {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  .hero-sub {
    font-size: 1rem;
    color: var(--text-secondary);
    max-width: 520px;
    line-height: 1.65;
    margin-bottom: 2.25rem;
    animation: fadeUp 0.7s 0.12s ease both;
  }

  .hero-btns {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
    animation: fadeUp 0.7s 0.18s ease both;
  }

  .hero-metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 1.1rem;
    justify-content: center;
    animation: fadeUp 0.7s 0.22s ease both;
  }
  .hero-metric {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.035);
    color: var(--text-secondary);
    font-size: 12px;
    line-height: 1.25;
    white-space: nowrap;
  }
  .hero-metric-label {
    white-space: nowrap;
  }
  .hero-metric-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255,255,255,0.35);
    box-shadow: 0 0 0 3px rgba(255,255,255,0.06);
  }
  .hero-metric-strong {
    color: var(--text);
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .landing-reveal {
    opacity: 0;
    transform: translateY(32px);
    transition:
      opacity 0.75s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .landing-reveal--in {
    opacity: 1;
    transform: translateY(0);
  }
  .landing-reveal .section-label,
  .landing-reveal .section-title,
  .landing-reveal .landing-precision-lead,
  .landing-reveal .landing-ia-grid > div,
  .landing-reveal .landing-features-2 > div,
  .landing-reveal .cta-box {
    opacity: 0;
    transform: translateY(22px) scale(0.985);
    transition:
      opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .landing-reveal--in .section-label,
  .landing-reveal--in .section-title,
  .landing-reveal--in .landing-precision-lead,
  .landing-reveal--in .landing-ia-grid > div,
  .landing-reveal--in .landing-features-2 > div,
  .landing-reveal--in .cta-box {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  .landing-reveal .section-title { transition-delay: 70ms; }
  .landing-reveal .landing-precision-lead { transition-delay: 130ms; }
  .landing-reveal .landing-ia-grid > div:nth-child(1),
  .landing-reveal .landing-features-2 > div:nth-child(1) { transition-delay: 90ms; }
  .landing-reveal .landing-ia-grid > div:nth-child(2),
  .landing-reveal .landing-features-2 > div:nth-child(2) { transition-delay: 170ms; }
  .hero-scroll-indicator {
    position: absolute;
    left: 50%;
    bottom: 58px;
    transform: translateX(-50%);
    width: 2px;
    height: 34px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0.05));
    z-index: 3;
    animation: scroll-indicator-pulse 1.9s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes scroll-indicator-pulse {
    0%, 100% { opacity: 0.35; transform: translateX(-50%) scaleY(0.85); }
    50% { opacity: 0.9; transform: translateX(-50%) scaleY(1.05); }
  }

  .landing-marquee {
    position: relative;
    z-index: 3;
    overflow: hidden;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    background: rgba(0,0,0,0.35);
    padding: 14px 0;
    margin-top: 0;
    width: 100%;
  }
  .landing-marquee-track {
    display: flex;
    gap: 3.5rem;
    width: max-content;
    animation: landing-marquee 32s linear infinite;
  }
  .landing-marquee span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    white-space: nowrap;
  }
  @keyframes landing-marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  @keyframes cta-glow {
    0%, 100% { box-shadow: 0 16px 48px rgba(0,0,0,0.35); }
    50% { box-shadow: 0 24px 64px rgba(0,0,0,0.45), 0 0 80px rgba(255,255,255,0.035); }
  }

  .landing-feat-icon {
    width: 38px;
    height: 38px;
    margin-bottom: 12px;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--border);
  }
  .landing-icon-svg {
    width: 20px;
    height: 20px;
  }

  .landing-ia-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    align-items: start;
    margin-top: 1.5rem;
  }
  @media (max-width: 900px) {
    .landing-ia-grid {
      grid-template-columns: 1fr;
    }
  }
  .landing-ia-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .landing-ia-list li {
    font-size: 13px;
    color: var(--text-secondary);
    padding: 8px 0 8px 1.1rem;
    border-bottom: 1px solid var(--border);
    position: relative;
    line-height: 1.5;
  }
  .landing-ia-list li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 15px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--text-tertiary);
  }
  .landing-ia-list li:last-child {
    border-bottom: none;
  }

  .btn-primary {
    padding: 12px 26px;
    border-radius: 6px;
    border: 1px solid var(--text);
    background: var(--text);
    color: var(--bg);
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.2s;
  }

  .btn-primary:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: 0 0 24px rgba(0,30,80,0.35);
  }

  .btn-secondary {
    padding: 12px 22px;
    border-radius: 6px;
    border: 1px solid var(--border-strong);
    background: transparent;
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }

  .btn-secondary:hover {
    border-color: var(--text-tertiary);
    color: var(--text);
  }

  .landing-precision-section {
    position: relative;
    z-index: 3;
    padding: 1.75rem 2rem 3rem;
    border-top: none;
  }
  .landing-precision-inner {
    max-width: 1120px;
    margin: 0 auto;
  }
  .landing-precision-section .section-title {
    margin-bottom: 0.35rem;
  }
  .landing-precision-lead {
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.65;
    max-width: 38rem;
    margin-bottom: 2rem;
  }

  .landing-pipeline {
    margin-top: 0.5rem;
  }
  .landing-pipeline-row {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 0;
  }
  .landing-pipeline-step {
    flex: 1 1 0;
    min-width: 0;
    padding: 1.15rem 1rem;
    border-radius: 8px;
    background: var(--surface);
    border: 1px solid var(--border);
    text-align: left;
    opacity: 0;
    transform: translateY(16px);
    animation: pipeline-step-in 0.75s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  .landing-pipeline-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    color: var(--bg);
    background: var(--text);
    margin-bottom: 10px;
    animation: pipeline-node-glow 2.8s ease-in-out infinite;
  }
  .landing-pipeline-step-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    letter-spacing: -0.02em;
    margin-bottom: 6px;
    line-height: 1.25;
  }
  .landing-pipeline-step-sub {
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.5;
  }
  .landing-pipeline-connector {
    flex: 0 0 clamp(28px, 4vw, 52px);
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    min-height: 48px;
  }
  .landing-pipeline-line {
    width: 100%;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(
      90deg,
      var(--border),
      rgba(255,255,255,0.22),
      rgba(255,255,255,0.42),
      rgba(255,255,255,0.22),
      var(--border)
    );
    background-size: 200% 100%;
    animation: pipeline-shine 2.8s ease-in-out infinite;
    position: relative;
    overflow: hidden;
  }
  .landing-pipeline-line::after {
    content: '';
    position: absolute;
    top: 50%;
    left: -12%;
    width: 12%;
    height: 10px;
    transform: translateY(-50%);
    background: radial-gradient(circle, rgba(255,255,255,0.55), transparent 60%);
    opacity: 0.7;
    animation: pipeline-travel 1.7s ease-in-out infinite;
  }
  @keyframes pipeline-travel {
    0% { transform: translate(-10%, -50%) scale(0.7); opacity: 0.0; }
    15% { opacity: 0.6; }
    55% { opacity: 0.9; }
    100% { transform: translate(920%, -50%) scale(1.0); opacity: 0.0; }
  }
  .landing-pipeline-foot {
    margin-top: 1.75rem;
    font-size: 13px;
    color: var(--text-tertiary);
    text-align: center;
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
  }

  @keyframes pipeline-step-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes pipeline-shine {
    0%, 100% { background-position: 0% 50%; opacity: 0.75; }
    50% { background-position: 100% 50%; opacity: 1; }
  }
  @keyframes pipeline-node-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
    50% { box-shadow: 0 0 0 3px rgba(255,255,255,0.08); }
  }

  @media (max-width: 900px) {
    .landing-pipeline-row {
      flex-direction: column;
    }
    .landing-pipeline-connector {
      flex: 0 0 auto;
      width: 2px;
      height: 28px;
      min-height: 28px;
      margin: 0 auto;
    }
    .landing-pipeline-line {
      width: 2px;
      height: 100%;
      background: linear-gradient(
        180deg,
        var(--border),
        rgba(255,255,255,0.22),
        rgba(255,255,255,0.42),
        rgba(255,255,255,0.22),
        var(--border)
      );
      background-size: 100% 200%;
      animation: pipeline-shine-v 2.8s ease-in-out infinite;
    }
  }
  @keyframes pipeline-shine-v {
    0%, 100% { background-position: 50% 0%; opacity: 0.75; }
    50% { background-position: 50% 100%; opacity: 1; }
  }

  .landing-features-3 {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    max-width: 1120px;
    margin: 0 auto;
  }
  @media (max-width: 900px) {
    .landing-features-3 { grid-template-columns: 1fr; }
  }

  .landing-features-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    max-width: 1120px;
    margin: 0 auto;
  }
  @media (max-width: 768px) {
    .landing-features-2 { grid-template-columns: 1fr; }
  }

  .landing-feat-card {
    padding: 1.9rem;
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 1px solid var(--border);
    transition: transform 0.25s ease, border-color 0.25s;
  }
  .landing-feat-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
  }
  .landing-stagger-item {
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.55s ease, transform 0.55s ease;
  }
  .landing-reveal--in .landing-stagger-item {
    opacity: 1;
    transform: translateY(0);
  }
  .landing-feat-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 6px;
    letter-spacing: -0.02em;
  }
  .landing-feat-desc {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.65;
    white-space: pre-line;
  }

  .landing-chat-section {
    position: relative;
    z-index: 3;
    padding: 4rem 2rem;
    max-width: 920px;
    margin: 0 auto;
    text-align: center;
  }
  .landing-chat-box {
    margin-top: 2rem;
    padding: 1.5rem 1.25rem;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--bg-elevated);
  }
  .landing-chat-mock {
    margin-top: 14px;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: rgba(0,0,0,0.32);
    padding: 10px;
    min-height: 118px;
  }
  .landing-chat-mock-row {
    display: flex;
    margin-bottom: 8px;
  }
  .landing-chat-mock-row--user {
    justify-content: flex-end;
  }
  .landing-chat-mock-bubble {
    max-width: 78%;
    font-size: 11px;
    line-height: 1.45;
    border-radius: 8px;
    padding: 7px 9px;
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.03);
    color: var(--text-secondary);
  }
  .landing-chat-mock-row--user .landing-chat-mock-bubble {
    background: rgba(255,255,255,0.07);
    color: var(--text);
  }
  .landing-chat-typing {
    display: inline-flex;
    gap: 4px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.03);
  }
  .landing-chat-typing span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(255,255,255,0.55);
    animation: chat-dot-blink 1.1s ease-in-out infinite;
  }
  .landing-chat-typing span:nth-child(2) { animation-delay: 0.15s; }
  .landing-chat-typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes chat-dot-blink {
    0%, 80%, 100% { opacity: 0.28; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-2px); }
  }

  .stock-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.25rem;
    min-height: 240px;
    padding: 2rem;
  }
  .stock-loading-ring {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 3px solid rgba(148, 163, 184, 0.2);
    border-top-color: var(--loading-spin);
    animation: stock-loading-spin 0.8s linear infinite;
  }
  @keyframes stock-loading-spin {
    to { transform: rotate(360deg); }
  }
  .stock-refresh-overlay {
    position: absolute;
    inset: 0;
    z-index: 30;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: rgba(12, 12, 14, 0.78);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border-radius: 0;
    pointer-events: auto;
  }
  .stock-refresh-overlay .stock-loading-ring {
    width: 40px;
    height: 40px;
    border-width: 2px;
  }
  .stock-loading-bar {
    position: relative;
    width: 280px;
    max-width: 80%;
    height: 6px;
    margin: 14px auto 6px;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.18);
    overflow: hidden;
  }
  .stock-loading-bar--indeterminate::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 40%;
    border-radius: inherit;
    background: linear-gradient(90deg, rgba(149,184,236,0) 0%, var(--loading-spin) 50%, rgba(149,184,236,0) 100%);
    animation: stock-loading-bar-slide 1.4s ease-in-out infinite;
  }
  .stock-loading-bar__fill {
    position: absolute;
    inset: 0 auto 0 0;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--loading-spin) 0%, #93c5fd 100%);
    box-shadow: 0 0 12px rgba(147,197,253,0.35);
    transition: width 0.35s ease-out;
  }
  .stock-loading-percent {
    margin-top: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: rgba(226,232,240,0.7);
    font-variant-numeric: tabular-nums;
  }
  @keyframes stock-loading-bar-slide {
    0%   { transform: translateX(-110%); }
    100% { transform: translateX(310%); }
  }
  @media (prefers-reduced-motion: reduce) {
    .stock-loading-ring { animation: none; border-color: var(--loading-ring-static); }
    .stock-loading-bar--indeterminate::after { animation: none; opacity: 0.72; transform: translateX(40%); }
    .stock-loading-bar__fill { transition: none; }
  }

  .landing-ticker-wrap {
    position: relative;
    height: 152px;
    overflow: hidden;
    margin-top: 1.25rem;
    mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
    -webkit-mask-image: linear-gradient(to bottom, transparent, black 10%, black 90%, transparent);
  }
  .landing-ticker {
    display: flex;
    flex-direction: column;
    gap: 12px;
    animation: landing-ticker-scroll 20s linear infinite;
  }
  @keyframes landing-ticker-scroll {
    0% { transform: translateY(0); }
    100% { transform: translateY(-50%); }
  }
  .landing-ticker-line {
    font-size: 13px;
    color: var(--text-secondary);
    padding: 10px 12px;
    border-radius: 6px;
    background: rgba(0,0,0,0.4);
    border: 1px solid var(--border);
    text-align: left;
    flex-shrink: 0;
  }

  .landing-footer {
    position: relative;
    z-index: 3;
    padding: 2.5rem 2rem;
    text-align: center;
    font-size: 12px;
    color: var(--text-secondary);
    border-top: 1px solid var(--border);
  }

  .section--wide {
    max-width: 1120px;
  }

  .term-preview {
    position: relative;
    z-index: 1;
    margin: 2.5rem auto 0;
    max-width: 620px;
    width: 92%;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--bg-elevated);
    overflow: hidden;
    animation: fadeUp 0.7s 0.28s ease both;
    box-shadow: 0 24px 80px rgba(0,0,0,0.65);
  }

  .term-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--border);
    background: rgba(0,0,0,0.35);
  }

  .td { width: 9px; height: 9px; border-radius: 50%; background: #2a2a2a; border: 1px solid var(--border); }
  .td.r { background: #1a1a1a; }
  .td.y { background: #262626; }
  .td.g { background: #333333; }

  .term-title {
    margin-left: 6px;
    font-size: 11px;
    color: var(--text-tertiary);
    font-family: ui-monospace, monospace;
  }

  .term-body {
    padding: 1.1rem;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .tm { display: flex; gap: 10px; align-items: flex-start; }

  .tav {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .tav.ai {
    background: var(--text);
    color: var(--bg);
    border: 1px solid var(--border-strong);
  }

  .tav.u {
    background: var(--surface);
    color: var(--text-secondary);
    border: 1px solid var(--border);
  }

  .tt {
    font-size: 12px;
    line-height: 1.55;
    color: var(--text-secondary);
    padding: 8px 11px;
    border-radius: 6px;
    background: rgba(0,0,0,0.4);
    border: 1px solid var(--border);
  }

  .tm.u .tt {
    color: var(--text);
    border-color: var(--border-strong);
  }

  .section {
    position: relative;
    z-index: 3;
    padding: 3.5rem 2rem;
    max-width: 1040px;
    margin: 0 auto;
  }

  .section-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--text-tertiary);
    margin-bottom: 0.75rem;
  }

  .section-title {
    font-size: clamp(1.65rem, 3.5vw, 2.35rem);
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--text);
    margin-bottom: 2rem;
    line-height: 1.2;
  }

  .section.section--wide {
    max-width: 1120px;
  }

  .landing-chat-section.section--wide {
    text-align: left;
  }

  .landing-ia-grid .landing-chat-box {
    margin-top: 0;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
  }

  .stat-card {
    padding: 1.9rem;
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 1px solid var(--border);
    transition: border-color 0.25s;
  }

  .stat-card:hover {
    border-color: var(--border-strong);
  }

  .stat-num {
    font-size: 2rem;
    font-weight: 300;
    letter-spacing: -0.015em;
    color: var(--text);
    display: block;
    margin-bottom: 6px;
  }

  .stat-desc {
    font-size: 11px;
    color: var(--text-secondary);
    line-height: 1.45;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-variant: small-caps;
  }

  .features-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 12px;
  }

  .feat {
    padding: 1.9rem;
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 1px solid var(--border);
    transition: border-color 0.25s;
  }

  .feat:hover {
    border-color: var(--border-strong);
  }

  .feat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    margin-bottom: 1rem;
    background: rgba(0,0,0,0.5);
    border: 1px solid var(--border);
  }

  .fi1, .fi2, .fi3 { background: rgba(0,0,0,0.55); }

  .feat-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    margin-bottom: 6px;
  }

  .feat-desc {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.65;
  }

  .cta-section {
    position: relative;
    z-index: 3;
    padding: 2.5rem 2rem 5rem;
    text-align: center;
  }

  .cta-box {
    max-width: 640px;
    margin: 0 auto;
    padding: 3rem 2rem;
    border-radius: 8px;
    background: var(--surface);
    border: 1px solid var(--border-strong);
    transition: border-color 0.4s, box-shadow 0.4s;
    animation: cta-glow 6s ease-in-out infinite;
  }

  .cta-title {
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.03em;
    margin-bottom: 0.75rem;
    line-height: 1.25;
  }

  .cta-sub {
    color: var(--text-secondary);
    margin-bottom: 1.75rem;
    font-size: 14px;
    line-height: 1.6;
  }

  .chat-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    position: relative;
    overflow: hidden;
  }

  .chat-shell {
    display: flex;
    flex: 1;
    min-height: 0;
    position: relative;
    z-index: 1;
    width: 100%;
    padding: 0;
  }

  .chat-sidebar {
    width: 268px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border-right: 1px solid var(--border);
    background: rgba(5,5,5,0.97);
    backdrop-filter: blur(12px);
  }

  .sidebar-brand {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px 10px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 4px;
  }

  .sidebar-brand-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    padding: 4px 0;
    text-align: left;
    transition: color 0.2s;
  }

  .sidebar-brand-btn:hover {
    color: var(--text);
  }

  .sidebar-new {
    width: 100%;
    padding: 10px 12px;
    border-radius: 6px;
    border: 1px solid var(--border-strong);
    background: var(--text);
    color: var(--bg);
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  .sidebar-new:hover {
    opacity: 0.9;
  }

  .sidebar-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    padding: 8px 8px 4px;
  }

  .sidebar-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }

  .sidebar-item {
    width: 100%;
    text-align: left;
    padding: 10px 10px;
    border-radius: 6px;
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 12px;
    line-height: 1.4;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sidebar-item:hover {
    background: rgba(255,255,255,0.04);
    color: var(--text);
  }

  .sidebar-item.active {
    background: var(--surface);
    border-color: var(--border);
    color: var(--text);
  }

  .sidebar-item-title {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .sidebar-item-meta {
    font-size: 10px;
    color: var(--text-tertiary);
    font-weight: 500;
  }

  .sidebar-item.active .sidebar-item-meta {
    color: var(--text-secondary);
  }

  .chat-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: row;
    min-height: 0;
    width: 100%;
    max-width: none;
    margin: 0;
    border: none;
    border-radius: 0;
    overflow: hidden;
    background: var(--bg);
    backdrop-filter: none;
  }
  .chat-body {
    flex: 1;
    min-height: 0;
    display: flex;
    overflow: hidden;
  }
  .chat-panel {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .chat-nav {
    position: relative;
    z-index: 10;
    width: 252px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
    padding: 1rem 0.75rem;
    border-right: 1px solid var(--border);
    border-bottom: none;
    background: var(--surface);
    backdrop-filter: none;
    flex-shrink: 0;
  }
  .chat-nav-brand {
    display: inline-flex;
    align-items: center;
    opacity: 0.95;
    min-width: 0;
    padding: 0.25rem 0.35rem 0.7rem;
    border-bottom: 1px solid var(--border);
  }
  .chat-nav-brand img {
    height: 24px;
    width: 24px;
    object-fit: contain;
    display: block;
  }
  .chat-nav-right {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    min-width: 0;
    margin-top: auto;
  }

  .chat-tabs { display:flex; flex-direction:column; gap:6px; background:transparent; border-radius:0; padding:0; justify-self:center; min-width:0; }
  .chat-tab { width: 100%; text-align:left; padding:10px 12px; border-radius:6px; border:1px solid transparent; background:transparent; color:var(--muted); font-size:13px; font-weight:600; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; gap:8px; }
  .chat-tab-icon { width: 16px; display: inline-flex; justify-content: center; color: rgba(191,219,254,0.9); flex-shrink: 0; }
  .chat-tab.active { background:rgba(0,30,80,0.22);color:#bfdbfe; }
  .chat-tab:hover:not(.active):not(.chat-tab--locked) { color:var(--text); border-color: var(--border); background: rgba(255,255,255,0.03); }
  .chat-tab.chat-tab--locked { color: rgba(148,163,184,0.55); cursor: not-allowed; opacity: 0.7; white-space: nowrap; }
  .chat-tab.chat-tab--locked:hover { background: transparent; border-color: transparent; }
  .chat-tab.chat-tab--home {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-variant: small-caps;
    padding: 12px 12px;
    margin-bottom: 6px;
    border-color: rgba(0,30,80,0.35);
    background: rgba(0,30,80,0.1);
    color: #e2e8f0;
    box-shadow: inset 3px 0 0 0 #001E50;
  }
  .chat-tab.chat-tab--home:not(.active):hover {
    background: rgba(0,30,80,0.16);
    border-color: rgba(0,30,80,0.45);
    color: #f8fafc;
  }
  .chat-tab.chat-tab--home.active {
    background: rgba(0,30,80,0.32);
    border-color: rgba(126,166,255,0.4);
    color: #f8fafc;
    box-shadow: inset 3px 0 0 0 #7ea6ff;
  }
  .chat-user-badge { font-size: 12px; color: var(--muted); max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mobile-dashboard-header {
    display: none;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    background: rgba(15, 18, 23, 0.96);
    position: relative;
    z-index: 6050;
    backdrop-filter: blur(8px);
  }
  .mobile-dashboard-brand {
    display: inline-flex;
    align-items: center;
    gap: 0;
    min-width: 0;
    text-decoration: none;
    color: var(--text);
  }
  .mobile-dashboard-brand img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    display: block;
  }
  .mobile-dashboard-menu-btn {
    border: 1px solid rgba(126,166,255,0.45);
    background: rgba(0,30,80,0.88);
    color: #e2e8f0;
    border-radius: 8px;
    min-height: 38px;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 700;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }
  .mobile-nav-fab {
    position: fixed;
    left: 12px;
    top: 12px;
    z-index: 6200;
    width: 48px;
    height: 48px;
    border-radius: 999px;
    border: 1px solid rgba(126,166,255,0.45);
    background: rgba(0,30,80,0.92);
    color: #e2e8f0;
    display: none;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    cursor: pointer;
    box-shadow: 0 10px 26px rgba(0,0,0,0.35);
  }
  .mobile-nav-section-title {
    font-size: 10px;
    color: var(--text-tertiary);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin: 4px 2px 8px;
    font-weight: 700;
  }
  .mobile-nav-sheet__tabs--primary {
    margin-bottom: 10px;
  }
  .mobile-nav-sheet {
    position: fixed;
    inset: 0;
    z-index: 6100;
    background: rgba(6,10,14,0.6);
    backdrop-filter: blur(4px);
    display: none;
    align-items: flex-end;
    justify-content: center;
    padding: 12px;
  }
  .mobile-nav-sheet__panel {
    width: min(420px, 100%);
    border: 1px solid var(--border);
    border-radius: 12px;
    background: #151920;
    padding: 12px;
    max-height: 82vh;
    overflow: auto;
  }
  .mobile-nav-sheet__tabs {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .mobile-nav-tab {
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.03);
    color: var(--text-secondary);
    border-radius: 8px;
    min-height: 42px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    cursor: pointer;
  }
  .mobile-nav-tab.active {
    background: rgba(0,30,80,0.28);
    border-color: rgba(126,166,255,0.45);
    color: #e2e8f0;
  }
  .mobile-nav-tab.mobile-nav-tab--locked {
    color: rgba(148,163,184,0.55);
    cursor: not-allowed;
    opacity: 0.7;
    background: transparent;
    white-space: nowrap;
  }
  .mobile-nav-sheet__actions {
    margin-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .mobile-nav-action {
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.03);
    color: var(--text-secondary);
    border-radius: 8px;
    min-height: 40px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
  }

  .settings-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(18, 20, 22, 0.82);
    backdrop-filter: blur(10px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }
  .settings-sheet {
    width: min(480px, 100%);
    max-height: min(90vh, 680px);
    display: flex;
    flex-direction: column;
    background: var(--bg-elevated);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
  }
  .settings-sheet__head {
    flex-shrink: 0;
    padding: 1.75rem 1.75rem 1.25rem;
    border-bottom: 1px solid var(--border);
  }
  .settings-sheet__head-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
  }
  .settings-sheet__title {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text);
    line-height: 1.3;
  }
  .settings-sheet__subtitle {
    margin-top: 0.5rem;
    font-size: 12px;
    line-height: 1.55;
    color: var(--text-tertiary);
    font-weight: 400;
    max-width: 36em;
  }
  .settings-close {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: transparent;
    color: var(--text-tertiary);
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    font-family: inherit;
    transition: color 0.15s, border-color 0.15s, background 0.15s;
  }
  .settings-close:hover {
    color: var(--text);
    border-color: var(--border-strong);
    background: rgba(255, 255, 255, 0.04);
  }
  .settings-sheet__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem 1.75rem 1rem;
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }
  .settings-section {
    margin-bottom: 1.75rem;
  }
  .settings-section:last-child {
    margin-bottom: 0.5rem;
  }
  .settings-section__label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--text-tertiary);
    margin-bottom: 0.85rem;
    font-variant: small-caps;
  }
  .settings-field {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.7rem 0;
    border-bottom: 1px solid var(--border);
  }
  .settings-field:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .settings-field__text {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.45;
  }
  .settings-input-wrap {
    position: relative;
    flex-shrink: 0;
  }
  .settings-input {
    width: 100px;
    padding: 8px 34px 8px 12px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text);
    font-size: 15px;
    font-weight: 300;
    font-variant-numeric: tabular-nums;
    text-align: right;
    outline: none;
    font-family: inherit;
    transition: border-color 0.15s;
  }
  .settings-input:focus {
    border-color: rgba(0, 30, 80, 0.55);
  }
  .settings-input--narrow {
    width: 80px;
    padding-right: 30px;
  }
  .settings-input--mid {
    width: 90px;
    padding-right: 34px;
  }
  .settings-input-suffix {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    pointer-events: none;
  }
  .settings-hint {
    margin-top: 0.35rem;
    font-size: 11px;
    line-height: 1.5;
    color: var(--text-tertiary);
  }
  .settings-sheet__foot {
    flex-shrink: 0;
    padding: 1rem 1.75rem 1.5rem;
    border-top: 1px solid var(--border);
    background: var(--bg-elevated);
  }
  .settings-save {
    width: 100%;
    padding: 11px 16px;
    border-radius: var(--radius-sm);
    border: 1px solid rgba(0, 30, 80, 0.55);
    background: var(--accent);
    color: #f8fafc;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .settings-save:hover {
    background: #002a6e;
    border-color: rgba(126, 166, 255, 0.35);
  }
  .settings-nav-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-secondary);
    font-size: 11px;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .settings-nav-btn:hover {
    border-color: var(--border-strong);
    color: var(--text);
    background: rgba(255, 255, 255, 0.03);
  }
  .settings-nav-btn__glyph {
    font-size: 12px;
    color: var(--text-tertiary);
    letter-spacing: 0;
    text-transform: none;
  }

  .back-top-btn {
    position: fixed;
    right: 16px;
    bottom: 18px;
    z-index: 60;
    border: 1px solid rgba(0,30,80,0.45);
    background: rgba(23,26,31,0.88);
    color: #bfdbfe;
    border-radius: 999px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    backdrop-filter: blur(8px);
    box-shadow: 0 8px 26px rgba(0,0,0,0.35);
    transition: all 0.2s ease;
  }
  .back-top-btn:hover {
    background: rgba(0,30,80,0.2);
    border-color: rgba(126,166,255,0.75);
    color: #dbeafe;
  }
  .marche-stats-row {
    display: flex;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }

  .online {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--text-tertiary);
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .online-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--text);
    opacity: 0.85;
    animation: pulse-soft 2s ease-in-out infinite;
  }

  .messages {
    position: relative;
    z-index: 1;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }

  .mw {
    display: flex;
    gap: 10px;
    max-width: 720px;
    width: 100%;
    animation: fadeUp 0.35s ease both;
  }

  .mw.user {
    align-self: flex-end;
    flex-direction: row-reverse;
  }

  .mav {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
  }

  .mav.ai {
    background: var(--text);
    color: var(--bg);
    border: 1px solid var(--border-strong);
  }

  .mav.user {
    background: var(--surface);
    color: var(--text-secondary);
    border: 1px solid var(--border);
  }

  .mb {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.65;
    max-width: calc(100% - 40px);
    white-space: pre-wrap;
    word-break: break-word;
  }

  .mb a:hover {
    color: #bfdbfe;
  }

  .mw.ai .mb {
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--text-secondary);
    border-bottom-left-radius: 4px;
  }

  .mw.user .mb {
    background: rgba(255,255,255,0.06);
    border: 1px solid var(--border-strong);
    color: var(--text);
    border-bottom-right-radius: 4px;
  }

  .mb strong {
    color: #fff;
    font-weight: 700;
  }

  .estimation {
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.03);
    border-radius: 10px;
    padding: 12px;
    display: grid;
    gap: 8px;
  }

  .est-title {
    font-size: 13px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.02em;
  }

  .est-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
  }

  .est-label {
    color: var(--text-secondary);
    font-weight: 600;
  }

  .est-val {
    color: #fff;
    font-weight: 700;
    text-align: right;
  }

  .est-annonces {
    margin-top: 4px;
    font-size: 11px;
    color: #bfdbfe;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .est-ann {
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.5;
  }

  .est-ann a {
    color: #bfdbfe;
    text-decoration: underline;
  }

  /* Mon stock : 3 annonces par ligne (cartes plus lisibles) */
  .stock-annonces-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }
  @media (max-width: 1100px) {
    .stock-annonces-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 560px) {
    .stock-annonces-grid {
      grid-template-columns: 1fr;
    }
  }

  .typing-wrap {
    display: flex;
    gap: 10px;
    animation: fadeUp 0.3s ease both;
  }

  .typing-bubble {
    padding: 0.85rem 1rem;
    border-radius: 8px;
    border-bottom-left-radius: 4px;
    background: var(--surface);
    border: 1px solid var(--border);
    display: flex;
    gap: 5px;
    align-items: center;
  }

  .typing-bubble span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--text-tertiary);
    animation: bounce-dot 1s infinite;
  }

  .typing-bubble span:nth-child(2) { animation-delay: 0.12s; }
  .typing-bubble span:nth-child(3) { animation-delay: 0.24s; }

  .sugs {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding: 0 1.25rem 0.65rem;
  }

  .sug {
    padding: 7px 14px;
    border-radius: 6px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-secondary);
    font-family: inherit;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }

  .sug:hover {
    border-color: var(--border-strong);
    color: var(--text);
  }

  .input-area {
    position: relative;
    z-index: 1;
    padding: 0.65rem 1.25rem 1.25rem;
    background: rgba(5,5,5,0.92);
    backdrop-filter: blur(12px);
    border-top: 1px solid var(--border);
  }

  .input-wrap {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .chat-input {
    flex: 1;
    padding: 0.75rem 1rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text);
    font-family: inherit;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;
  }

  .chat-input::placeholder {
    color: var(--text-tertiary);
  }

  .chat-input:focus {
    border-color: var(--border-strong);
  }

  .send-btn {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    border: 1px solid var(--text);
    background: var(--text);
    color: var(--bg);
    font-size: 16px;
    cursor: pointer;
    transition: opacity 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .send-btn:hover:not(:disabled) {
    opacity: 0.9;
  }

  .send-btn:disabled {
    background: var(--surface);
    border-color: var(--border);
    color: var(--text-tertiary);
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    .nav {
      animation: none !important;
    }
    .landing-marquee-track,
    .hero-float-dot,
    .hero-scroll-indicator,
    .landing-pipeline-line,
    .landing-pipeline-num,
    .landing-pipeline-step,
    .landing-chat-typing span,
    .cta-box,
    .grad {
      animation: none !important;
    }
    .landing-pipeline-line::after {
      animation: none !important;
      opacity: 0 !important;
    }
    .landing-reveal {
      opacity: 1;
      transform: none;
      transition: none;
    }
    .landing-feat-card {
      transition: none !important;
    }
    .landing-stagger-item {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
    .landing-feat-card:hover {
      transform: none;
    }
    .landing-pipeline-step {
      opacity: 1 !important;
      transform: none !important;
    }
  }

  @media (max-width: 900px) {
    .chat-shell {
      padding: 0;
    }
    .chat-main {
      border-radius: 0;
      flex-direction: column;
    }
    .chat-nav {
      display: none;
    }
    .mobile-dashboard-header { display: flex; }
    .mobile-nav-fab { display: none; }
    .mobile-nav-sheet { display: flex; }
  }

  @media (max-width: 640px) {
    .chat-shell {
      padding: 0;
    }
    .chat-main {
      border-radius: 0;
      border-left: none;
      border-right: none;
    }
    .chat-nav {
      padding: 0.65rem 0.5rem;
      overflow-x: auto;
    }
    .back-top-btn {
      right: 10px;
      bottom: 12px;
      padding: 7px 10px;
      font-size: 11px;
    }
    .marche-stats-row {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    .marche-page-root [role='tablist'],
    .marche-page-root .marche-tabs {
      overflow-x: auto;
    }
    .marche-page-root select {
      width: 100%;
      min-height: 40px;
    }
    .stock-page-root {
      padding: 0.85rem !important;
    }
    .stock-page-root h2 {
      font-size: 1.24rem !important;
    }
    .stock-page-root .stock-summary-card {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 12px !important;
      padding: 1rem !important;
    }
    .stock-page-root .stock-view-tabs,
    .stock-page-root .stock-filter-tabs {
      overflow-x: auto;
      white-space: nowrap;
      padding-bottom: 4px;
    }
    .stock-page-root .stock-view-tabs button,
    .stock-page-root .stock-filter-tabs button {
      flex: 0 0 auto;
    }
  }

  @media (max-width: 420px) {
    .marche-page-root .marche-stats-row > div {
      border-bottom: none;
      padding: 0.75rem 0.35rem !important;
    }
    .marche-page-root [data-marche-scroll] {
      padding: 0.75rem !important;
    }
    .stock-page-root {
      padding: 0.65rem !important;
    }
  }
`

/** 10 marques majeures CH, ordre alphabétique (libellé) */
const REGISTER_MOTO_MARQUES = [
  { key: 'aprilia', label: 'Aprilia' },
  { key: 'bmw', label: 'BMW' },
  { key: 'ducati', label: 'Ducati' },
  { key: 'harley-davidson', label: 'Harley-Davidson' },
  { key: 'honda', label: 'Honda' },
  { key: 'kawasaki', label: 'Kawasaki' },
  { key: 'ktm', label: 'KTM' },
  { key: 'suzuki', label: 'Suzuki' },
  { key: 'triumph', label: 'Triumph' },
  { key: 'yamaha', label: 'Yamaha' },
].sort((a, b) => a.label.localeCompare(b.label, 'fr'))
const AUTO_MARQUES = new Set(['audi', 'porsche'])

function isAutoOnlyAccount(user) {
  if (String(user?.vehicle_scope || '').toLowerCase() === 'auto') return true
  const list = Array.isArray(user?.marques_interet)
    ? user.marques_interet.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean)
    : []
  if (!list.length) return false
  const motoSet = new Set(REGISTER_MOTO_MARQUES.map((m) => m.key))
  const hasAuto = list.some((m) => AUTO_MARQUES.has(m))
  const hasMoto = list.some((m) => motoSet.has(m))
  return hasAuto && !hasMoto
}

function formatCarrosserieLabel(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  return raw.toLowerCase() === 'estate' ? 'break' : raw
}

/**
 * FIX 2026-05 : on arrondit toujours à la dizaine supérieure/proche
 * (Math.round(x/10)*10) pour que les prix recommandés se terminent par 0
 * (présentation commerciale : 6 500 plutôt que 6 498).
 */
function roundCommercialPrice(value) {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return n
  return Math.round(n / 10) * 10
}

function resolveProbableSalePrice(cotationLike, fallbackMarketValue) {
  const stratP = Number(cotationLike?.strategie_vente?.recommande?.prix)
  const rawProbable =
    Number.isFinite(stratP) && stratP > 0 ? stratP : Number(cotationLike?.prix_vente_probable)
  if (!Number.isFinite(rawProbable) || rawProbable <= 0) {
    return { price: null, adjustedToMarket: false, hadProbable: false }
  }
  const marketRaw =
    fallbackMarketValue != null
      ? Number(fallbackMarketValue)
      : Number(cotationLike?.valeur_estimee)
  if (!Number.isFinite(marketRaw) || marketRaw <= 0) {
    return { price: roundCommercialPrice(rawProbable), adjustedToMarket: false, hadProbable: true }
  }
  const nbVentes = Number(cotationLike?.stats_ventes?.nb_ventes)
  if (rawProbable > marketRaw && (!Number.isFinite(nbVentes) || nbVentes < 6)) {
    return { price: roundCommercialPrice(marketRaw), adjustedToMarket: true, hadProbable: true }
  }
  return { price: roundCommercialPrice(rawProbable), adjustedToMarket: false, hadProbable: true }
}

function LoginPage({ onLogin, onBack, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nom, setNom] = useState('')
  const [selectedMarques, setSelectedMarques] = useState([])
  const [sellerId, setSellerId] = useState('')
  const [concessions, setConcessions] = useState([])
  const [concessionsLoading, setConcessionsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)

  const switchMode = (next) => {
    setMode(next)
    setError('')
    setSuccess('')
    if (next === 'register') {
      setSelectedMarques([])
      setSellerId('')
    }
  }

  useEffect(() => {
    if (mode !== 'register') return
    setConcessionsLoading(true)
    fetchWithTimeout(`${BACKEND}/api/auth/concessions`)
      .then((r) => r.json())
      .then((d) => setConcessions(Array.isArray(d.concessions) ? d.concessions : []))
      .catch(() => setConcessions([]))
      .finally(() => setConcessionsLoading(false))
  }, [mode])

  const toggleMarque = (key) => {
    setSelectedMarques((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const submitLogin = async () => {
    setError('')
    setSuccess('')
    setLoading(true)
    try {
      const res = await fetchWithTimeout(`${BACKEND}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      let data = {}
      try {
        data = await res.json()
      } catch {
        setError(res.ok ? 'Réponse invalide du serveur.' : `Erreur serveur (${res.status}).`)
        setLoading(false)
        return
      }
      if (!res.ok) {
        setError(data.error || 'Erreur de connexion')
        setLoading(false)
        return
      }
      localStorage.setItem('radar_token', data.token)
      localStorage.setItem('radar_user', JSON.stringify(data.user))
      onLogin(data.user, data.token)
    } catch {
      setError('Erreur de connexion')
      setLoading(false)
    }
  }

  const submitRegister = async () => {
    setError('')
    setSuccess('')
    if (selectedMarques.length === 0) {
      setError('Sélectionnez au moins une marque.')
      return
    }
    const sid = parseInt(String(sellerId), 10)
    if (!Number.isFinite(sid) || sid < 1) {
      setError('Choisissez la concession que vous représentez.')
      return
    }
    setLoading(true)
    try {
      const res = await fetchWithTimeout(`${BACKEND}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          nom: nom.trim() || undefined,
          marques: selectedMarques,
          seller_id: sid,
        }),
      })
      let data = {}
      try {
        data = await res.json()
      } catch {
        setError(res.ok ? 'Réponse invalide du serveur.' : `Erreur serveur (${res.status}).`)
        setLoading(false)
        return
      }
      if (!res.ok) {
        setError(data.error || "Inscription impossible")
        setLoading(false)
        return
      }
      setSuccess(data.message || 'Inscription enregistrée. Validation du compte en attente.')
      setPassword('')
      setSelectedMarques([])
      setSellerId('')
    } catch {
      setError('Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  const isRegister = mode === 'register'

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ width: '100%', maxWidth: 460, padding: '0 1.5rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff', marginBottom: '0.5rem' }}>
          {isRegister ? 'Créer un compte' : 'Connexion'}
        </h1>
        <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: '1rem' }}>
          {isRegister
            ? 'Demandez un accès concessionnaire. Vous pourrez vous connecter après validation.'
            : 'Accédez à votre compte concessionnaire'}
        </p>
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: 16, padding: '1.5rem' }}>
          <input className="chat-input" style={{ width: '100%', marginBottom: 12 }} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input
            className="chat-input"
            style={{ width: '100%', marginBottom: 12 }}
            placeholder="Mot de passe"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (isRegister ? submitRegister() : submitLogin())}
          />
          {isRegister && (
            <>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, fontWeight: 600 }}>
                Marques qui vous intéressent
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px 10px',
                  marginBottom: 14,
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                }}
              >
                {REGISTER_MOTO_MARQUES.map(({ key, label }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedMarques.includes(key)}
                      onChange={() => toggleMarque(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6, fontWeight: 600 }}>
                Concession que vous représentez
              </div>
              {concessionsLoading ? (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>Chargement des concessions…</div>
              ) : concessions.length === 0 ? (
                <div style={{ fontSize: 12, color: '#f87171', marginBottom: 14 }}>
                  Aucune concession trouvée dans la base pour le moment. Réessayez plus tard.
                </div>
              ) : (
                <select
                  className="chat-input"
                  style={{ width: '100%', marginBottom: 14, cursor: 'pointer' }}
                  value={sellerId}
                  onChange={(e) => setSellerId(e.target.value)}
                >
                  <option value="">Choisir</option>
                  {concessions.map((c) => (
                    <option key={c.seller_id} value={c.seller_id}>
                      {c.nom}
                    </option>
                  ))}
                </select>
              )}
              <input className="chat-input" style={{ width: '100%', marginBottom: 12 }} placeholder="Nom (optionnel)" type="text" value={nom} onChange={(e) => setNom(e.target.value)} />
            </>
          )}
          {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 10 }}>{error}</div>}
          {success && <div style={{ color: '#4ade80', fontSize: 13, marginBottom: 10 }}>{success}</div>}
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={isRegister ? submitRegister : submitLogin}
            disabled={
              loading ||
              (isRegister && (concessionsLoading || concessions.length === 0))
            }
          >
            {loading ? (isRegister ? 'Envoi...' : 'Connexion...') : isRegister ? "S'inscrire" : 'Se connecter'}
          </button>
          <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13 }}>
            {isRegister ? (
              <button
                type="button"
                onClick={() => switchMode('login')}
                style={{ background: 'none', border: 'none', color: '#bfdbfe', cursor: 'pointer', fontSize: 13 }}
              >
                déjà un compte ? Se connecter
              </button>
            ) : (
              <button
                type="button"
                onClick={() => switchMode('register')}
                style={{ background: 'none', border: 'none', color: '#bfdbfe', cursor: 'pointer', fontSize: 13 }}
              >
                Créer un compte
              </button>
            )}
          </div>
        </div>
        <button type="button" onClick={onBack} style={{ display: 'block', margin: '1rem auto 0', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}>
          Retour à l&apos;accueil
        </button>
      </div>
    </div>
  )
}

/** Nombre de mouvements à la baisse dans l'historique (marché). */
function countMarchePriceDrops(history) {
  if (!Array.isArray(history) || history.length === 0) return 0
  let n = 0
  for (const step of history) {
    const ap = step?.ancien_prix
    const np = step?.nouveau_prix
    const dRaw = step?.delta
    const d =
      dRaw != null && Number.isFinite(Number(dRaw))
        ? Number(dRaw)
        : Number.isFinite(Number(np)) && Number.isFinite(Number(ap))
          ? Number(np) - Number(ap)
          : NaN
    if (Number.isFinite(d) && d < 0) n++
  }
  return n
}

function marcheDaysSinceIso(iso) {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  return Math.max(0, Math.floor((Date.now() - t) / 86400000))
}

/** Montant absolu de la dernière baisse (ligne marché « baisses »). */
function marcheLastDropChfAbs(b) {
  const d = Number(b?.delta)
  if (Number.isFinite(d) && d < 0) return Math.abs(d)
  const ap = Number(b?.ancien_prix)
  const np = Number(b?.nouveau_prix)
  if (Number.isFinite(ap) && Number.isFinite(np) && ap > np) return ap - np
  return null
}

/** Pourcentage absolu de la dernière baisse vs ancien prix (ex: 6.2). */
function marcheLastDropPctAbs(b) {
  const dropAbs = marcheLastDropChfAbs(b)
  const oldPrice = Number(b?.ancien_prix)
  if (!Number.isFinite(dropAbs) || !Number.isFinite(oldPrice) || oldPrice <= 0) return null
  return (dropAbs / oldPrice) * 100
}

/** Somme absolue de toutes les baisses dans l'historique de prix. */
function marcheTotalDropsChfAbs(history) {
  if (!Array.isArray(history) || history.length === 0) return 0
  let total = 0
  for (const step of history) {
    const ap = Number(step?.ancien_prix)
    const np = Number(step?.nouveau_prix)
    const dRaw = Number(step?.delta)
    const d =
      Number.isFinite(dRaw)
        ? dRaw
        : Number.isFinite(ap) && Number.isFinite(np)
          ? np - ap
          : NaN
    if (Number.isFinite(d) && d < 0) total += Math.abs(d)
  }
  return total
}

function niceStep(span, candidates) {
  const safeSpan = Math.max(1, Number(span) || 1)
  for (const step of candidates) {
    if (safeSpan / step <= 6) return step
  }
  return candidates[candidates.length - 1]
}

function roundDownToStep(value, step) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value
  return Math.floor(value / step) * step
}

function roundUpToStep(value, step) {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return value
  return Math.ceil(value / step) * step
}

/** Colonne droite : délai médian jours (première vue à vente) pour le modèle, renvoyé par l'API. */
function marcheDelayFromRow(x) {
  const d = Number(x?.delai_vente_median_j)
  if (!Number.isFinite(d) || d < 1) {
    return { delayMain: '—', delaySub: null }
  }
  const rounded = Math.round(d)
  return {
    delayMain: `${rounded} j`,
    delaySub: `Méd. ${rounded}j`,
  }
}

function marcheSortNum(a, b, dir) {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return (a - b) * dir
}

function formatModeleOptionLabel(slug) {
  if (slug === 'tous') return 'Tous les modèles'
  return String(slug)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Remplit le champ « Finition / précision » depuis titre + version d'annonce (SP, DCT, Tech Max).
 * Heuristiques alignées sur analytics/cotation.js (versions spéciales + phrases fortes).
 */
function extraireFinitionsDepuisAnnonce(titre, version) {
  const brut = `${version || ''} ${titre || ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
  if (!brut.trim()) return ''

  const out = []
  const seen = new Set()
  const add = (label) => {
    const L = String(label).trim()
    if (!L) return
    const k = L.toLowerCase()
    if (seen.has(k)) return
    seen.add(k)
    out.push(L)
  }

  const phraseRules = [
    [/africa\s*twin\s*adventure\s*sports?/i, 'Adventure Sports'],
    [/tech\s*max\s*plus/i, 'Tech Max+'],
    [/tech\s*max/i, 'Tech Max'],
    [/world\s*raid/i, 'World Raid'],
    [/rally\s*edition/i, 'Rally Edition'],
    [/monster\s*edition/i, 'Monster Edition'],
    [/low\s*version/i, 'Low'],
    [/fireblade\s*sp/i, 'Fireblade SP'],
    [/adventure\s*sports/i, 'Adventure Sports'],
    [/e[\s-]?clutch/i, 'E-Clutch'],
    [/i\s*mode/i, 'i Mode'],
    [/\bdct\b/i, 'DCT'],
    [/r1m\b/i, 'R1M'],
    [/\br1\s+m\b/i, 'R1M'],
    [/gytr/i, 'GYTR'],
    [/35\s*kw/i, '35 kW'],
    [/\b70th\b/i, 'Anniversary'],
    [/anniversary/i, 'Anniversary'],
    [/\bpro\s*mode\b/i, 'Pro Mode'],
    [/\bsmart\s*key\b/i, 'Smart Key'],
  ]

  for (const [re, label] of phraseRules) {
    if (!re.test(brut)) continue
    if (label === 'Tech Max' && /tech\s*max\s*plus/i.test(brut)) continue
    if (label === 'Adventure Sports' && seen.has('adventure sports')) continue
    add(label)
  }

  if (!/fireblade\s*sp/i.test(brut)) {
    const spHeuristique =
      brut.includes(' sp') || brut.includes('-sp') || /\b[\w'-]+\s+sp\b/i.test(brut)
    if (spHeuristique) add('SP')
  }

  if (/\ba2\b/i.test(brut)) add('A2')

  return out.join(', ').slice(0, 400)
}

/** Année d'immat. absente en base mais souvent dans le titre d'annonce (ex. concurrence / stock). */
function inferAnneeDepuisTitre(titre) {
  const m = String(titre || '').match(/\b(20[0-3]\d|201\d|199\d)\b/)
  return m ? parseInt(m[1], 10) : null
}

function sortModelesAlphabetiques(list) {
  const rows = Array.isArray(list) ? [...list] : []
  rows.sort((a, b) =>
    String(a?.model || '').localeCompare(String(b?.model || ''), 'fr', {
      sensitivity: 'base',
      numeric: true,
    })
  )
  return rows
}

/** Compte affiché au modèle : annonces avec année d'immat. exploitable pour la liste « Année » (sinon total legacy). */
function labelNbModeleCotation(m) {
  const total = Number(m?.nb ?? 0)
  const avecAnnee =
    m?.nb_avec_annee != null && Number.isFinite(Number(m.nb_avec_annee)) ? Number(m.nb_avec_annee) : total
  if (m?.nb_avec_annee != null && total > avecAnnee) return `${avecAnnee}/${total}`
  return String(avecAnnee)
}

function anneeKmOkPourCotationStock(mm) {
  if (!mm || !String(mm.model || '').trim()) return { ok: false, annee: null, km: null }
  const yRaw = mm.first_registration_year ?? mm.annee
  let annee = null
  if (yRaw != null && yRaw !== '' && Number.isFinite(Number(yRaw))) {
    const n = Number(yRaw)
    if (n >= 1990 && n <= 2035) annee = n
  }
  if (annee == null) annee = inferAnneeDepuisTitre(mm.titre)
  if (!Number.isFinite(annee) || annee < 1990 || annee > 2035) return { ok: false, annee: null, km: null }
  if (mm.km === null || mm.km === undefined || mm.km === '') return { ok: false, annee, km: null }
  const km = Number(mm.km)
  if (!Number.isFinite(km) || km < 0) return { ok: false, annee, km: null }
  return { ok: true, annee, km }
}

function CotationPage({ token, prefs, user = null, prefillRequest = null, onPrefillHandled = null, onOpenMarcheFicheModel = null }) {
  const [marques, setMarques] = useState([])
  const [modeles, setModeles] = useState([])
  const [annees, setAnnees] = useState([])

  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [annee, setAnnee] = useState('')
  const [km, setKm] = useState('')
  const [garantie, setGarantie] = useState(false)
  const [accident, setAccident] = useState(false)
  const [detailsMoto, setDetailsMoto] = useState('')
  const [, setCotationVersions] = useState([])
  const [cotationCarrosseries, setCotationCarrosseries] = useState([])
  const [cotationMotorisations, setCotationMotorisations] = useState([])
  const [autoPrecisionEnabled, setAutoPrecisionEnabled] = useState(false)
  const [variantesLoading, setVariantesLoading] = useState(false)
  const [carrosserieChoisie, setCarrosserieChoisie] = useState('')
  const [motorisationChoisie, setMotorisationChoisie] = useState('')

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [prixRachat, setPrixRachat] = useState('')
  const [showBackTop, setShowBackTop] = useState(false)
  const cotationScrollRef = useRef(null)
  const marcheScatterNavTimerRef = useRef(null)
  const lastPrefillIdRef = useRef(null)
  /** Contexte annonce (stock) : doit être renvoyé à chaque POST /api/coter, sinon « Obtenir la cotation » diverge du préremplissage. */
  const listingContextRef = useRef({ make: '', model: '', titre: '', version: '' })
  const [cotationHistoryOpen, setCotationHistoryOpen] = useState(false)
  const [cotationHistory, setCotationHistory] = useState(() => loadCotationHistory())
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false)
  )
  const autoAccount = isAutoOnlyAccount(user)
  const profileMarquesAllowed = useMemo(() => {
    const list = Array.isArray(user?.marques_interet)
      ? user.marques_interet.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean)
      : []
    return [...new Set(list)]
  }, [user?.marques_interet])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = (e) => {
      setIsMobileLayout(e.matches)
      if (e.matches) setCotationHistoryOpen(false)
    }
    setIsMobileLayout(mq.matches)
    if (mq.matches) setCotationHistoryOpen(false)
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
    mq.addListener(onChange)
    return () => mq.removeListener(onChange)
  }, [])

  const appendCotationHistory = useCallback((data, overrides = {}) => {
    const cot = data?.cotation
    if (!cot) return
    const probable = resolveProbableSalePrice(cot)
    const prixMain = Number(probable.price ?? cot.valeur_estimee)
    if (!Number.isFinite(prixMain) || prixMain <= 0) return
    const mk = String(overrides.make ?? make ?? '').trim()
    const md = String(overrides.model ?? model ?? '').trim()
    const an = String(overrides.annee ?? annee ?? '').trim()
    const kmO = String(overrides.km ?? km ?? '').trim()
    const imgPool = [
      ...(Array.isArray(cot.annonces_graphiques) ? cot.annonces_graphiques : []),
      ...(Array.isArray(cot.annonces_similaires) ? cot.annonces_similaires : []),
    ]
    const thumb =
      imgPool.map((x) => x?.image_url || x?.image_principale_url).find(Boolean) || null
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      at: new Date().toISOString(),
      make: mk,
      model: md,
      annee: an,
      km: kmO,
      prix: prixMain,
      thumb,
    }
    setCotationHistory((prev) => {
      const next = [entry, ...prev].slice(0, 10)
      try {
        localStorage.setItem(COTATION_HISTORY_LS_KEY, JSON.stringify(next))
      } catch {
        /* ignore quota */
      }
      return next
    })
  }, [make, model, annee, km])

  useEffect(() => {
    setPrixRachat('')
  }, [result])

  useEffect(() => {
    if (!token) return
    const url = autoAccount ? `${BACKEND}/api/marques?vehicle_type=auto` : `${BACKEND}/api/marques`
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        const all = Array.isArray(d) ? d : []
        const byType = autoAccount
          ? all.filter((m) => AUTO_MARQUES.has(String(m?.make || '').toLowerCase()))
          : all
        // PERF FIX 2026-04 : garde-fou front · cotation limitée strictement aux marques du compte connecté.
        const filtered = byType.filter((m) => profileMarquesAllowed.includes(String(m?.make || '').toLowerCase()))
        setMarques(filtered)
      })
      .catch(() => {})
  }, [token, autoAccount, profileMarquesAllowed])

  useEffect(() => {
    if (!Array.isArray(marques) || !marques.length) {
      setMake('')
      return
    }
    const current = String(make || '').toLowerCase()
    const allowed = marques.map((m) => String(m?.make || '').toLowerCase())
    if (current && allowed.includes(current)) return
    const preferred = autoAccount
      ? marques.find((m) => String(m?.make || '').toLowerCase() === 'audi') || marques[0]
      : marques[0]
    if (preferred?.make) setMake(String(preferred.make))
  }, [autoAccount, marques, make])

  useEffect(() => {
    if (!make) {
      setModeles([])
      setModel('')
      setCotationVersions([])
      setCotationCarrosseries([])
      setCotationMotorisations([])
      setAutoPrecisionEnabled(false)
      setVariantesLoading(false)
      setCarrosserieChoisie('')
      setMotorisationChoisie('')
      return
    }
    fetchWithTimeout(`${BACKEND}/api/modeles?make=${encodeURIComponent(make)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setModeles(sortModelesAlphabetiques(d)))
      .catch(() => {})
    setModel('')
    setAnnee('')
    setResult(null)
  }, [make, token])

  useEffect(() => {
    if (!make || !model) {
      setAnnees([])
      setAnnee('')
      setCotationVersions([])
      setCotationCarrosseries([])
      setCotationMotorisations([])
      setAutoPrecisionEnabled(false)
      setVariantesLoading(false)
      setCarrosserieChoisie('')
      setMotorisationChoisie('')
      return
    }
    fetch(
      `${BACKEND}/api/annees?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((d) => setAnnees(Array.isArray(d) ? d : []))
      .catch(() => {})
    setAnnee('')
    setResult(null)
  }, [make, model, token])

  useEffect(() => {
    if (!make || !model || !annee || !token) return
    const qs = new URLSearchParams({
      make: String(make),
      model: String(model),
      annee: String(annee),
    })
    const autoMake = AUTO_MARQUES.has(String(make || '').toLowerCase())
    const canUseAutoPrecision = autoMake || autoAccount
    setAutoPrecisionEnabled(canUseAutoPrecision)
    if (!canUseAutoPrecision) {
      setCotationVersions([])
      setCotationCarrosseries([])
      setCotationMotorisations([])
      setCarrosserieChoisie('')
      setMotorisationChoisie('')
    }
    setVariantesLoading(true)
    fetchWithTimeout(
      `${BACKEND}/api/cotation-variantes?${qs.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } },
      20000
    )
      .then((r) => r.json())
      .then((d) => {
        const versions = Array.isArray(d?.versions) ? d.versions : []
        const carrosseries = Array.isArray(d?.carrosseries) ? d.carrosseries : []
        const motorisations = Array.isArray(d?.motorisations) ? d.motorisations : []
        setAutoPrecisionEnabled(canUseAutoPrecision)
        setCotationVersions(canUseAutoPrecision ? versions : [])
        setCotationCarrosseries(canUseAutoPrecision ? carrosseries : [])
        setCotationMotorisations(canUseAutoPrecision ? motorisations : [])
      })
      .catch(() => {
        setAutoPrecisionEnabled(canUseAutoPrecision)
        setCotationVersions([])
        setCotationCarrosseries([])
        setCotationMotorisations([])
      })
      .finally(() => setVariantesLoading(false))
  }, [make, model, annee, token, autoAccount])

  const soumettreCotation = useCallback(async (payload = null) => {
    const makeVal = payload?.make ?? make
    const modelVal = payload?.model ?? model
    const anneeVal = payload?.annee ?? annee
    const kmVal = payload?.km ?? km
    const garantieVal = payload?.garantie ?? garantie
    const accidentVal = payload?.accident ?? accident
    const detailsMotoVal = payload?.detailsMoto ?? detailsMoto
    const carrosserieVal = payload?.carrosserie ?? carrosserieChoisie
    const motorisationVal = payload?.motorisation ?? motorisationChoisie

    if (!makeVal || !modelVal || !anneeVal || !kmVal) {
      setError('Remplissez tous les champs')
      return
    }
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const kmNum = parseInt(String(kmVal).replace(/\s/g, ''), 10)
      const meta = listingContextRef.current
      const makeKey = String(makeVal || '')
        .trim()
        .toLowerCase()
      const modelKey = String(modelVal || '').trim()
      const sameListingContext =
        meta.make &&
        makeKey === meta.make &&
        modelKey === meta.model
      const detailsFinal = String(detailsMotoVal || '').trim().slice(0, 400)
      const versionFinal = String(meta.version || '').trim().slice(0, 200)
      const res = await fetchWithTimeout(`${BACKEND}/api/coter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          make: makeVal,
          model: modelVal,
          annee: parseInt(String(anneeVal), 10),
          km: kmNum,
          garantie: Boolean(garantieVal),
          had_accident: Boolean(accidentVal),
          ...(autoPrecisionEnabled ? { vehicle_type: 'auto' } : {}),
          ...(autoPrecisionEnabled && carrosserieVal
            ? { selected_carrosserie: String(carrosserieVal).trim() }
            : {}),
          ...(autoPrecisionEnabled && motorisationVal
            ? { selected_motorisation: String(motorisationVal).trim() }
            : {}),
          ...(autoPrecisionEnabled && String(detailsMotoVal || '').trim()
            ? { details_precision: String(detailsMotoVal).trim().slice(0, 200) }
            : {}),
          ...(sameListingContext && meta.titre ? { titre_listing: meta.titre } : {}),
          ...(versionFinal ? { version_listing: versionFinal } : {}),
          ...(detailsFinal ? { details_moto: detailsFinal } : {}),
        }),
      }, 120000)
      const data = await res.json()
      if (!res.ok) {
        setError(data?.error || 'Erreur de cotation')
        setLoading(false)
        return
      }
      setResult(data)
      appendCotationHistory(data)
      invalidateRadarPageCaches()
    } catch {
      setError('Erreur de connexion')
    }
    setLoading(false)
  }, [make, model, annee, km, garantie, accident, detailsMoto, carrosserieChoisie, motorisationChoisie, autoPrecisionEnabled, token, appendCotationHistory])

  const coter = useCallback(async () => {
    await soumettreCotation()
  }, [soumettreCotation])

  useEffect(() => {
    const onKey = (e) => {
      if (!e.ctrlKey || e.key !== 'Enter') return
      if (loading || !make || !model || !annee || !km) return
      e.preventDefault()
      soumettreCotation()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [loading, make, model, annee, km, soumettreCotation])

  const runCotationPrefill = useCallback(async (req) => {
    const makePrefill = String(req?.make || '').trim().toLowerCase()
    const modelPrefill = String(req?.model || '').trim()
    const anneePrefill = Number(req?.annee)
    const kmPrefill = Number(req?.km)
    if (!makePrefill || !modelPrefill || !Number.isFinite(anneePrefill) || !Number.isFinite(kmPrefill)) return

    const titreStock = String(req?.titre ?? req?.titre_listing ?? '').trim()
    const versionStock = String(req?.version ?? req?.version_listing ?? '').trim()
    const finitionAuto = extraireFinitionsDepuisAnnonce(titreStock, versionStock)
    const detailsFinale =
      req?.details_moto != null && String(req.details_moto).trim()
        ? String(req.details_moto).trim().slice(0, 400)
        : finitionAuto.slice(0, 400)

    setError('')
    const garPref = Boolean(req?.garantie)
    const accPref = Boolean(req?.had_accident ?? req?.accident)
    setGarantie(garPref)
    setAccident(accPref)
    setDetailsMoto(detailsFinale)
    setMake(makePrefill)

    const modelesRes = await fetchWithTimeout(`${BACKEND}/api/modeles?make=${encodeURIComponent(makePrefill)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const modelesData = await modelesRes.json()
    const modelesList = sortModelesAlphabetiques(modelesData)
    setModeles(modelesList)

    const modeleChoisi =
      modelesList.find((m) => String(m?.model || '').toLowerCase() === modelPrefill.toLowerCase())?.model ||
      modelesList.find((m) => String(m?.model || '').toLowerCase().includes(modelPrefill.toLowerCase()))?.model ||
      modelesList[0]?.model ||
      modelPrefill
    setModel(modeleChoisi)

    const anneesRes = await fetch(
      `${BACKEND}/api/annees?make=${encodeURIComponent(makePrefill)}&model=${encodeURIComponent(modeleChoisi)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const anneesData = await anneesRes.json()
    const anneesList = Array.isArray(anneesData) ? anneesData : []
    setAnnees(anneesList)
    const anneeChoisie = anneesList.find((a) => Number(a?.annee) === anneePrefill)?.annee || anneePrefill

    setAnnee(String(anneeChoisie))
    setKm(String(kmPrefill))

    listingContextRef.current = {
      make: makePrefill,
      model: modeleChoisi,
      titre: titreStock.slice(0, 400),
      version: versionStock.slice(0, 200),
    }

    setLoading(true)
    setResult(null)
    try {
      const kmNum = parseInt(String(kmPrefill).replace(/\s/g, ''), 10)
      const res = await fetchWithTimeout(`${BACKEND}/api/coter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          make: makePrefill,
          model: modeleChoisi,
          annee: parseInt(String(anneeChoisie), 10),
          km: kmNum,
          garantie: garPref,
          had_accident: accPref,
          ...(titreStock ? { titre_listing: titreStock.slice(0, 400) } : {}),
          ...(versionStock ? { version_listing: versionStock.slice(0, 200) } : {}),
          ...(detailsFinale ? { details_precision: String(detailsFinale).slice(0, 200) } : {}),
          ...(detailsFinale ? { details_moto: detailsFinale } : {}),
        }),
      }, 120000)
      const data = await res.json()
      if (!res.ok) {
        setError(data?.error || 'Erreur de cotation')
      } else {
        setResult(data)
        appendCotationHistory(data, {
          make: makePrefill,
          model: modeleChoisi,
          annee: String(anneeChoisie),
          km: String(kmPrefill),
        })
        invalidateRadarPageCaches()
      }
    } catch {
      setError('Erreur de connexion')
    }
    setLoading(false)
  }, [token, appendCotationHistory])

  const voirExemple = useCallback(async () => {
    if (!token || loading) return
    try {
      const modelesRes = await fetchWithTimeout(`${BACKEND}/api/modeles?make=honda`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const modelesData = await modelesRes.json()
      const modelesList = Array.isArray(modelesData) ? modelesData : []
      const modele125 =
        modelesList.find((m) => String(m?.model || '').toLowerCase() === 'sh 125')?.model ||
        modelesList.find((m) => /\b125\b/.test(String(m?.model || '').toLowerCase()))?.model ||
        'SH 125'
      await runCotationPrefill({
        make: 'honda',
        model: modele125,
        annee: 2023,
        km: 8000,
      })
    } catch {
      setError('Impossible de charger un exemple pour le moment')
    }
  }, [token, loading, runCotationPrefill])

  useEffect(() => {
    if (!prefillRequest || !token) return
    const prefillId = String(prefillRequest?.id || `${prefillRequest?.make}-${prefillRequest?.model}-${prefillRequest?.annee}-${prefillRequest?.km}`)
    if (lastPrefillIdRef.current === prefillId) return
    lastPrefillIdRef.current = prefillId

    runCotationPrefill(prefillRequest)
      .catch(() => setError('Impossible de lancer la cotation depuis le stock'))
      .finally(() => onPrefillHandled?.())
  }, [prefillRequest, token, runCotationPrefill, onPrefillHandled])

  const onCotationScroll = useCallback((e) => {
    setShowBackTop(e.currentTarget.scrollTop > 420)
  }, [])

  const scrollCotationTop = useCallback(() => {
    cotationScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const selectStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--border)',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: 14,
    cursor: 'pointer',
    appearance: 'none',
    outline: 'none',
  }

  const c = result?.cotation
  const kmNum = Number.parseInt(String(km || ''), 10)
  const margeCiblePct = Number(prefs?.marge_cible_pct) || DEFAULT_PREFS.marge_cible_pct
  const margeMinPct = Number(prefs?.marge_min_pct) || DEFAULT_PREFS.marge_min_pct
  const margePlancherPct = Math.max(margeCiblePct + 5, margeMinPct)
  const pickAnnonceImageUrl = useCallback((item) => {
    if (!item || typeof item !== 'object') return null
    if (typeof item.image_url === 'string' && item.image_url.trim()) return item.image_url.trim()
    if (typeof item.image_principale_url === 'string' && item.image_principale_url.trim()) return item.image_principale_url.trim()
    if (typeof item.photo_url === 'string' && item.photo_url.trim()) return item.photo_url.trim()
    if (typeof item.thumbnail_url === 'string' && item.thumbnail_url.trim()) return item.thumbnail_url.trim()
    if (typeof item.image === 'string' && item.image.trim()) return item.image.trim()
    if (Array.isArray(item.images)) {
      const first = item.images.find((x) => typeof x === 'string' && x.trim())
      if (first) return first.trim()
    }
    if (Array.isArray(item.photos)) {
      const first = item.photos.find((x) => typeof x === 'string' && x.trim())
      if (first) return first.trim()
    }
    return null
  }, [])

  const preparerScatterData = useCallback((annoncesGraphiques, ventesReelles, kmCible, anneeRef, valeurEstimee, motoMeta = {}) => {
    const anneeCible = Number.parseInt(String(anneeRef || ''), 10)

    const memeAnnee = (annoncesGraphiques || [])
      .filter((a) => Number(a?.annee) === anneeCible)
      .map((a) => ({
        km: Number(a.km),
        prix: Number(a.prix),
        annee: a.annee,
        ville: a.ville,
        canton: a.canton,
        garantie: a.garantie,
        titre: a.titre || a.title || null,
        model: a.model != null ? String(a.model).trim() : null,
        url: a.url,
        image_url: pickAnnonceImageUrl(a),
        baisse_recente_chf:
          Number.isFinite(Number(a.baisse_recente_chf)) && Number(a.baisse_recente_chf) > 0
            ? Number(a.baisse_recente_chf)
            : null,
        type: 'meme_annee',
      }))
      .filter((a) => Number.isFinite(a.km) && Number.isFinite(a.prix))

    const autreAnnee = (annoncesGraphiques || [])
      .filter((a) => Number(a?.annee) !== anneeCible)
      .map((a) => ({
        km: Number(a.km),
        prix: Number(a.prix),
        annee: a.annee,
        ville: a.ville,
        canton: a.canton,
        garantie: a.garantie,
        titre: a.titre || a.title || null,
        model: a.model != null ? String(a.model).trim() : null,
        url: a.url,
        image_url: pickAnnonceImageUrl(a),
        baisse_recente_chf:
          Number.isFinite(Number(a.baisse_recente_chf)) && Number(a.baisse_recente_chf) > 0
            ? Number(a.baisse_recente_chf)
            : null,
        type: 'autre_annee',
      }))
      .filter((a) => Number.isFinite(a.km) && Number.isFinite(a.prix))

    const vendues = (ventesReelles || [])
      .filter((v) => {
        const y = Number(v?.annee)
        return Number.isFinite(y) && Number.isFinite(anneeCible) && Math.abs(y - anneeCible) <= 2
      })
      .map((v) => ({
        km: Number(v.km),
        prix: Number(v.prix),
        annee: v.annee,
        jours: v.jours_en_ligne,
        titre: v.titre || v.title || null,
        model: v.model != null ? String(v.model).trim() : null,
        url: v.url || null,
        ville: v.ville || null,
        canton: v.canton || null,
        garantie: v.garantie === true,
        image_url: pickAnnonceImageUrl(v),
        date_vente: v.date_vente || null,
        baisse_recente_chf:
          Number.isFinite(Number(v.baisse_recente_chf)) && Number(v.baisse_recente_chf) > 0
            ? Number(v.baisse_recente_chf)
            : null,
        type: 'vendue',
      }))
      .filter((v) => Number.isFinite(v.km) && Number.isFinite(v.prix))

    const kmParsed = Number.parseInt(String(kmCible || ''), 10)
    const estimatedMarketPrice = Number(valeurEstimee)
    const probableCtx = resolveProbableSalePrice(
      { ...motoMeta, valeur_estimee: estimatedMarketPrice },
      estimatedMarketPrice
    )
    const probableSalePrice = Number(probableCtx.price)
    const prixVotreMoto = Number.isFinite(probableSalePrice) && probableSalePrice > 0 ? probableSalePrice : estimatedMarketPrice
    const baisseVotre =
      Number.isFinite(Number(motoMeta?.baisse_recente_chf)) && Number(motoMeta.baisse_recente_chf) > 0
        ? Number(motoMeta.baisse_recente_chf)
        : recentDropChfFromHistoriqueRows(motoMeta?.historique_prix_rows)

    const votreMoto = Number.isFinite(kmParsed) && Number.isFinite(prixVotreMoto)
      ? [{
          km: kmParsed,
          prix: prixVotreMoto,
          label: 'Votre véhicule',
          titre: motoMeta?.titre || 'Votre véhicule',
          model: motoMeta?.model != null ? String(motoMeta.model).trim() : null,
          type: 'votre_moto',
          baisse_recente_chf: hasRecentPriceDropChf(baisseVotre) ? baisseVotre : null,
          prix_reference_source:
            Number.isFinite(probableSalePrice) && probableSalePrice > 0
              ? (probableCtx.adjustedToMarket ? 'prix_vente_probable_ajuste_marche' : 'prix_vente_probable')
              : 'valeur_estimee',
          annee: Number.isFinite(anneeCible) ? anneeCible : null,
          fourchette_basse: Number.isFinite(Number(motoMeta?.fourchette_basse)) ? Number(motoMeta.fourchette_basse) : null,
          fourchette_haute: Number.isFinite(Number(motoMeta?.fourchette_haute)) ? Number(motoMeta.fourchette_haute) : null,
          confiance: Number.isFinite(Number(motoMeta?.confiance)) ? Number(motoMeta.confiance) : null,
          label_liquidite: motoMeta?.label_liquidite || null,
          prix_vente_probable:
            Number.isFinite(probableSalePrice) && probableSalePrice > 0
              ? probableSalePrice
              : null,
        }]
      : []

    const vehicleRef =
      votreMoto.length > 0
        ? { km: votreMoto[0].km, prix: votreMoto[0].prix, annee: votreMoto[0].annee, url: motoMeta?.url }
        : null
    const memeDeduped = vehicleRef
      ? filterScatterMarketPoints(memeAnnee, vehicleRef, motoMeta?.url)
      : memeAnnee
    const autreDeduped = vehicleRef
      ? filterScatterMarketPoints(autreAnnee, vehicleRef, motoMeta?.url)
      : autreAnnee

    return { memeAnnee: memeDeduped, autreAnnee: autreDeduped, vendues, votreMoto }
  }, [pickAnnonceImageUrl])

  const scatterImageByUrl = useMemo(() => {
    const map = new Map()
    const pools = [
      ...(Array.isArray(c?.annonces_graphiques) ? c.annonces_graphiques : []),
      ...(Array.isArray(c?.annonces_similaires) ? c.annonces_similaires : []),
      ...(Array.isArray(c?.stats_ventes?.dernieres_ventes) ? c.stats_ventes.dernieres_ventes : []),
    ]
    for (const item of pools) {
      const key = typeof item?.url === 'string' ? item.url.trim() : ''
      const img = pickAnnonceImageUrl(item)
      if (key && img && !map.has(key)) map.set(key, img)
    }
    return map
  }, [c, pickAnnonceImageUrl])

  const scatterData = useMemo(
    () =>
      preparerScatterData(c?.annonces_graphiques, c?.stats_ventes?.dernieres_ventes, km, annee, c?.valeur_estimee, {
        titre: `${String(make || '').toUpperCase()} ${String(model || '').toUpperCase()}`.trim(),
        model: String(model || '').trim() || null,
        fourchette_basse: c?.fourchette_basse,
        fourchette_haute: c?.fourchette_haute,
        confiance: c?.confiance,
        label_liquidite: c?.label_liquidite,
        prix_vente_probable: c?.prix_vente_probable,
        historique_prix_rows: Array.isArray(c?.historique_prix?.historique) ? c.historique_prix.historique : [],
      }),
    [c, km, annee, make, model, preparerScatterData]
  )

  const scatterDomains = useMemo(() => {
    const sv = c?.strategie_vente
    const prixStrat = sv
      ? [
          Number(sv.vente_rapide?.prix),
          Number(sv.recommande?.prix),
          Number(sv.prix_marche?.prix),
        ].filter((v) => Number.isFinite(v) && v > 0)
      : []
    const prixVals = [
      ...scatterData.memeAnnee.map((a) => a.prix),
      ...scatterData.autreAnnee.map((a) => a.prix),
      ...scatterData.vendues.map((a) => a.prix),
      Number(c?.valeur_estimee),
      ...prixStrat,
    ].filter((v) => Number.isFinite(v))

    const kmVals = [
      ...scatterData.memeAnnee.map((a) => a.km),
      ...scatterData.autreAnnee.map((a) => a.km),
      ...scatterData.vendues.map((a) => a.km),
      Number.parseInt(String(km || ''), 10),
    ].filter((v) => Number.isFinite(v))

    if (!prixVals.length || !kmVals.length) return null

    const rawPrixMin = Math.min(...prixVals)
    const rawPrixMax = Math.max(...prixVals)
    const rawKmMin = Math.min(...kmVals)
    const rawKmMax = Math.max(...kmVals)

    const kmSpan = Math.max(1, rawKmMax - rawKmMin)
    const prixSpan = Math.max(1, rawPrixMax - rawPrixMin)

    // Graduations lisibles: évite les axes bizarres quand km très faible (ex. 400–1200 km).
    const kmStep = niceStep(kmSpan, [100, 200, 500, 1000, 2000, 5000, 10000, 20000])
    const prixStep = niceStep(prixSpan, [100, 200, 500, 1000, 2000, 5000, 10000])

    const kmPad = Math.max(kmStep, Math.round(kmSpan * 0.12))
    const prixPad = Math.max(prixStep, Math.round(prixSpan * 0.1))

    let kmMin = Math.max(0, roundDownToStep(rawKmMin - kmPad, kmStep))
    let kmMax = roundUpToStep(rawKmMax + kmPad, kmStep)
    if (kmMax <= kmMin) kmMax = kmMin + kmStep

    let prixMin = Math.max(0, roundDownToStep(rawPrixMin - prixPad, prixStep))
    let prixMax = roundUpToStep(rawPrixMax + prixPad, prixStep)
    if (prixMax <= prixMin) prixMax = prixMin + prixStep

    return { prixMin, prixMax, kmMin, kmMax, kmStep, prixStep }
  }, [scatterData, c, km])

  // Désempile les points superposés (mêmes km/prix) pour qu'ils soient
  // tous lisibles au lieu de se chevaucher en un seul disque. Les valeurs
  // réelles sont préservées dans kmReal/prixReal pour le tooltip.
  const scatterDataDisplay = useMemo(() => {
    if (!scatterDomains) return scatterData
    const tagged = [
      ...scatterData.memeAnnee.map((p) => ({ ...p, _tag: 'meme' })),
      ...scatterData.autreAnnee.map((p) => ({ ...p, _tag: 'autre' })),
      ...scatterData.vendues.map((p) => ({ ...p, _tag: 'vendue' })),
      ...scatterData.votreMoto.map((p) => ({ ...p, _tag: 'moi' })),
    ]
    const jittered = applyClusterJitter(tagged, scatterDomains)
    return {
      memeAnnee: jittered.filter((p) => p._tag === 'meme'),
      autreAnnee: jittered.filter((p) => p._tag === 'autre'),
      vendues: jittered.filter((p) => p._tag === 'vendue'),
      votreMoto: jittered.filter((p) => p._tag === 'moi'),
    }
  }, [scatterData, scatterDomains])

  const optimalPriceBand = useMemo(() => {
    if (!scatterDomains) return null
    const sv = c?.strategie_vente
    const rapide = Number(sv?.vente_rapide?.prix)
    const marche = Number(sv?.prix_marche?.prix)
    if (Number.isFinite(rapide) && Number.isFinite(marche) && marche > rapide) {
      return {
        y1: Math.max(scatterDomains.prixMin, rapide),
        y2: Math.min(scatterDomains.prixMax, marche),
      }
    }
    const prixSpan = Math.max(1, scatterDomains.prixMax - scatterDomains.prixMin)
    const midRaw = Number(c?.valeur_estimee)
    const mid = Number.isFinite(midRaw) ? midRaw : scatterDomains.prixMin + prixSpan / 2
    const halfBand = Math.max(180, prixSpan * 0.06)
    return {
      y1: Math.max(scatterDomains.prixMin, mid - halfBand),
      y2: Math.min(scatterDomains.prixMax, mid + halfBand),
    }
  }, [scatterDomains, c])

  const tunnelData = useMemo(() => {
    if (!scatterDomains) return { bas: [], haut: [], yourOut: false }
    const sv = c?.strategie_vente
    const low = Number.isFinite(Number(sv?.vente_rapide?.prix)) && Number(sv?.vente_rapide?.prix) > 0
      ? Number(sv.vente_rapide.prix)
      : Number(c?.fourchette_basse)
    const high = Number.isFinite(Number(sv?.prix_marche?.prix)) && Number(sv?.prix_marche?.prix) > 0
      ? Number(sv.prix_marche.prix)
      : Number(c?.fourchette_haute)
    const base = Number.isFinite(Number(sv?.recommande?.prix)) && Number(sv?.recommande?.prix) > 0
      ? Number(sv.recommande.prix)
      : Number(c?.valeur_estimee)
    const kmTarget = Number.parseInt(String(km || ''), 10)
    if (!Number.isFinite(low) || !Number.isFinite(high) || !Number.isFinite(base) || high <= low) {
      return { bas: [], haut: [], yourOut: false }
    }
    const spanKm = Math.max(1, scatterDomains.kmMax - scatterDomains.kmMin)
    const mkPoint = (x, y) => ({ km: x, prix: y })
    const xs = [scatterDomains.kmMin, scatterDomains.kmMin + spanKm * 0.25, scatterDomains.kmMin + spanKm * 0.5, scatterDomains.kmMin + spanKm * 0.75, scatterDomains.kmMax]
    const bandHalf = (high - low) / 2
    const bas = xs.map((x) => {
      const rel = (x - (Number.isFinite(kmTarget) ? kmTarget : scatterDomains.kmMin + spanKm * 0.5)) / spanKm
      return mkPoint(x, Math.round((base - bandHalf) - rel * (spanKm * 0.0008)))
    })
    const haut = xs.map((x) => {
      const rel = (x - (Number.isFinite(kmTarget) ? kmTarget : scatterDomains.kmMin + spanKm * 0.5)) / spanKm
      return mkPoint(x, Math.round((base + bandHalf) - rel * (spanKm * 0.0008)))
    })
    const yourPrix = Number(scatterData?.votreMoto?.[0]?.prix)
    const yourOut = Number.isFinite(yourPrix) && (yourPrix < low || yourPrix > high)
    return { bas, haut, yourOut }
  }, [scatterDomains, c, km, scatterData])

  const cotationNoMatchMessage = useMemo(() => {
    const raw = String(error || '').trim()
    if (!raw) return ''
    const normalized = raw
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
    const looksLikeNoMatch =
      normalized.includes('aucune annonce') ||
      normalized.includes('pas assez d annonces') ||
      normalized.includes('pas assez de donnees') ||
      normalized.includes('aucun vehicule')
    if (!looksLikeNoMatch) return ''
    return "Aucun véhicule de notre base de données ne correspond à votre recherche actuelle. Essayez d'élargir un critère (carrosserie, finition ou année)."
  }, [error])

  const ScatterTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const d = payload[0].payload
    if (!d) return null
    const extraireTitreDepuisUrl = (url) => {
      if (!url) return ''
      try {
        const parsed = new URL(url)
        const slug = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || '')
        const cleaned = slug
          .replace(/\.(html?|php)$/i, '')
          .replace(/[-_]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
        return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : ''
      } catch {
        return ''
      }
    }
    const titreAffiche =
      d.titre || d.label || extraireTitreDepuisUrl(d.url) || (d.type === 'vendue' ? 'Vente confirmée' : 'Annonce marché')
    const nbVentesCote = Number(c?.stats_ventes?.nb_ventes) || 0
    const probableCtx = resolveProbableSalePrice(c)
    const refCote =
      Number(probableCtx.price) > 0 && nbVentesCote >= 3 ? Number(probableCtx.price) : Number(c?.valeur_estimee)
    // En cas de jitter, prixReal/kmReal contiennent les valeurs originales.
    const prixOrig = Number(d?.prixReal != null ? d.prixReal : d.prix)
    const kmOrig = Number(d?.kmReal != null ? d.kmReal : d.km)
    const ecartChf = Number.isFinite(refCote) && refCote > 0 ? prixOrig - refCote : null
    const ecartPct = ecartChf != null && refCote !== 0
      ? Math.round((ecartChf / refCote) * 100)
      : null
    const dropLine = formatRecentDropLine(d.baisse_recente_chf)
    const typeLabel = d.type === 'meme_annee'
      ? 'Annonce même année'
      : d.type === 'autre_annee'
        ? 'Annonce autre année'
        : d.type === 'vendue'
          ? 'Vente réelle confirmée'
          : 'Votre véhicule'
    const prixPoint = prixOrig
    const kmPoint = kmOrig
    const tooltipImage = d.type !== 'votre_moto'
      ? (d.image_url || (d.url ? scatterImageByUrl.get(String(d.url)) : null))
      : null
    return (
      <div style={{ background: 'var(--surface-alt)', border: '1px solid rgba(0,30,80,0.35)', borderRadius: 6, padding: '9px 12px', fontSize: 12, minWidth: 260, maxWidth: 360 }}>
        {tooltipImage && (
          <img
            src={tooltipImage}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 6, marginBottom: 8, border: '1px solid rgba(255,255,255,0.08)' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        )}
        <div style={{ color: '#bfdbfe', fontSize: 10, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontVariant: 'small-caps' }}>
          {typeLabel}
        </div>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 12, lineHeight: 1.35, marginBottom: 6 }}>
          {titreAffiche}
        </div>
        <div style={{ color: '#fff', fontWeight: 600 }}>
          {Number.isFinite(prixPoint) ? `${prixPoint.toLocaleString('fr-CH')} CHF` : 'Prix indisponible'}
          {ecartChf != null && (
            <span style={{ color: ecartChf > 0 ? '#f87171' : '#4ade80', marginLeft: 6, fontWeight: 600 }}>
              ({ecartChf > 0 ? '+' : ''}{ecartChf.toLocaleString('fr-CH')} CHF{ecartPct != null ? ` · ${ecartPct > 0 ? '+' : ''}${ecartPct}%` : ''})
            </span>
          )}
        </div>
        {dropLine ? (
          <div style={{ color: '#fecaca', fontSize: 11, fontWeight: 600, marginTop: 4 }}>{dropLine}</div>
        ) : null}
        <div style={{ color: 'rgba(255,255,255,0.5)' }}>
          {Number.isFinite(kmPoint) ? `${kmPoint.toLocaleString('fr-CH')} km` : 'km n.d.'} {d.annee ? ` · ${d.annee}` : ''}
        </div>
        {d.type === 'votre_moto' && (
          <div style={{ color: '#bfdbfe', fontSize: 11, marginTop: 2 }}>
            Recherche : {d.titre}{d.annee ? ` · ${d.annee}` : ''}
          </div>
        )}
        {d.label && d.type !== 'votre_moto' && <div style={{ color: '#bfdbfe', fontSize: 11, marginTop: 2 }}>{d.label}</div>}
        {d.type === 'votre_moto' && d.prix_vente_probable != null && (
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 2 }}>
            Vente probable : {Number(d.prix_vente_probable).toLocaleString('fr-CH')} CHF
          </div>
        )}
        {d.type === 'votre_moto' && d.fourchette_basse != null && d.fourchette_haute != null && (
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 2 }}>
            Fourchette : {Number(d.fourchette_basse).toLocaleString('fr-CH')} - {Number(d.fourchette_haute).toLocaleString('fr-CH')} CHF
          </div>
        )}
        {d.type === 'votre_moto' && (d.confiance != null || d.label_liquidite) && (
          <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, marginTop: 2 }}>
            {d.confiance != null ? `Confiance : ${d.confiance}` : ''}{d.confiance != null && d.label_liquidite ? ' · ' : ''}{d.label_liquidite || ''}
          </div>
        )}
        {d.jours && <div style={{ color: '#f87171', fontSize: 11, marginTop: 2 }}>Vendu en {d.jours} jours</div>}
        {d.date_vente && (
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
            Vente confirmée: {new Date(d.date_vente).toLocaleDateString('fr-CH')}
          </div>
        )}
        {d.garantie && <div style={{ color: '#4ade80', fontSize: 10, marginTop: 2 }}>✓ Garantie</div>}
        {d.ville && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>{d.ville} {d.canton}</div>}
        {d.url && (
          <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, marginTop: 4 }}>
            Double-cliquez sur le point pour ouvrir l&apos;annonce
          </div>
        )}
      </div>
    )
  }

  const tronquerTitre = (txt, max = 24) => {
    const s = String(txt || '').trim()
    if (!s) return ''
    return s.length > max ? `${s.slice(0, max - 1)}…` : s
  }

  const cancelMarcheScatterNav = useCallback(() => {
    if (marcheScatterNavTimerRef.current) {
      clearTimeout(marcheScatterNavTimerRef.current)
      marcheScatterNavTimerRef.current = null
    }
  }, [])

  const scheduleMarcheScatterNav = useCallback(
    (payload) => {
      if (!onOpenMarcheFicheModel) return
      cancelMarcheScatterNav()
      marcheScatterNavTimerRef.current = setTimeout(() => {
        marcheScatterNavTimerRef.current = null
        const mk = String(payload?.model || model || '')
          .trim()
          .toLowerCase()
        if (mk) onOpenMarcheFicheModel(mk)
      }, 300)
    },
    [onOpenMarcheFicheModel, model, cancelMarcheScatterNav]
  )

  useEffect(() => () => cancelMarcheScatterNav(), [cancelMarcheScatterNav])

  const PointShape = ({
    cx,
    cy,
    fill,
    stroke,
    strokeWidth,
    payload,
    r = 6,
    showTitle = false,
    titleColor = 'rgba(255,255,255,0.45)',
    hollow = true,
    glow = false,
    onMarcheNavigate,
    onMarcheNavigateCancel,
  }) => {
    const recentDrop = hasRecentPriceDropChf(payload?.baisse_recente_chf)
    return (
    <g
      onClick={(e) => {
        e.stopPropagation()
        onMarcheNavigate?.(payload)
      }}
      onDoubleClick={(e) => {
        e.stopPropagation()
        onMarcheNavigateCancel?.()
        if (payload?.url) window.open(payload.url, '_blank', 'noopener,noreferrer')
      }}
      style={{ cursor: (payload?.url || onMarcheNavigate) ? 'pointer' : 'default' }}
    >
      {/* Hitbox invisible plus large pour stabiliser le hover tooltip sur les points. */}
      <circle
        cx={cx}
        cy={cy}
        r={Math.max(r + 7, 12)}
        fill="rgba(0,0,0,0.001)"
        stroke="none"
      />
      {recentDrop ? (
        <circle
          cx={cx}
          cy={cy}
          r={r + 3.2}
          fill="none"
          stroke="rgba(248,113,113,0.72)"
          strokeWidth={2}
          pointerEvents="none"
        />
      ) : null}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={hollow ? 'none' : fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        style={glow ? { filter: 'drop-shadow(0 0 6px rgba(0,30,80,0.6))' } : undefined}
      >
        {payload?.titre ? <title>{payload.titre}</title> : null}
      </circle>
      {showTitle && payload?.titre && (
        <text
          x={cx + r + 3}
          y={cy - r - 1}
          fill={titleColor}
          fontSize={9}
          pointerEvents="none"
        >
          {tronquerTitre(payload.titre)}
        </text>
      )}
    </g>
    )
  }

  const scatterPointShapeProps =
    onOpenMarcheFicheModel != null
      ? { onMarcheNavigate: scheduleMarcheScatterNav, onMarcheNavigateCancel: cancelMarcheScatterNav }
      : {}

  const CotationMiniPriceBar = ({ basse, haute, valeur, markerColor }) => {
    const lo = Number(basse)
    const hi = Number(haute)
    const v = Number(valeur)
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo || !Number.isFinite(v)) return null
    const pct = Math.max(2, Math.min(98, ((v - lo) / (hi - lo)) * 100))
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, rgba(248,113,113,0.22), rgba(251,191,36,0.25), rgba(74,222,128,0.28))',
            }}
          />
          <div
            title="Position estimée dans la fourchette basse–haute"
            style={{
              position: 'absolute',
              left: `${pct}%`,
              top: -2,
              width: 3,
              height: 10,
              marginLeft: -1.5,
              borderRadius: 1,
              background: markerColor || '#e2e8f0',
              boxShadow: '0 0 0 1px rgba(15,23,42,0.5)',
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            fontSize: 9,
            color: 'rgba(226,232,240,0.38)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span>{lo.toLocaleString('fr-CH')}</span>
          <span>{hi.toLocaleString('fr-CH')}</span>
        </div>
      </div>
    )
  }

  const LIQ_SCORE_TOOLTIP =
    'Score sur 10 combinant la dispersion des prix sur les annonces comparables, le volume de références, la qualité du modèle statistique (R², pondération régression / similarité) et les ventes réelles observées. Un score plus élevé indique en général une rotation plus rapide à prix cohérent.'

  const LiquidityGauge = ({ score, label, r2 }) => {
    const n = Number(score)
    const safe = Number.isFinite(n) ? Math.max(0, Math.min(10, n)) : 0
    const scoreColor = safe >= 7 ? '#bbf7d0' : safe >= 4 ? '#fde68a' : '#fecaca'
    const RotIcon = safe >= 8 ? Zap : safe >= 4 ? Hourglass : Flame
    const chipLabel = safe >= 8 ? 'Rotation rapide' : safe >= 4 ? 'Rotation normale' : 'Rotation lente'
    const chipBorder = safe >= 8 ? 'rgba(74,222,128,0.45)' : safe >= 4 ? 'rgba(251,191,36,0.5)' : 'rgba(248,113,113,0.45)'
    const chipBg = safe >= 8 ? 'rgba(22,101,52,0.25)' : safe >= 4 ? 'rgba(161,98,7,0.18)' : 'rgba(127,29,29,0.22)'
    const cone = `conic-gradient(from -90deg, rgba(34,197,94,0.55) 0deg, rgba(251,191,36,0.5) ${72 + safe * 10}deg, rgba(248,113,113,0.55) ${200 + safe * 8}deg, rgba(23,26,31,0.65) 360deg)`
    return (
      <div style={{ textAlign: 'right' }}>
        <div
          title={LIQ_SCORE_TOOLTIP}
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'help',
            position: 'relative',
            width: 120,
            height: 120,
          }}
        >
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: cone }} />
          <div
            style={{
              position: 'absolute',
              inset: 5,
              borderRadius: '50%',
              background: 'rgba(23,26,31,0.94)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                fontSize: 30,
                fontWeight: 300,
                fontVariantNumeric: 'tabular-nums',
                color: scoreColor,
                lineHeight: 1,
              }}
            >
              {safe.toFixed(1)}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(226,232,240,0.42)' }}>/10</span>
            {Number.isFinite(Number(r2)) && (
              <span style={{ fontSize: 9, color: 'rgba(226,232,240,0.28)', marginTop: 2 }}>
                R² {Number(r2).toFixed(2)}
              </span>
            )}
          </div>
        </div>
        <div
          style={{
            marginTop: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            borderRadius: 6,
            border: `1px solid ${chipBorder}`,
            background: chipBg,
            padding: '6px 12px',
            fontSize: 11,
            fontWeight: 700,
            color: '#f1f5f9',
          }}
        >
          <RotIcon size={16} strokeWidth={2} aria-hidden style={{ opacity: 0.92 }} />
          {chipLabel}
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>{label || 'Liquidité estimée'}</div>
      </div>
    )
  }

  return (
    <div
      ref={cotationScrollRef}
      onScroll={onCotationScroll}
      style={{ flex: 1, overflow: 'auto', padding: isMobileLayout ? '0.8rem' : 'clamp(1rem, 2.2vw, 2rem)', width: '100%' }}
    >
      <div style={{ display: 'flex', gap: isMobileLayout ? 10 : 16, alignItems: 'stretch', width: '100%', flexDirection: isMobileLayout ? 'column' : 'row', position: 'relative' }}>
        {isMobileLayout && cotationHistoryOpen ? (
          <button
            type="button"
            aria-label="Fermer l'historique"
            onClick={() => setCotationHistoryOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 39,
              border: 'none',
              padding: 0,
              margin: 0,
              background: 'rgba(17,21,26,0.55)',
              cursor: 'pointer',
            }}
          />
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 6,
            flexWrap: isMobileLayout ? 'wrap' : 'nowrap',
          }}
        >
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', margin: 0, flex: 1, minWidth: 0 }}>
            Cotation rapide
          </h2>
          {isMobileLayout ? (
            <button
              type="button"
              onClick={() => setCotationHistoryOpen((o) => !o)}
              aria-expanded={cotationHistoryOpen}
              title={cotationHistoryOpen ? "Fermer l'historique" : "Afficher l'historique des cotations"}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.12)',
                background: cotationHistoryOpen ? 'rgba(0,30,80,0.42)' : 'rgba(255,255,255,0.05)',
                color: '#e2e8f0',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <History size={17} aria-hidden strokeWidth={2} />
              Historique
              {cotationHistory.length > 0 ? (
                <span style={{ fontVariantNumeric: 'tabular-nums', color: 'rgba(226,232,240,0.55)', fontWeight: 800 }}>
                  ({cotationHistory.length})
                </span>
              ) : null}
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={voirExemple}
          disabled={loading}
          style={{
            marginBottom: 8,
            padding: '6px 10px',
            borderRadius: 6,
            border: '1px solid rgba(0,30,80,0.4)',
            background: 'rgba(0,30,80,0.14)',
            color: '#bfdbfe',
            fontSize: 12,
            fontWeight: 700,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          Voir un exemple
        </button>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>
          Sélectionnez un véhicule pour obtenir une cotation instantanée
        </p>
      </div>

      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--border)',
          borderRadius: 16,
          padding: isMobileLayout ? '1rem' : '1.5rem',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
              Marque
            </label>
            <select style={selectStyle} value={make} onChange={(e) => setMake(e.target.value)}>
              <option value="">Sélectionner...</option>
              {marques.map((m) => (
                <option key={m.make} value={m.make} style={{ background: '#1a1a1a' }}>
                  {String(m.make).charAt(0).toUpperCase() + String(m.make).slice(1)} ({m.nb})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
              Modèle
            </label>
            <select style={selectStyle} value={model} onChange={(e) => setModel(e.target.value)} disabled={!make}>
              <option value="">Sélectionner...</option>
              {modeles.map((m) => (
                <option key={m.model} value={m.model} style={{ background: '#1a1a1a' }}>
                  {String(m.model).toUpperCase()} ({labelNbModeleCotation(m)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
              Année
            </label>
            <select style={selectStyle} value={annee} onChange={(e) => setAnnee(e.target.value)} disabled={!model}>
              <option value="">Sélectionner...</option>
              {annees.map((a) => (
                <option key={a.annee} value={a.annee} style={{ background: '#1a1a1a' }}>
                  {a.annee} ({a.nb})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
            Kilométrage
          </label>
          <input
            style={{ ...selectStyle, cursor: 'text' }}
            type="number"
            placeholder="ex: 15000"
            value={km}
            onChange={(e) => setKm(e.target.value)}
          />
        </div>

        {(autoPrecisionEnabled || variantesLoading) ? (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
              Motorisation (base)
            </label>
            <select
              style={selectStyle}
              value={motorisationChoisie}
              onChange={(e) => setMotorisationChoisie(e.target.value)}
              disabled={!model || !annee}
            >
              <option value="" style={{ background: '#1a1a1a', color: '#fff' }}>Non spécifiée (toutes)</option>
              {cotationMotorisations.map((m) => (
                <option key={m.value} value={m.value} style={{ background: '#1a1a1a' }}>
                  {m.value} ({m.nb})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
              Carrosserie
            </label>
            <select
              style={selectStyle}
              value={carrosserieChoisie}
              onChange={(e) => setCarrosserieChoisie(e.target.value)}
              disabled={!model}
            >
              <option value="" style={{ background: '#1a1a1a', color: '#fff' }}>Aucune</option>
              {cotationCarrosseries.map((v) => (
                <option key={v.value} value={v.value} style={{ background: '#1a1a1a' }}>
                  {formatCarrosserieLabel(v.value)} ({v.nb})
                </option>
              ))}
            </select>
          </div>
        </div>
        {variantesLoading ? (
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'rgba(226,232,240,0.45)' }}>Chargement des variantes moteur/carrosserie...</p>
        ) : null}
        </>
        ) : null}

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
            Finition / précision (optionnel)
          </label>
          <textarea
            style={{
              ...selectStyle,
              cursor: 'text',
              minHeight: 72,
              resize: 'vertical',
              lineHeight: 1.45,
            }}
            maxLength={400}
            rows={3}
            placeholder="ex. DCT, i Mode, CB 300 R, Fireblade SP, Africa Twin Adventure Sports…"
            value={detailsMoto}
            onChange={(e) => setDetailsMoto(e.target.value)}
          />
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: '6px 0 0', lineHeight: 1.4 }}>
            Sert à rapprocher votre véhicule des bonnes annonces marché (même cylindrée, boîte DCT, finition, etc.).
          </p>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
          {[
            { label: 'Avec garantie', val: garantie, set: setGarantie },
            { label: 'Accident déclaré', val: accident, set: setAccident },
          ].map((opt, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--muted)' }}>
              <input
                type="checkbox"
                checked={opt.val}
                onChange={(e) => opt.set(e.target.checked)}
                style={{ accentColor: '#001E50', width: 16, height: 16 }}
              />
              {opt.label}
            </label>
          ))}
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 11, color: 'rgba(226,232,240,0.42)', lineHeight: 1.45 }}>
          Prises en compte après clic sur « Obtenir la cotation » : accident (−12 % sur la base), garantie (bonus si peu d’annonces comparables l’affichent).
        </p>

        {error && !loading && !cotationNoMatchMessage && (
          <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}
        {cotationNoMatchMessage && !loading && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 6,
              border: '1px solid rgba(148,163,184,0.22)',
              background: 'rgba(148,163,184,0.08)',
              color: 'rgba(226,232,240,0.92)',
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {cotationNoMatchMessage}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobileLayout ? 'stretch' : 'flex-end', gap: 6 }}>
          <button
            type="button"
            onClick={coter}
            disabled={loading || !make || !model || !annee || !km}
            style={{
              width: isMobileLayout ? '100%' : 'auto',
              maxWidth: isMobileLayout ? 'none' : 280,
              padding: '0.75rem 1.35rem',
              borderRadius: 6,
              background:
                loading || !make || !model || !annee || !km
                  ? 'rgba(0,30,80,0.3)'
                  : 'linear-gradient(135deg, #001E50, #1e3a8a)',
              border: 'none',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'Calcul en cours...' : 'Obtenir la cotation'}
          </button>
          <span style={{ fontSize: 10, color: 'rgba(226,232,240,0.42)', fontWeight: 500 }}>
            Raccourci : <kbd style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.05)', fontSize: 10 }}>Ctrl</kbd>{' '}
            +{' '}
            <kbd style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.05)', fontSize: 10 }}>Entrée</kbd>
          </span>
        </div>
      </div>

      {!loading && c && (
        <div
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: isMobileLayout ? '1rem' : '1.5rem',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: '#bfdbfe', marginBottom: detailsMoto.trim() ? 6 : '1.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {c.modele?.toUpperCase()} · {annee} · {Number(km || 0).toLocaleString('fr-CH')} km
          </div>
          {detailsMoto.trim() ? (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: '1.25rem', lineHeight: 1.45 }}>
              Précision : {detailsMoto.trim()}
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: isMobileLayout ? 'stretch' : 'center',
              justifyContent: 'space-between',
              flexDirection: isMobileLayout ? 'column' : 'row',
              gap: isMobileLayout ? 14 : 0,
              padding: isMobileLayout ? '0.9rem 1rem' : '1.2rem 1.5rem',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 14,
              marginBottom: 12,
            }}
          >
            <div>
              {(() => {
                const probableCtx = resolveProbableSalePrice(c)
                const pvp = probableCtx.price
                const ve = c.valeur_estimee
                const hasPvp = Number.isFinite(Number(pvp)) && Number(pvp) > 0
                const hasVe = Number.isFinite(Number(ve)) && Number(ve) > 0
                const differe =
                  hasPvp && hasVe && Math.round(Number(pvp)) !== Math.round(Number(ve))
                return (
                  <>
                    {hasPvp ? (
                      <>
                        <div
                          style={{
                            fontSize: 11,
                            color: '#bfdbfe',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            marginBottom: 4,
                            fontWeight: 700,
                          }}
                        >
                          Prix recommandé (ventes réelles)
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, rowGap: 10 }}>
                          <div style={{ fontSize: 36, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
                            {Number(pvp).toLocaleString('fr-CH')} CHF
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                              padding: '6px 12px',
                              borderRadius: 4,
                              border: '1px solid rgba(255,255,255,0.12)',
                              background: 'rgba(0,30,80,0.32)',
                              color: 'rgba(241,245,249,0.95)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Confiance {String(c.confiance || '—').replace(/^\w/, (x) => x.toUpperCase())} ·{' '}
                            {c.nb_annonces_reference} annonces · {c.stats_ventes?.nb_ventes || 0} ventes
                            {probableCtx.adjustedToMarket ? ' · plafonné au prix marché' : ''}
                          </div>
                        </div>
                        {differe ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: 'rgba(255,255,255,0.42)',
                              marginTop: 10,
                              paddingTop: 10,
                              borderTop: '1px solid rgba(255,255,255,0.08)',
                              lineHeight: 1.45,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 10,
                                color: 'var(--muted)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                marginBottom: 4,
                              }}
                            >
                              Estimation sur annonces actives
                            </div>
                            <span style={{ fontSize: 18, fontWeight: 800, color: '#e5e5e5' }}>
                              {Number(ve).toLocaleString('fr-CH')} CHF
                            </span>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginTop: 4 }}>
                              Indicatif complémentaire (annonces en ligne) : le stock affiche la référence ventes en
                              priorité.
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginTop: 6, lineHeight: 1.4 }}>
                            C&apos;est le même chiffre que le « prix marché » sur Mon Stock lorsque des ventes réelles
                            sont disponibles.
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            marginBottom: 4,
                          }}
                        >
                          Valeur marché estimée
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, rowGap: 10 }}>
                          <div style={{ fontSize: 36, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
                            {hasVe ? Number(ve).toLocaleString('fr-CH') : '—'} CHF
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              letterSpacing: '0.02em',
                              padding: '6px 12px',
                              borderRadius: 4,
                              border: '1px solid rgba(255,255,255,0.12)',
                              background: 'rgba(0,30,80,0.32)',
                              color: 'rgba(241,245,249,0.95)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Confiance {String(c.confiance || '—').replace(/^\w/, (x) => x.toUpperCase())} ·{' '}
                            {c.nb_annonces_reference} annonces · {c.stats_ventes?.nb_ventes || 0} ventes
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)', marginTop: 6 }}>
                          Pas assez de ventes récentes pour un prix de vente probable — référence basée sur les
                          annonces actives.
                        </div>
                      </>
                    )}
                  </>
                )
              })()}
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', marginTop: 10 }}>
                Fourchette{' '}
                {Number.isFinite(Number(c.fourchette_basse))
                  ? Number(c.fourchette_basse).toLocaleString('fr-CH')
                  : '—'}{' '}
                –{' '}
                {Number.isFinite(Number(c.fourchette_haute))
                  ? Number(c.fourchette_haute).toLocaleString('fr-CH')
                  : '—'}{' '}
                CHF · Délai moyen observé ~ {c.stats_ventes?.jours_moyen_vente ?? '—'} jours
              </div>
            </div>

            <div style={{ textAlign: isMobileLayout ? 'left' : 'right' }}>
              <LiquidityGauge score={c.score_liquidite} label={c.label_liquidite} r2={c.r2} />
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>
                Délai estimé ~{c.stats_ventes?.jours_moyen_vente || '—'} jours
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              alignItems: 'center',
              padding: '14px 0 4px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              marginBottom: 8,
            }}
          >
            {[
              { Icon: FileDown, label: 'Exporter PDF', onClick: () => window.alert('Export PDF : à brancher (génération document).') },
              { Icon: Mail, label: 'Envoyer au client', onClick: () => window.alert('Envoi client : à brancher (e-mail / lien partageable).') },
              { Icon: BookmarkPlus, label: 'Sauvegarder', onClick: () => window.alert("Sauvegarde : déjà mémorisée dans l'historique local à droite.") },
              { Icon: Link2, label: 'Appliquer à une annonce', onClick: () => window.alert('Associer à une annonce : ouvrez Mon stock et lancez la cotation depuis une ligne.') },
            ].map(({ Icon, label, onClick }, i) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                {i > 0 ? <span style={{ color: 'rgba(226,232,240,0.2)', padding: '0 6px', fontSize: 12 }}>·</span> : null}
                <button
                  type="button"
                  onClick={onClick}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    border: 'none',
                    background: 'none',
                    color: '#bfdbfe',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: '4px 2px',
                    fontFamily: 'inherit',
                  }}
                >
                  <Icon size={15} strokeWidth={1.75} aria-hidden />
                  {label}
                </button>
              </span>
            ))}
          </div>

          <div
            style={{
              padding: '1.2rem 1.5rem',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 14,
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Stratégie de vente
            </div>
            {(() => {
              const sv = c.strategie_vente
              const probableCtx = resolveProbableSalePrice(c)
              const pvp = probableCtx.price
              const nbVentes = c.stats_ventes?.nb_ventes || 0
              const delaiMoyen = c.stats_ventes?.jours_moyen_vente
              // FIX 2026-05 : si le recommande a ete plafonne au prix marche, on
              // s'assure que `vente_rapide` reste strictement inferieur (sinon
              // la carte « Vente rapide » afficherait un prix >= « Recommande »).
              const recoFinal = Number.isFinite(Number(pvp)) && Number(pvp) > 0
                ? Number(pvp)
                : sv?.recommande?.prix
              const venteRapidePrix = Number.isFinite(Number(sv?.vente_rapide?.prix)) && Number.isFinite(Number(recoFinal))
                ? Math.min(Number(sv.vente_rapide.prix), Number(recoFinal) - 50)
                : sv?.vente_rapide?.prix
              const scenarios = sv
                ? [
                    {
                      label: 'Vente rapide',
                      prix: venteRapidePrix,
                      delai: sv.vente_rapide?.jours_estimes ?? 38,
                      desc:
                        sv.vente_rapide?.ecart_vs_marche_pct != null
                          ? `−${sv.vente_rapide.ecart_vs_marche_pct}% vs marché`
                          : '— vs marché',
                      color: '#4ade80',
                      highlight: false,
                      tooltip: sv.vente_rapide?.description || 'Prix agressif pour vendre vite',
                    },
                    {
                      label: 'Recommandé',
                      // FIX 2026-05 : on aligne sur `resolveProbableSalePrice(c)`
                      // utilisé par le bloc "Prix recommandé (ventes réelles)" du
                      // header. Il plafonne au prix marché quand peu de ventes
                      // (< 6). Sinon header=6 498 et footer=6 700 → incohérence.
                      prix: Number.isFinite(Number(pvp)) && Number(pvp) > 0 ? Number(pvp) : sv.recommande?.prix,
                      delai: sv.recommande?.jours_estimes ?? 68,
                      desc: probableCtx.adjustedToMarket
                        ? `Basé sur ${nbVentes} ventes réelles + ${sv.nb_voisins_stricts ?? 0} annonces (ajusté au marché)`
                        : sv.recommande?.footer || `Basé sur ${nbVentes} ventes réelles + ${sv.nb_voisins_stricts ?? 0} annonces`,
                      color: '#60efbc',
                      highlight: true,
                      tooltip: sv.recommande?.description || 'Compromis marge / vitesse',
                    },
                    {
                      label: 'Prix marché',
                      prix: sv.prix_marche?.prix ?? c.valeur_estimee,
                      delai: sv.prix_marche?.jours_estimes ?? 115,
                      desc: 'Prix affiché moyen',
                      color: '#bfdbfe',
                      highlight: false,
                      tooltip: sv.prix_marche?.description || 'Prix moyen des annonces similaires actives',
                    },
                  ]
                : [
                    {
                      label: 'Vente rapide',
                      prix: Math.round((c.valeur_estimee * 0.93) / 50) * 50,
                      delai: delaiMoyen ? Math.round(delaiMoyen * 0.55) : 38,
                      desc: '-7% vs marché',
                      color: '#4ade80',
                      highlight: false,
                      tooltip: 'Prix agressif pour vendre vite',
                    },
                    {
                      label: 'Recommandé',
                      prix: pvp || Math.round((c.valeur_estimee * 0.96) / 50) * 50,
                      delai: delaiMoyen ? Math.round(delaiMoyen * 0.72) : 68,
                      desc: pvp
                        ? probableCtx.adjustedToMarket
                          ? `Basé sur ${nbVentes} ventes réelles (ajusté au marché)`
                          : `Basé sur ${nbVentes} ventes réelles`
                        : 'Estimation marché',
                      color: '#60efbc',
                      highlight: true,
                      tooltip: 'Prix auquel ce véhicule va probablement se vendre',
                    },
                    {
                      label: 'Prix marché',
                      prix: c.valeur_estimee,
                      delai: delaiMoyen ? Math.round(delaiMoyen * 1.12) : 115,
                      desc: 'Prix affiché moyen',
                      color: '#bfdbfe',
                      highlight: false,
                      tooltip: 'Prix moyen des annonces similaires actives',
                    },
                  ]
              return (
                <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(3, 1fr)', gap: 12, alignItems: 'stretch' }}>
                  {scenarios.map((s, i) => (
                    <div
                      key={`sc-${s.label}-${i}`}
                      title={s.tooltip}
                      style={{
                        padding: '1rem',
                        borderRadius: 6,
                        textAlign: 'center',
                        position: 'relative',
                        transform: s.highlight ? 'translateY(-8px)' : undefined,
                        boxShadow: s.highlight ? '0 14px 32px rgba(0,0,0,0.28)' : undefined,
                        border: s.highlight ? '1px solid transparent' : '1px solid rgba(255,255,255,0.06)',
                        background: s.highlight
                          ? 'linear-gradient(180deg, rgba(26,29,34,0.98), rgba(20,22,26,0.99)) padding-box, linear-gradient(135deg, rgba(74,222,128,0.65), rgba(0,30,80,0.92), rgba(96,239,188,0.45)) border-box'
                          : 'rgba(255,255,255,0.02)',
                        backgroundOrigin: s.highlight ? 'border-box' : undefined,
                        backgroundClip: s.highlight ? 'padding-box, border-box' : undefined,
                      }}
                    >
                      <div style={{ minHeight: 18, marginBottom: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <span style={{ fontSize: 9, opacity: 0 }} aria-hidden>
                          {'\u00A0'}
                        </span>
                      </div>
                      <div style={{ fontSize: 10, color: s.color, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {s.label}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', marginBottom: 4 }}>
                        {Number(s.prix)?.toLocaleString()} CHF
                      </div>
                      <CotationMiniPriceBar basse={c.fourchette_basse} haute={c.fourchette_haute} valeur={s.prix} markerColor={s.color} />
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>~{s.delai} jours</div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{s.desc}</div>
                    </div>
                  ))}
                </div>
              )
            })()}
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center', marginTop: 10 }}>
              Basé sur {c.nb_annonces_reference} annonces · {c.stats_ventes?.nb_ventes || 0} ventes confirmées
            </div>
          </div>

          <div
            style={{
              padding: '1.2rem 1.5rem',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 14,
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Prix de reprise concessionnaire
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: 10, textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Plancher</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#f87171' }}>
                  {c.prix_reprise?.min?.toLocaleString()} CHF
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>Marge {margePlancherPct}%</div>
              </div>
              <div
                style={{
                  borderLeft: '1px solid rgba(255,255,255,0.06)',
                  padding: '0 10px',
                }}
              >
                <div style={{ fontSize: 10, color: '#bfdbfe', marginBottom: 4, fontWeight: 700 }}>Cible recommandée</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#fff' }}>
                  {c.prix_reprise?.cible?.toLocaleString()} CHF
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>Marge {margeCiblePct}%</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center', marginTop: 10 }}>
              Frais reconditionnement estimés : {c.prix_reprise?.frais_recond?.toLocaleString()} CHF inclus
            </div>
          </div>

          {result && (() => {
            const probableCtx = resolveProbableSalePrice(result.cotation)
            const valeurRaw = probableCtx.price ?? result.cotation.valeur_estimee
            const valeur = Number.isFinite(Number(valeurRaw)) ? Number(valeurRaw) : null
            if (valeur == null || valeur <= 0) return null
            const baseLabel = probableCtx.hadProbable
              ? `prix vente probable ${valeur.toLocaleString()} CHF`
              : `valeur marché ${valeur.toLocaleString()} CHF`
            const fraisPrefs = Number.isFinite(kmNum)
              ? kmNum > 30000
                ? Number(prefs?.frais_recond_haut) || DEFAULT_PREFS.frais_recond_haut
                : kmNum > 15000
                  ? Number(prefs?.frais_recond_moyen) || DEFAULT_PREFS.frais_recond_moyen
                  : Number(prefs?.frais_recond_bas) || DEFAULT_PREFS.frais_recond_bas
              : Number(prefs?.frais_recond_moyen) || DEFAULT_PREFS.frais_recond_moyen
            const frais = result.cotation.prix_reprise?.frais_recond || fraisPrefs
            const delaiMoyen = result.cotation.stats_ventes?.jours_moyen_vente || 90
            const prixRachatNum = Number.parseInt(prixRachat, 10)

            const marge = Number.isFinite(prixRachatNum)
              ? Math.round(valeur - prixRachatNum - frais)
              : null
            const margePct = marge !== null
              ? Math.round((marge / valeur) * 100)
              : null
            const verdict = margePct === null
              ? null
              : margePct >= 20
                ? { label: 'Excellente affaire', color: '#4ade80' }
                : margePct >= 12
                  ? { label: 'Marge correcte', color: '#bfdbfe' }
                  : margePct >= 5
                    ? { label: 'Marge faible', color: '#fbbf24' }
                    : { label: 'Attention au risque de perte', color: '#f87171' }

            return (
              <div
                style={{
                  padding: '1.5rem',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 16,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: 16,
                  }}
                >
                  Simulateur de reprise
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>
                      Prix de rachat envisag?
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        value={prixRachat}
                        onChange={(e) => setPrixRachat(e.target.value)}
                        placeholder="ex: 7000"
                        style={{
                          width: '100%',
                          padding: '10px 50px 10px 14px',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 10,
                          color: '#fff',
                          fontSize: 16,
                          fontWeight: 700,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          right: 14,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: 13,
                          color: 'rgba(255,255,255,0.4)',
                        }}
                      >
                        CHF
                      </span>
                    </div>
                  </div>
                </div>

                {marge !== null && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr 1fr',
                      gap: 10,
                      padding: '1rem',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 12,
                      border: `1px solid ${verdict.color}33`,
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>Marge estimée</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: marge >= 0 ? '#4ade80' : '#f87171' }}>
                        {marge >= 0 ? '+' : ''}
                        {marge.toLocaleString()} CHF
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
                        {margePct >= 0 ? '+' : ''}
                        {margePct}%
                      </div>
                    </div>

                    <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.06)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>Délai revente</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>~{delaiMoyen}j</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
                        estimé marché
                      </div>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>Verdict</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: verdict.color, marginTop: 6 }}>
                        {verdict.label}
                      </div>
                    </div>
                  </div>
                )}

                {marge !== null && (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', marginTop: 10, textAlign: 'center' }}>
                    Calcul : {prixRachatNum.toLocaleString()} CHF rachat + {frais} CHF recond. ? revente sur base {baseLabel}
                  </div>
                )}
              </div>
            )
          })()}

          {c.stats_ventes && (
            <div style={{ padding: '1rem', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Ventes réelles ({c.stats_ventes.nb_ventes} ventes similaires)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Prix moyen vente</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{c.stats_ventes.prix_moyen_vente?.toLocaleString('fr-CH')} CHF</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Délai moyen</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{c.stats_ventes.jours_moyen_vente} jours</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Fourchette ventes</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
                    {c.stats_ventes.prix_min_vente?.toLocaleString('fr-CH')} - {c.stats_ventes.prix_max_vente?.toLocaleString('fr-CH')} CHF
                  </div>
                </div>
              </div>
            </div>
          )}

          {scatterDomains && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '1.85rem', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontVariant: 'small-caps', marginBottom: 4 }}>
                Prix vs Kilométrage
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16 }}>
                Blanc = même année · Gris = autre année · Rouge = vendu · Bleu VW = votre véhicule · Halo rouge = baisse de prix sur 14 jours
                {c?.strategie_vente ? (
                  <span style={{ display: 'block', marginTop: 6, color: 'rgba(165,180,252,0.85)', fontSize: 10 }}>
                    Corridor : fond indigo = fourchette stratégie (rapide → marché) · ligne claire pointillée = prix recommandé
                  </span>
                ) : null}
              </div>

              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  {optimalPriceBand &&
                    (c?.strategie_vente ? (
                      <ReferenceArea
                        x1={scatterDomains.kmMin}
                        x2={scatterDomains.kmMax}
                        y1={optimalPriceBand.y1}
                        y2={optimalPriceBand.y2}
                        ifOverflow="visible"
                        fill="rgba(99,102,241,0.09)"
                        stroke="rgba(165,180,252,0.22)"
                        strokeWidth={1}
                      />
                    ) : (
                      <ReferenceArea
                        x1={scatterDomains.kmMin}
                        x2={scatterDomains.kmMax}
                        y1={optimalPriceBand.y1}
                        y2={optimalPriceBand.y2}
                        ifOverflow="visible"
                        fill="rgba(59,130,246,0.12)"
                        stroke="rgba(147,197,253,0.45)"
                        strokeWidth={1}
                      />
                    ))}
                  <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="rgba(226,232,240,0.08)" />
                  <XAxis
                    dataKey="km"
                    type="number"
                    name="km"
                    domain={[scatterDomains.kmMin, scatterDomains.kmMax]}
                    tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickCount={6}
                    allowDecimals={false}
                    tickFormatter={(val) => {
                      const n = Number(val)
                      if (!Number.isFinite(n)) return ''
                      if (n < 10000) return `${Math.round(n).toLocaleString('fr-CH')}`
                      return `${Math.round(n / 1000)}k`
                    }}
                    label={{ value: 'Kilométrage (km)', position: 'insideBottom', offset: -10, fill: 'rgba(255,255,255,0.2)', fontSize: 11 }}
                  />
                  <YAxis
                    dataKey="prix"
                    type="number"
                    name="prix"
                    domain={[scatterDomains.prixMin, scatterDomains.prixMax]}
                    tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickCount={6}
                    allowDecimals={false}
                    tickFormatter={(val) => {
                      const n = Number(val)
                      if (!Number.isFinite(n)) return ''
                      if (n < 10000) return `${Math.round(n).toLocaleString('fr-CH')}`
                      return `${(n / 1000).toFixed(1)}k`
                    }}
                    width={52}
                    label={{ value: 'Prix (CHF)', angle: -90, position: 'insideLeft', fill: 'rgba(255,255,255,0.2)', fontSize: 11 }}
                  />
                  <Tooltip content={<ScatterTooltip />} />

                  <ReferenceLine
                    y={Number.isFinite(Number(c?.strategie_vente?.recommande?.prix)) && Number(c?.strategie_vente?.recommande?.prix) > 0
                      ? Number(c.strategie_vente.recommande.prix)
                      : c.valeur_estimee}
                    stroke={c?.strategie_vente ? 'rgba(226,232,240,0.72)' : 'rgba(147,197,253,0.75)'}
                    strokeWidth={c?.strategie_vente ? 1.25 : 1.5}
                    strokeDasharray={c?.strategie_vente ? '4 6' : '5 5'}
                  />
                  <ReferenceLine x={parseInt(String(km || '0'), 10)} stroke="rgba(255,255,255,0.15)" strokeDasharray="4 4" />
                  {!c?.strategie_vente ? (
                    <>
                      <Scatter data={tunnelData.bas} line={{ stroke: 'rgba(148,163,184,0.65)', strokeWidth: 2 }} shape={() => null} />
                      <Scatter data={tunnelData.haut} line={{ stroke: 'rgba(148,163,184,0.65)', strokeWidth: 2 }} shape={() => null} />
                    </>
                  ) : null}

                  <Scatter
                    name="Autre année"
                    data={scatterDataDisplay.autreAnnee}
                    fill="rgba(226,232,240,0.12)"
                    stroke="rgba(226,232,240,0.34)"
                    strokeWidth={1}
                    shape={(props) => <PointShape {...props} {...scatterPointShapeProps} r={5} hollow titleColor="rgba(226,232,240,0.4)" />}
                  />
                  <Scatter
                    name="Même année"
                    data={scatterDataDisplay.memeAnnee}
                    fill="rgba(252,252,255,0.94)"
                    stroke="rgba(255,255,255,0.98)"
                    strokeWidth={1.35}
                    shape={(props) => (
                      <PointShape
                        {...props}
                        {...scatterPointShapeProps}
                        r={6}
                        hollow={false}
                        titleColor="rgba(248,250,252,0.92)"
                      />
                    )}
                  />
                  <Scatter
                    name="Vendues"
                    data={scatterDataDisplay.vendues}
                    fill="rgba(248,113,113,0.7)"
                    stroke="#f87171"
                    strokeWidth={1.25}
                    shape={(props) => <PointShape {...props} {...scatterPointShapeProps} r={6} hollow titleColor="rgba(248,113,113,0.78)" />}
                  />

                  <Scatter
                    name="Votre véhicule"
                    data={scatterDataDisplay.votreMoto}
                    fill="#001E50"
                    stroke="#7ea6ff"
                    strokeWidth={tunnelData.yourOut ? 3 : 2}
                    shape={(props) => (
                      <PointShape
                        {...props}
                        {...scatterPointShapeProps}
                        r={9}
                        hollow={false}
                        glow
                        titleColor={tunnelData.yourOut ? '#fca5a5' : '#bfdbfe'}
                      />
                    )}
                  />
                </ScatterChart>
              </ResponsiveContainer>
              <div style={{ marginTop: 6, fontSize: 10, color: 'rgba(255,255,255,0.28)', textAlign: 'center' }}>
                Échelle arrondie: axe X en kilomètres, axe Y en CHF.
              </div>

              <div style={{ display: 'flex', gap: 16, marginTop: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                {[
                  { color: 'rgba(252,252,255,0.94)', label: `Même année (${scatterData.memeAnnee.length})` },
                  { color: 'rgba(255,255,255,0.2)', label: `Autre année (${scatterData.autreAnnee.length})` },
                  { color: '#f87171', label: `Vendues (${scatterData.vendues.length})` },
                  { color: '#001E50', border: '#7ea6ff', label: 'Votre véhicule' },
                  { color: 'transparent', border: 'rgba(248,113,113,0.72)', label: 'Baisse 14 j (halo)' },
                  { color: 'rgba(129,140,248,0.55)', border: 'rgba(165,180,252,0.5)', label: c?.strategie_vente ? 'Corridor stratégie' : 'Corridor optimal' },
                ].map((l, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: l.color,
                        border: l.border ? `2px solid ${l.border}` : 'none',
                        boxSizing: 'border-box',
                        flexShrink: 0,
                      }}
                    />
                    {l.label}
                  </div>
                ))}
              </div>
              {tunnelData.yourOut && (
                <div style={{ marginTop: 8, textAlign: 'center', fontSize: 11, color: '#fca5a5' }}>
                  Votre véhicule est hors corridor de prix idéal.
                </div>
              )}
            </div>
          )}

          {Array.isArray(c.annonces_similaires) && c.annonces_similaires.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Annonces similaires ({c.annonces_similaires.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {c.annonces_similaires.map((a, i) => (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      padding: '0.625rem 0.875rem',
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid transparent',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(0,30,80,0.25)'
                      e.currentTarget.style.background = 'rgba(0,30,80,0.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'transparent'
                      e.currentTarget.style.background = 'rgba(255,255,255,0.02)'
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', flexShrink: 0 }}>{a.prix?.toLocaleString('fr-CH')} CHF</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)', minWidth: 0 }}>
                      {a.annee} · {a.km?.toLocaleString('fr-CH')} km · {a.ville} {a.canton}
                    </span>
                    {a.garantie && <span style={{ fontSize: 10, color: '#4ade80', background: 'rgba(74,222,128,0.1)', padding: '2px 6px', borderRadius: 4 }}>Garantie</span>}
                    <span style={{ fontSize: 11, color: '#bfdbfe', flexShrink: 0 }}>Voir</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '1rem', fontSize: 11, color: 'var(--muted)', textAlign: 'right' }}>
            Méthode : {c.methode} · R² · {c.r2} · {c.poids_regression}% régression + {c.poids_similarite}% similarité
          </div>
        </div>
      )}
        </div>
        <aside
          aria-label="Historique des cotations"
          style={
            isMobileLayout
              ? cotationHistoryOpen
                ? {
                    width: 'auto',
                    flexShrink: 0,
                    transition: 'none',
                    borderLeft: 'none',
                    borderTop: 'none',
                    paddingLeft: 14,
                    paddingRight: 14,
                    paddingTop: 12,
                    paddingBottom: 14,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    minHeight: 0,
                    position: 'fixed',
                    left: 'max(8px, env(safe-area-inset-left, 0px))',
                    right: 'max(8px, env(safe-area-inset-right, 0px))',
                    bottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
                    top: 'auto',
                    maxHeight: 'min(420px, 58vh)',
                    zIndex: 40,
                    background: 'rgba(21,25,32,0.98)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    boxShadow: '0 -8px 32px rgba(0,0,0,0.45)',
                  }
                : { display: 'none' }
              : {
                  width: cotationHistoryOpen ? 272 : 44,
                  flexShrink: 0,
                  transition: 'width 200ms ease',
                  borderLeft: '1px solid rgba(255,255,255,0.08)',
                  borderTop: 'none',
                  paddingLeft: cotationHistoryOpen ? 12 : 6,
                  paddingTop: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  minHeight: 120,
                  position: 'relative',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                }
          }
        >
          {!isMobileLayout ? (
            <button
              type="button"
              onClick={() => setCotationHistoryOpen((o) => !o)}
              aria-expanded={cotationHistoryOpen}
              title={cotationHistoryOpen ? "Replier l'historique" : "Afficher l'historique"}
              style={{
                alignSelf: 'flex-start',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(15,23,42,0.86)',
                color: '#e2e8f0',
                borderRadius: 4,
                padding: 6,
                cursor: 'pointer',
                marginBottom: 8,
              }}
            >
              {cotationHistoryOpen ? <ChevronRight size={18} aria-hidden /> : <ChevronLeft size={18} aria-hidden />}
            </button>
          ) : null}
          {cotationHistoryOpen ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginBottom: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'rgba(226,232,240,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Historique (10 max.)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {isMobileLayout ? (
                    <button
                      type="button"
                      onClick={() => setCotationHistoryOpen(false)}
                      style={{
                        border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.05)',
                        color: '#cbd5e1',
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        borderRadius: 6,
                        padding: '4px 8px',
                        cursor: 'pointer',
                      }}
                    >
                      Fermer
                    </button>
                  ) : null}
                  {cotationHistory.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm("Supprimer tout l'historique de cotation local ?")) return
                        setCotationHistory([])
                        try {
                          localStorage.removeItem(COTATION_HISTORY_LS_KEY)
                        } catch {
                          /* ignore */
                        }
                      }}
                      style={{
                        border: '1px solid rgba(248,113,113,0.35)',
                        background: 'rgba(248,113,113,0.08)',
                        color: '#fca5a5',
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        borderRadius: 6,
                        padding: '4px 7px',
                        cursor: 'pointer',
                      }}
                    >
                      Vider
                    </button>
                  ) : null}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: 1, paddingRight: 4 }}>
                {cotationHistory.length === 0 ? (
                  <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0, lineHeight: 1.45 }}>
                    Aucune cotation enregistrée localement.
                  </p>
                ) : (
                  cotationHistory.map((h) => (
                    <button
                      key={h.id}
                      type="button"
                      onClick={() => {
                        setMake(String(h.make || '').toLowerCase())
                        setModel(String(h.model || ''))
                        setAnnee(String(h.annee ?? ''))
                        setKm(String(h.km ?? ''))
                        setResult(null)
                        setError('')
                      }}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        textAlign: 'left',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.03)',
                        padding: '8px 10px',
                        cursor: 'pointer',
                        color: 'inherit',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 4,
                          flexShrink: 0,
                          background: 'rgba(255,255,255,0.06)',
                          overflow: 'hidden',
                        }}
                      >
                        {h.thumb ? (
                          <img src={h.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'rgba(226,232,240,0.35)' }}>
                            —
                          </div>
                        )}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#f1f5f9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {String(h.make || '').charAt(0).toUpperCase()}
                          {String(h.make || '').slice(1)} {String(h.model || '').toUpperCase()}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#e2e8f0', marginTop: 2 }}>
                          {Number(h.prix).toLocaleString('fr-CH')} CHF
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(226,232,240,0.38)', marginTop: 2 }}>
                          {h.at ? new Date(h.at).toLocaleString('fr-CH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </>
          ) : null}
        </aside>
      </div>
      {showBackTop && (
        <button type="button" className="back-top-btn" onClick={scrollCotationTop}>
          Remonter
        </button>
      )}
    </div>
  )
}

/** Hauteur de ligne des tableaux Stock / Marché (profil « large » fixe, sans réglage utilisateur). */
const VEHICLE_TABLE_ROW_HEIGHT_PX = 72
/** max-height déplié pour l'en-tête Marché (animation scroll) · assez grand pour filtres + puces sans rogner. */
const MARCHE_HEADER_EXPAND_PX = 3200

function MarchePage({
  token,
  user = null,
  initialTab = 'baisses',
  onInitialTabConsumed = null,
  pendingFicheRequest = null,
  onPendingFicheConsumed = null,
}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  /** Rechargement API marché après le 1er chargement (changement de modèle) — sans masquer toute la page. */
  const [marcheRefreshing, setMarcheRefreshing] = useState(false)
  const marcheDataRef = useRef(null)
  const [statsLive, setStatsLive] = useState(null)
  const [opportunites, setOpportunites] = useState([])
  const [loadingOpps, setLoadingOpps] = useState(false)
  const [loadingMoreOpps, setLoadingMoreOpps] = useState(false)
  const [hasMoreOpps, setHasMoreOpps] = useState(false)
  /** Taille des pages ? charger plus ? opportunités (API). */
  const OPPORTUNITES_PAGE_SIZE = 50
  /** Première charge légère sans filtre (vendus / nouvelles / opportunités). */
  const MARCHE_LIST_LITE = 20
  const MARCHE_LIST_FULL = 50
  const [tab, setTab] = useState(initialTab || 'baisses')
  const [filtre, setFiltre] = useState('tous')
  const [marqueFiltre, setMarqueFiltre] = useState('toutes')
  const [cantonFiltre, setCantonFiltre] = useState('tous')
  const [modeleRecherche, setModeleRecherche] = useState('')
  const [prixMinInput, setPrixMinInput] = useState('')
  const [prixMaxInput, setPrixMaxInput] = useState('')
  const [priceFilterTouched, setPriceFilterTouched] = useState(false)
  const [showMarcheFilters, setShowMarcheFilters] = useState(false)
  const [modeleOptions, setModeleOptions] = useState(['tous'])
  const [marqueOptions, setMarqueOptions] = useState(['toutes'])
  const [cantonOptions, setCantonOptions] = useState(['tous'])
  const [modelesLoading, setModelesLoading] = useState(true)
  const [headerCollapsed, setHeaderCollapsed] = useState(false)
  const [showBackTop, setShowBackTop] = useState(false)
  const marcheHeaderRef = useRef(null)
  const marcheScrollRef = useRef(null)
  const [marchePanel, setMarchePanel] = useState(null)
  /** 'toutes' | 'recurrentes' | 'sup5' — uniquement onglet Baisses. */
  const [marcheBaissesTriMode, setMarcheBaissesTriMode] = useState('toutes')
  /** Tri colonnes listes marché (clés alignées sur VehicleRowList). */
  const [marcheSortColumn, setMarcheSortColumn] = useState('jours')
  const [marcheSortDesc, setMarcheSortDesc] = useState(() => (initialTab || 'baisses') !== 'opportunites')
  /** Fiche Synthèse modèle (panneau latéral). */
  const [marcheFicheModele, setMarcheFicheModele] = useState(null)
  const autoOnlyAccount = String(user?.vehicle_scope || '').toLowerCase() === 'auto'
  const hydratedFromCacheRef = useRef(false)
  // Sections déjà chargées depuis l'API (Évite les double-fetch lors des changements d'onglet).
  const loadedSectionsRef = useRef(new Set())
  // Opportunités déjà chargées au moins une fois (pour ne pas re-fetcher à chaque retour sur l'onglet).
  const oppsLoadedRef = useRef(false)
  /** Dès qu'un filtre marché est utilisé, on recharge vendus / nouvelles / opportunités avec la limite complète. */
  const [marcheHeavyListsExpanded, setMarcheHeavyListsExpanded] = useState(false)
  const prevMarcheHeavyExpandedRef = useRef(false)
  const marcheCacheKey = useMemo(
    () => buildPageCacheKey('marche', token, autoOnlyAccount ? 'auto' : 'mixed', user?.id || ''),
    [token, autoOnlyAccount, user?.id]
  )

  useEffect(() => {
    if (!token) return
    const cached = readPageCache(marcheCacheKey, 1000 * 60 * 30)
    if (!cached) return
    // PERF FIX 2026-04 : hydrater immédiatement Marché depuis cache navigateur après reload.
    if (cached.data && typeof cached.data === 'object') {
      setData(cached.data)
      marcheDataRef.current = cached.data
      setLoading(false)
      hydratedFromCacheRef.current = true
    }
    if (cached.statsLive && typeof cached.statsLive === 'object') setStatsLive(cached.statsLive)
    if (Array.isArray(cached.opportunites)) {
      setOpportunites(cached.opportunites)
      setHasMoreOpps(cached.opportunites.length >= MARCHE_LIST_LITE)
    }
  }, [token, marcheCacheKey])

  useEffect(() => {
    if (!token) return
    if (!data && !statsLive && opportunites.length === 0) return
    writePageCache(marcheCacheKey, {
      data,
      statsLive,
      opportunites,
    })
  }, [token, marcheCacheKey, data, statsLive, opportunites])

  useEffect(() => {
    if (!token) return
    const params = new URLSearchParams()
    if (autoOnlyAccount) params.set('vehicle_type', 'auto')
    setModelesLoading(true)
    fetchWithTimeout(`${BACKEND}/api/modeles-disponibles?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d.modeles) ? d.modeles.map((m) => String(m).trim()).filter(Boolean) : []
        setModeleOptions(['tous', ...list])
        setFiltre((f) => {
          if (f === 'tous') return f
          const hit = list.find((x) => String(x).toLowerCase() === String(f).toLowerCase())
          return hit ?? 'tous'
        })
      })
      .catch(() => setModeleOptions(['tous']))
      .finally(() => setModelesLoading(false))
  }, [token, autoOnlyAccount])

  useEffect(() => {
    if (!data) return
    if (modeleOptions.length > 1) return
    const fromData = [
      ...(Array.isArray(data?.baisses) ? data.baisses : []),
      ...(Array.isArray(data?.vendus) ? data.vendus : []),
      ...(Array.isArray(data?.nouvelles) ? data.nouvelles : []),
    ]
      .map((x) => String(x?.model || '').trim())
      .filter(Boolean)
    const uniq = [...new Set(fromData)].sort((a, b) => a.localeCompare(b, 'fr'))
    if (uniq.length > 0) setModeleOptions(['tous', ...uniq])
  }, [data, modeleOptions.length])

  useEffect(() => {
    const fromData = [
      ...(Array.isArray(data?.baisses) ? data.baisses : []),
      ...(Array.isArray(data?.vendus) ? data.vendus : []),
      ...(Array.isArray(data?.nouvelles) ? data.nouvelles : []),
      ...(Array.isArray(opportunites) ? opportunites : []),
    ]
      .map((x) => String(x?.make || '').trim().toLowerCase())
      .filter(Boolean)
    const uniq = [...new Set(fromData)].sort((a, b) => a.localeCompare(b, 'fr'))
    setMarqueOptions(['toutes', ...uniq])
    setMarqueFiltre((prev) => (prev === 'toutes' || uniq.includes(prev) ? prev : 'toutes'))
  }, [data, opportunites])

  useEffect(() => {
    const fromData = [
      ...(Array.isArray(data?.baisses) ? data.baisses : []),
      ...(Array.isArray(data?.vendus) ? data.vendus : []),
      ...(Array.isArray(data?.nouvelles) ? data.nouvelles : []),
      ...(Array.isArray(opportunites) ? opportunites : []),
    ]
      .map((x) => String(x?.canton || '').trim().toUpperCase())
      .filter(Boolean)
    const uniq = [...new Set(fromData)].sort((a, b) => a.localeCompare(b, 'fr'))
    setCantonOptions(['tous', ...uniq])
    setCantonFiltre((prev) => (prev === 'tous' || uniq.includes(prev) ? prev : 'tous'))
  }, [data, opportunites])

  const modeleFiltreActif = useMemo(() => {
    if (filtre === 'tous') return 'tous'
    const hit = modeleOptions.find(
      (x) => x !== 'tous' && String(x).toLowerCase() === String(filtre).toLowerCase()
    )
    return hit ?? 'tous'
  }, [filtre, modeleOptions])

  const modeleRechercheNorm = useMemo(() => String(modeleRecherche || '').trim().toLowerCase(), [modeleRecherche])
  const prixMin = useMemo(() => {
    const n = Number(String(prixMinInput || '').replace(/\s+/g, '').replace(',', '.'))
    return Number.isFinite(n) && n >= 0 ? n : null
  }, [prixMinInput])
  const prixMax = useMemo(() => {
    const n = Number(String(prixMaxInput || '').replace(/\s+/g, '').replace(',', '.'))
    return Number.isFinite(n) && n >= 0 ? n : null
  }, [prixMaxInput])
  const hasPriceFilter = priceFilterTouched && (prixMin != null || prixMax != null)
  const activeMarcheFiltersCount = useMemo(() => {
    let n = 0
    if (marqueFiltre !== 'toutes') n += 1
    if (modeleFiltreActif !== 'tous') n += 1
    if (cantonFiltre !== 'tous') n += 1
    if (modeleRechercheNorm) n += 1
    if (hasPriceFilter) n += 1
    return n
  }, [marqueFiltre, modeleFiltreActif, cantonFiltre, modeleRechercheNorm, hasPriceFilter])

  useEffect(() => {
    const touched =
      marqueFiltre !== 'toutes' ||
      cantonFiltre !== 'tous' ||
      modeleFiltreActif !== 'tous' ||
      Boolean(modeleRechercheNorm) ||
      hasPriceFilter
    if (touched) setMarcheHeavyListsExpanded(true)
  }, [marqueFiltre, cantonFiltre, modeleFiltreActif, modeleRechercheNorm, hasPriceFilter])

  useEffect(() => {
    if (!token) return
    const nowEx = marcheHeavyListsExpanded
    const prev = prevMarcheHeavyExpandedRef.current
    if (nowEx && !prev) {
      loadedSectionsRef.current.delete('vendus')
      loadedSectionsRef.current.delete('nouvelles')
      oppsLoadedRef.current = false
    }
    prevMarcheHeavyExpandedRef.current = nowEx
  }, [token, marcheHeavyListsExpanded])

  const resetMarcheFilters = useCallback(() => {
    setMarqueFiltre('toutes')
    setFiltre('tous')
    setCantonFiltre('tous')
    setModeleRecherche('')
    setPrixMinInput('')
    setPrixMaxInput('')
    setPriceFilterTouched(false)
  }, [])

  const matchesModeleRecherche = useCallback((item) => {
    if (!modeleRechercheNorm) return true
    const title = String(item?.titre || '').toLowerCase()
    const model = String(item?.model || '').toLowerCase()
    return title.includes(modeleRechercheNorm) || model.includes(modeleRechercheNorm)
  }, [modeleRechercheNorm])

  const matchesMarque = useCallback((item) => {
    if (marqueFiltre === 'toutes') return true
    return String(item?.make || '').trim().toLowerCase() === String(marqueFiltre || '').toLowerCase()
  }, [marqueFiltre])

  const matchesPriceRange = useCallback((item) => {
    if (!hasPriceFilter) return true
    const parsePrice = (v) => {
      if (v == null || v === '') return null
      if (typeof v === 'number') return Number.isFinite(v) ? v : null
      const cleaned = String(v)
        .replace(/['?`]/g, '')
        .replace(/\s+/g, '')
        .replace(/,/g, '.')
        .replace(/[^\d.-]/g, '')
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : null
    }
    const prix = parsePrice(item?.nouveau_prix) ?? parsePrice(item?.prix_chf) ?? parsePrice(item?.prix)
    if (prix == null) return false
    if (prixMin != null && prix < prixMin) return false
    if (prixMax != null && prix > prixMax) return false
    return true
  }, [hasPriceFilter, prixMin, prixMax])

  useEffect(() => {
    if (!token) {
      setStatsLive(null)
      return
    }
    let alive = true
    const chargerStatsLive = () => {
      fetchWithTimeout(`${BACKEND}/api/marche/stats-live`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((d) => {
          if (alive) setStatsLive(d && typeof d === 'object' ? d : null)
        })
        .catch(() => {
          if (alive) setStatsLive(null)
        })
    }
    chargerStatsLive()
    const timer = setInterval(chargerStatsLive, 2 * 60 * 1000)
    return () => {
      alive = false
      clearInterval(timer)
    }
  }, [token])

  useEffect(() => {
    marcheDataRef.current = data
  }, [data])

  // Charge une seule section marché (?section=baisses|vendus|nouvelles) et fusionne dans `data`.
  const loadMarcheSection = useCallback((sectionName, { forceRefresh = false, signal, limit } = {}) => {
    if (!token) return
    const lim =
      limit != null && Number.isFinite(Number(limit)) ? Math.min(100, Math.max(1, Math.floor(Number(limit)))) : MARCHE_LIST_FULL
    const params = new URLSearchParams({ limit: String(lim), section: sectionName })
    if (forceRefresh) params.set('refresh', '1')
    if (autoOnlyAccount) params.set('vehicle_type', 'auto')
    if (modeleFiltreActif !== 'tous') {
      params.set('model', modeleFiltreActif)
      if (sectionName === 'baisses') params.set('baisses_limit', '600')
    }
    return fetchWithTimeout(
      `${BACKEND}/api/marche?${params}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
      90000
    )
      .then((r) => r.json())
      .then((d) => {
        if (!d || typeof d !== 'object') return
        // FIX 2026-05 : on ne fusionne QUE la section demandée. Le backend
        // renvoie systématiquement `vendus: []` / `baisses: []` / `nouvelles: []`
        // même quand on demande juste section=baisses : sans cette garde, un
        // utilisateur qui charge Vendus (data.vendus=20) puis clique Baisses
        // voyait son data.vendus écrasé par [] → en revenant sur Vendus,
        // `loadedSectionsRef` dit « déjà chargé » donc aucun re-fetch → liste vide.
        setData((prev) => {
          const next = { ...(prev || {}), stats: d.stats ?? prev?.stats }
          if (sectionName === 'all') {
            if (Array.isArray(d.baisses))   next.baisses   = d.baisses
            if (Array.isArray(d.vendus))    next.vendus    = d.vendus
            if (Array.isArray(d.nouvelles)) next.nouvelles = d.nouvelles
          } else if (sectionName === 'baisses' && Array.isArray(d.baisses)) {
            next.baisses = d.baisses
          } else if (sectionName === 'vendus' && Array.isArray(d.vendus)) {
            next.vendus = d.vendus
          } else if (sectionName === 'nouvelles' && Array.isArray(d.nouvelles)) {
            next.nouvelles = d.nouvelles
          }
          return next
        })
        loadedSectionsRef.current.add(sectionName)
        marcheDataRef.current = true
      })
  }, [token, autoOnlyAccount, modeleFiltreActif])

  useEffect(() => {
    if (!token) {
      setLoading(false)
      setMarcheRefreshing(false)
      setModelesLoading(false)
      setModeleOptions(['tous'])
      setData(null)
      marcheDataRef.current = null
      loadedSectionsRef.current = new Set()
      oppsLoadedRef.current = false
      return
    }
    const ac = new AbortController()
    // Réinitialiser les sections chargées quand le filtre modèle change.
    loadedSectionsRef.current = new Set()
    oppsLoadedRef.current = false

    // PERF FIX 2026-05 : si l'onglet actif est "opportunites", son propre
    // useEffect (plus bas) déclenche déjà /api/marche/opportunites. Éviter de
    // charger "baisses" en plus au même moment — c'était la principale source
    // de lenteur au premier chargement sur cet onglet.
    if (tab === 'opportunites') {
      const forceRefreshOpp = hydratedFromCacheRef.current
      if (forceRefreshOpp) {
        hydratedFromCacheRef.current = false
        oppsLoadedRef.current = false // force re-fetch opportunités
      }
      if (marcheDataRef.current == null) setLoading(false)
      return () => ac.abort()
    }

    const initialSection = (!tab || tab === 'baisses') ? 'baisses' : tab
    const limitInitial =
      initialSection === 'baisses'
        ? MARCHE_LIST_FULL
        : initialSection === 'vendus' || initialSection === 'nouvelles'
          ? marcheHeavyListsExpanded
            ? MARCHE_LIST_FULL
            : MARCHE_LIST_LITE
          : MARCHE_LIST_FULL
    if (marcheDataRef.current != null) setMarcheRefreshing(true)
    else setLoading(true)

    const forceRefresh = hydratedFromCacheRef.current
    if (forceRefresh) hydratedFromCacheRef.current = false

    loadMarcheSection(initialSection, { forceRefresh, signal: ac.signal, limit: limitInitial })
      .then(() => {
        setLoading(false)
        setMarcheRefreshing(false)
      })
      .catch((err) => {
        if (err?.name === 'AbortError') {
          setMarcheRefreshing(false)
          return
        }
        setLoading(false)
        setMarcheRefreshing(false)
      })
    return () => ac.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, modeleFiltreActif, autoOnlyAccount])

  // Charge lazily une section marché quand l'utilisateur switch d'onglet.
  useEffect(() => {
    if (!token || tab === 'opportunites') return
    if (loadedSectionsRef.current.has(tab)) return
    const ac = new AbortController()
    const lim =
      tab === 'vendus' || tab === 'nouvelles'
        ? marcheHeavyListsExpanded
          ? MARCHE_LIST_FULL
          : MARCHE_LIST_LITE
        : MARCHE_LIST_FULL
    loadMarcheSection(tab, { signal: ac.signal, limit: lim })?.catch(() => {})
    return () => ac.abort()
  }, [tab, token, loadMarcheSection, marcheHeavyListsExpanded])

  // En quittant l'onglet opportunités : permettre un nouveau fetch au prochain passage (sinon liste vide / obsolète bloquée par oppsLoadedRef).
  useEffect(() => {
    if (tab !== 'opportunites') {
      oppsLoadedRef.current = false
      setLoadingOpps(false)
    }
  }, [tab])

  // Opportunités : chargement à chaque visite d'onglet (ref réinitialisée ci-dessus en sortie d'onglet).
  useEffect(() => {
    if (!token || tab !== 'opportunites') return
    if (oppsLoadedRef.current) return
    const ac = new AbortController()
    setLoadingOpps(true)
    const params = new URLSearchParams()
    const oppLimit = marcheHeavyListsExpanded ? MARCHE_LIST_FULL : MARCHE_LIST_LITE
    params.set('limit', String(oppLimit))
    if (autoOnlyAccount) params.set('vehicle_type', 'auto')
    if (modeleFiltreActif !== 'tous') params.set('model', modeleFiltreActif)
    fetchWithTimeout(`${BACKEND}/api/marche/opportunites?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ac.signal,
    })
      .then(async (r) => {
        if (!r.ok) {
          const txt = await r.text().catch(() => '')
          throw new Error(txt || `HTTP ${r.status}`)
        }
        return r.json()
      })
      .then((d) => {
        const rows = Array.isArray(d) ? d : []
        setOpportunites(rows)
        setHasMoreOpps(rows.length >= oppLimit)
        setLoadingOpps(false)
        oppsLoadedRef.current = true
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        setOpportunites([])
        setHasMoreOpps(false)
        setLoadingOpps(false)
        oppsLoadedRef.current = false
      })
    return () => ac.abort()
  }, [tab, token, modeleFiltreActif, autoOnlyAccount, marcheHeavyListsExpanded])

  const handleLoadMoreOpps = useCallback(() => {
    if (!token || !marcheHeavyListsExpanded || loadingOpps || loadingMoreOpps || !hasMoreOpps) return
    setLoadingMoreOpps(true)
    const params = new URLSearchParams()
    params.set('limit', String(OPPORTUNITES_PAGE_SIZE))
    params.set('offset', String(opportunites.length))
    if (autoOnlyAccount) params.set('vehicle_type', 'auto')
    if (modeleFiltreActif !== 'tous') params.set('model', modeleFiltreActif)
    fetchWithTimeout(`${BACKEND}/api/marche/opportunites?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const rows = Array.isArray(d) ? d : []
        setOpportunites((prev) => [...prev, ...rows])
        setHasMoreOpps(rows.length >= OPPORTUNITES_PAGE_SIZE)
      })
      .catch(() => {})
      .finally(() => setLoadingMoreOpps(false))
  }, [
    token,
    loadingOpps,
    loadingMoreOpps,
    hasMoreOpps,
    OPPORTUNITES_PAGE_SIZE,
    opportunites.length,
    autoOnlyAccount,
    modeleFiltreActif,
    marcheHeavyListsExpanded,
  ])

  useEffect(() => {
    if (tab !== 'baisses') setMarcheBaissesTriMode('toutes')
  }, [tab])

  useEffect(() => {
    setMarcheSortColumn('jours')
    setMarcheSortDesc(tab !== 'opportunites')
  }, [tab])

  const handleMarcheSort = useCallback((colKey) => {
    if (marcheSortColumn === colKey) {
      setMarcheSortDesc((d) => !d)
      return
    }
    setMarcheSortColumn(colKey)
    const oppEcart = tab === 'opportunites' && (colKey === 'jours' || colKey === 'ecart')
    setMarcheSortDesc(colKey === 'model' ? false : oppEcart ? false : true)
  }, [marcheSortColumn, tab])

  useEffect(() => {
    if (!token || !marcheFicheModele?.modelKey || !marcheFicheModele.nonce) return
    const { modelKey, nonce } = marcheFicheModele
    let ignore = false
    const ac = new AbortController()
    ;(async () => {
      try {
        const r = await fetchWithTimeout(`${BACKEND}/api/marche/fiche-modele?model=${encodeURIComponent(modelKey)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        })
        const d = await r.json().catch(() => ({}))
        if (ignore) return
        if (!r.ok) {
          setMarcheFicheModele((prev) =>
            prev && prev.nonce === nonce
              ? { ...prev, loading: false, error: d.error || `Erreur ${r.status}`, data: null }
              : prev
          )
          return
        }
        setMarcheFicheModele((prev) =>
          prev && prev.nonce === nonce ? { ...prev, loading: false, error: null, data: d } : prev
        )
      } catch (e) {
        if (ignore || e.name === 'AbortError') return
        setMarcheFicheModele((prev) =>
          prev && prev.nonce === nonce ? { ...prev, loading: false, error: e.message || 'Erreur réseau', data: null } : prev
        )
      }
    })()
    return () => {
      ignore = true
      ac.abort()
    }
    /* Déclenché uniquement à l'ouverture fiche (modelKey + nonce), pas sur loading/data. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, marcheFicheModele?.modelKey, marcheFicheModele?.nonce])

  useEffect(() => {
    if (!initialTab) return
    setTab(initialTab)
    onInitialTabConsumed?.()
  }, [initialTab, onInitialTabConsumed])

  const handleMarcheScroll = useCallback((e) => {
    const next = e.currentTarget.scrollTop > 16
    setHeaderCollapsed((prev) => (prev === next ? prev : next))
    setShowBackTop(e.currentTarget.scrollTop > 420)
  }, [])

  const scrollMarcheTop = useCallback(() => {
    marcheScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const displayList = useMemo(() => {
    const raw =
      tab === 'baisses' ? data?.baisses : tab === 'vendus' ? data?.vendus : data?.nouvelles
    if (!raw) return []
    const modelFiltered =
      modeleFiltreActif === 'tous'
        ? [...raw]
        : raw.filter((i) => {
            const f = String(modeleFiltreActif).toLowerCase()
            const m = i.model && String(i.model).toLowerCase()
            return (m && m === f) || String(i.titre || '').toLowerCase().includes(f)
          })
    const base =
      cantonFiltre === 'tous'
        ? modelFiltered
        : modelFiltered.filter((i) => String(i?.canton || '').trim().toUpperCase() === cantonFiltre)
    const baseFiltered = base
      .filter(matchesMarque)
      .filter(matchesModeleRecherche)
      .filter(matchesPriceRange)

    const deduped = []
    const seen = new Set()
    for (const item of baseFiltered) {
      const key =
        item?.id_source ||
        item?.hist_id ||
        item?.url ||
        `${item?.titre || ''}-${item?.first_registration_year || ''}-${item?.km || ''}-${item?.prix_chf || ''}`
      if (seen.has(key)) continue
      seen.add(key)
      deduped.push(item)
    }

    if (tab === 'nouvelles') {
      deduped.sort((a, b) => {
        const tb = Date.parse(b.premiere_vue_at) || 0
        const ta = Date.parse(a.premiere_vue_at) || 0
        return tb - ta
      })
      return deduped
    }
    if (tab === 'vendus') {
      deduped.sort((a, b) => {
        const tb = Date.parse(b.disparue_at) || 0
        const ta = Date.parse(a.disparue_at) || 0
        return tb - ta
      })
      return deduped
    }
    if (tab !== 'baisses') return deduped
    const recurrentOnly = marcheBaissesTriMode === 'recurrentes'
  const sup5Only = marcheBaissesTriMode === 'sup5'
  const baissesList = recurrentOnly
      ? deduped.filter((b) => countMarchePriceDrops(b.price_history) >= 2)
      : sup5Only
        ? deduped.filter((b) => {
            const pct = marcheLastDropPctAbs(b)
            return Number.isFinite(pct) && pct > 5
          })
        : deduped
    return baissesList.sort((a, b) => {
      if (recurrentOnly) {
        const dropSumA = marcheTotalDropsChfAbs(a.price_history)
        const dropSumB = marcheTotalDropsChfAbs(b.price_history)
        if (dropSumB !== dropSumA) return dropSumB - dropSumA
        const na = countMarchePriceDrops(a.price_history)
        const nb = countMarchePriceDrops(b.price_history)
        if (nb !== na) return nb - na
      }
      const ta = a.baisse_at ? Date.parse(a.baisse_at) : NaN
      const tb = b.baisse_at ? Date.parse(b.baisse_at) : NaN
      if (Number.isFinite(tb) && Number.isFinite(ta) && tb !== ta) return tb - ta
      const na = Array.isArray(a.price_history) ? a.price_history.length : 0
      const nb = Array.isArray(b.price_history) ? b.price_history.length : 0
      if (nb !== na) return nb - na
      return (Number(b.hist_id) || 0) - (Number(a.hist_id) || 0)
    })
  }, [
    tab,
    modeleFiltreActif,
    cantonFiltre,
    data,
    marcheBaissesTriMode,
    matchesMarque,
    matchesModeleRecherche,
    matchesPriceRange,
  ])

  useEffect(() => {
    if (!marchePanel) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setMarchePanel(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [marchePanel])

  useEffect(() => {
    if (!marcheFicheModele) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setMarcheFicheModele(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [marcheFicheModele])

  // Bouton retour système (mobile) → ferme la fiche modèle plutôt que de
  // quitter l'app. Pour `marchePanel` c'est géré dans MarcheVehiclePanel.
  useBackToClose(Boolean(marcheFicheModele), () => setMarcheFicheModele(null))

  useEffect(() => {
    if (!showMarcheFilters) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setShowMarcheFilters(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [showMarcheFilters])

  const ouvrirFicheModele = useCallback((modelKey) => {
    const k = String(modelKey || '').trim()
    if (!k || k === 'tous') return
    setFiltre(k)
    setMarcheFicheModele({ modelKey: k.toLowerCase(), nonce: Date.now(), loading: true, data: null, error: null })
  }, [])

  useEffect(() => {
    if (!pendingFicheRequest?.modelKey || !pendingFicheRequest.nonce) return
    if (modelesLoading) return
    const k = String(pendingFicheRequest.modelKey || '')
      .trim()
      .toLowerCase()
    if (!k) {
      onPendingFicheConsumed?.()
      return
    }
    const inList = modeleOptions.some((x) => x !== 'tous' && String(x).toLowerCase() === k)
    if (!inList) {
      setModeleOptions((prev) => {
        if (prev.some((x) => String(x).toLowerCase() === k)) return prev
        const rest = prev.filter((x) => x !== 'tous')
        return ['tous', ...rest, k]
      })
      return
    }
    ouvrirFicheModele(k)
    onPendingFicheConsumed?.()
  }, [pendingFicheRequest, modelesLoading, modeleOptions, ouvrirFicheModele, onPendingFicheConsumed])

  const marcheVehicleRows = useMemo(() => {
    const metaLine = (x) =>
      [x.ville, x.canton, x.km != null ? `${Number(x.km).toLocaleString('fr-CH')} km` : null, x.first_registration_year ?? null]
        .filter(Boolean)
        .join(' · ') || '—'
    const refLine = (x) => cotationReferenceLabel(x)
    if (tab === 'baisses') {
      return displayList.map((b, i) => {
        const nDrop = countMarchePriceDrops(b.price_history)
        const dropChf = marcheLastDropChfAbs(b)
        const daysAgo = marcheDaysSinceIso(b.baisse_at)
        const dateLabel = daysAgo != null ? `Il y a ${daysAgo} j` : b.baisse_at ? new Date(b.baisse_at).toLocaleDateString('fr-CH') : null
        const hasTotalDropsLabel = nDrop >= 2
        const daysSub = dateLabel || hasTotalDropsLabel
          ? (
            <>
              {dateLabel}
              {dateLabel && hasTotalDropsLabel ? ' · ' : null}
              {hasTotalDropsLabel ? <span style={{ color: '#f87171' }}>{`${nDrop} baisses au total`}</span> : null}
            </>
            )
          : null
        const ecB = Number(b.ecart_pct ?? b.ecart_vs_mediane)
        return {
        rowKey: String(b.hist_id ?? `${b.url}-${i}`),
        urgency: marcheUrgencyFromRow(b),
        imageUrl: b.image_url,
        fallbackLetters: `${String(b.model || '')[0] || ''}${String(b.titre || '')[0] || ''}`.toUpperCase(),
        titleLine: b.titre,
        metaLine: metaLine(b),
        daysMain: dropChf != null ? `−${Number(dropChf).toLocaleString('fr-CH')} CHF` : '—',
        daysSub,
        priceMain: b.nouveau_prix != null ? `${Number(b.nouveau_prix).toLocaleString('fr-CH')} CHF` : '—',
        priceSub:
          b.ancien_prix != null && Number.isFinite(Number(b.ancien_prix))
            ? `Avant ${Number(b.ancien_prix).toLocaleString('fr-CH')} CHF`
            : null,
        ...marcheDelayFromRow(b),
        ecartPct: b.ecart_pct ?? b.ecart_vs_mediane,
        positionSub: refLine(b),
        showPosition: true,
        showAction: false,
        onRowClick: () => setMarchePanel({ variant: 'baisse', record: b, rowKey: String(b.hist_id ?? `${b.url}-${i}`) }),
        _sort: {
          model: String(b.titre || b.model || '').toLowerCase(),
          jours: dropChf != null ? Number(dropChf) : null,
          prix: Number.isFinite(Number(b.nouveau_prix)) ? Number(b.nouveau_prix) : null,
          delai: Number.isFinite(Number(b.delai_vente_median_j)) ? Number(b.delai_vente_median_j) : null,
          ecart: Number.isFinite(ecB) ? ecB : null,
        },
        }
      })
    }
    if (tab === 'vendus') {
      return displayList.map((v, i) => {
        const t0 = v.premiere_vue_at || v.created_date
        const t1 = v.disparue_at
        let soldDays = null
        if (t0 && t1) {
          const d0 = Date.parse(t0)
          const d1 = Date.parse(t1)
          if (Number.isFinite(d0) && Number.isFinite(d1) && d1 >= d0) soldDays = Math.round((d1 - d0) / (86400000))
        }
        const ecV = Number(v.ecart_pct ?? v.ecart_vs_mediane)
        return {
          rowKey: `${v.url}-${i}`,
          urgency: marcheUrgencyFromRow(v),
          imageUrl: v.image_url,
          fallbackLetters: `${String(v.model || '')[0] || ''}${String(v.titre || '')[0] || ''}`.toUpperCase(),
          titleLine: v.titre,
          metaLine: metaLine(v),
          daysMain: v.disparue_at
            ? new Date(v.disparue_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' })
            : '—',
          daysSub:
            soldDays != null
              ? `${soldDays} j en annonce avant vente`
              : v.vendue_probable
                ? 'Vente probable'
                : null,
          priceMain: v.prix_chf != null ? `${Number(v.prix_chf).toLocaleString('fr-CH')} CHF` : '—',
          priceSub:
            v.prix_median != null && Number.isFinite(Number(v.prix_median)) && Number(v.prix_median) > 0
              ? `Médiane ${Number(v.prix_median).toLocaleString('fr-CH')} CHF`
              : null,
          ...marcheDelayFromRow(v),
          ecartPct: v.ecart_pct ?? v.ecart_vs_mediane,
          positionSub: refLine(v),
          showPosition: true,
          showAction: false,
          onRowClick: () => setMarchePanel({ variant: 'vendu', record: v, rowKey: `${v.url}-${i}` }),
          _sort: {
            model: String(v.titre || v.model || '').toLowerCase(),
            jours: v.disparue_at ? Date.parse(v.disparue_at) : null,
            prix: Number.isFinite(Number(v.prix_chf)) ? Number(v.prix_chf) : null,
            delai: Number.isFinite(Number(v.delai_vente_median_j)) ? Number(v.delai_vente_median_j) : null,
            ecart: Number.isFinite(ecV) ? ecV : null,
          },
        }
      })
    }
    if (tab === 'nouvelles') {
      return displayList.map((n, i) => {
        const ecN = Number(n.ecart_pct ?? n.ecart_vs_mediane)
        const onlineDays = n.premiere_vue_at ? marcheDaysSinceIso(n.premiere_vue_at) : null
        return {
          rowKey: `${n.url}-${i}`,
          urgency: marcheUrgencyFromRow(n),
          imageUrl: n.image_url,
          fallbackLetters: `${String(n.model || '')[0] || ''}${String(n.titre || '')[0] || ''}`.toUpperCase(),
          titleLine: n.titre,
          metaLine: metaLine(n),
          daysMain: onlineDays != null ? `${onlineDays} j` : '—',
          daysSub: n.premiere_vue_at
            ? new Date(n.premiere_vue_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' })
            : null,
          priceMain: n.prix_chf != null ? `${Number(n.prix_chf).toLocaleString('fr-CH')} CHF` : '—',
          priceSub:
            n.prix_median != null && Number.isFinite(Number(n.prix_median)) && Number(n.prix_median) > 0
              ? `Médiane ${Number(n.prix_median).toLocaleString('fr-CH')} CHF`
              : null,
          ...marcheDelayFromRow(n),
          ecartPct: n.ecart_pct ?? n.ecart_vs_mediane,
          positionSub: refLine(n),
          showPosition: true,
          showAction: false,
          onRowClick: () => setMarchePanel({ variant: 'nouvelle', record: n, rowKey: `${n.url}-${i}` }),
          _sort: {
            model: String(n.titre || n.model || '').toLowerCase(),
            jours: n.premiere_vue_at ? Date.parse(n.premiere_vue_at) : null,
            prix: Number.isFinite(Number(n.prix_chf)) ? Number(n.prix_chf) : null,
            delai: Number.isFinite(Number(n.delai_vente_median_j)) ? Number(n.delai_vente_median_j) : null,
            ecart: Number.isFinite(ecN) ? ecN : null,
          },
        }
      })
    }
    if (tab === 'opportunites') {
      return opportunites
        .filter((o) => cantonFiltre === 'tous' || String(o?.canton || '').trim().toUpperCase() === cantonFiltre)
        .filter((o) =>
          modeleFiltreActif === 'tous'
            ? true
            : String(o?.model || '').trim().toLowerCase() === String(modeleFiltreActif).toLowerCase() ||
              String(o?.titre || '').toLowerCase().includes(String(modeleFiltreActif).toLowerCase())
        )
        .filter(matchesMarque)
        .filter(matchesModeleRecherche)
        .filter(matchesPriceRange)
        .map((o, i) => {
          const ecart = Number(o?.ecart_pct)
          const ecartTxt = Number.isFinite(ecart) ? `${ecart.toFixed(1)} %` : '—'
          const conf = Number(o?.confiance)
          const extras = [
            Number.isFinite(conf) && conf > 0 ? `Confiance ${conf}%` : null,
            o.garantie ? 'Garantie' : null,
          ].filter(Boolean)
          const ecoSecond =
            o.baisse_recente != null ? (
              <span style={{ color: '#f87171' }}>Baisse -{Number(o.baisse_recente).toLocaleString('fr-CH')} CHF</span>
            ) : Number.isFinite(conf) && conf >= 70 ? (
              <span style={{ color: '#4ade80' }}>Score cote fiable</span>
            ) : null
          const daysMainOpp = ecartTxt
          const record = {
            ...o,
            image_url: o.image_principale_url || o.image_url,
          }
          const ecO = Number.isFinite(ecart) ? ecart : null
          return {
            rowKey: String(o.id_source || `${o.url}-${i}`),
            urgency: marcheUrgencyFromRow(o),
            imageUrl: o.image_principale_url || o.image_url,
            fallbackLetters: `${String(o.model || '')[0] || ''}${String(o.titre || '')[0] || ''}`.toUpperCase(),
            titleLine: o.titre,
            metaLine: [metaLine(o), ...extras].filter(Boolean).join(' · ') || '—',
            daysMain: daysMainOpp,
            daysSub: ecoSecond || null,
            priceMain: o.prix_chf != null ? `${Number(o.prix_chf).toLocaleString('fr-CH')} CHF` : '—',
            priceSub:
              (o.reference_type === 'ventes_reelles' && Number(o.prix_vente_probable) > 0)
                ? `Ref ${Number(o.prix_vente_probable).toLocaleString('fr-CH')} CHF`
                : o.valeur_estimee != null && Number(o.valeur_estimee) > 0
                  ? `Ref ${Number(o.valeur_estimee).toLocaleString('fr-CH')} CHF`
                : null,
            ...marcheDelayFromRow(o),
            ecartPct: ecart,
            positionSub: refLine(o),
            showPosition: true,
            showAction: false,
            onRowClick: () =>
              setMarchePanel({
                variant: 'opportunite',
                record,
                rowKey: String(o.id_source || `${o.url}-${i}`),
              }),
            _sort: {
              model: String(o.titre || o.model || '').toLowerCase(),
              jours: ecO,
              prix: Number.isFinite(Number(o.prix_chf)) ? Number(o.prix_chf) : null,
              delai: Number.isFinite(Number(o.delai_vente_median_j)) ? Number(o.delai_vente_median_j) : null,
              ecart: ecO,
            },
          }
        })
    }
    return []
  }, [tab, displayList, opportunites, cantonFiltre, modeleFiltreActif, matchesMarque, matchesModeleRecherche, matchesPriceRange])

  const marcheVehicleRowsDisplayed = useMemo(() => {
    if (!marcheVehicleRows.length) return marcheVehicleRows
    const dir = marcheSortDesc ? -1 : 1
    return [...marcheVehicleRows].sort((ra, rb) => {
      const sa = ra._sort || {}
      const sb = rb._sort || {}
      if (marcheSortColumn === 'model') {
        const c = String(sa.model || '').localeCompare(String(sb.model || ''), 'fr')
        return marcheSortDesc ? -c : c
      }
      return marcheSortNum(sa[marcheSortColumn], sb[marcheSortColumn], dir)
    })
  }, [marcheVehicleRows, marcheSortColumn, marcheSortDesc])

  if (!token) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', minWidth: 0 }}>
        Connectez-vous pour voir le marché.
      </div>
    )
  }

  if (loading && !data) {
    return (
      <div className="stock-loading" role="status" aria-live="polite" aria-busy="true" style={{ minWidth: 0 }}>
        <div className="stock-loading-ring" aria-hidden />
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f4f4f5', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Chargement du marché
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
            Préparation des opportunités, baisses de prix et tendances en cours.
          </div>
          <div className="stock-loading-bar stock-loading-bar--indeterminate" aria-hidden />
        </div>
      </div>
    )
  }

  const stats = data?.stats || {}
  const statsCards = [
    { key: 'vendues_7j', legacy: 'vendues_semaine', lbl: 'vendues 7j', color: '#4ade80' },
    { key: 'baisses_7j', legacy: 'baisses_recentes', lbl: 'baisses récentes', color: '#f87171' },
    { key: 'nouvelles_7j', legacy: 'nouvelles_semaine', lbl: 'nouvelles 7j', color: '#bfdbfe' },
  ]
  const heuresDepuisMaj = (() => {
    const ts = statsLive?.derniere_maj
    if (!ts) return '—'
    const diffMs = Date.now() - new Date(ts).getTime()
    if (!Number.isFinite(diffMs) || diffMs < 0) return '—'
    return Math.max(0, Math.round(diffMs / (1000 * 60 * 60)))
  })()
  const economieMoyenne = (() => {
    if (!opportunites.length) return 0
    const vals = opportunites
      .map((o) => Number(o?.ecart_pct))
      .filter((v) => Number.isFinite(v))
      .map((v) => Math.abs(v))
    if (!vals.length) return 0
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10
  })()

  return (
    <div className="marche-page-root" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, minHeight: 0 }}>
      <div
        ref={marcheHeaderRef}
        style={{
          flexShrink: 0,
          overflow: 'hidden',
          maxHeight: headerCollapsed ? 0 : MARCHE_HEADER_EXPAND_PX,
          opacity: headerCollapsed ? 0 : 1,
          transform: headerCollapsed ? 'translateY(-18px)' : 'translateY(0)',
          transition: 'max-height 0.32s ease, opacity 0.24s ease, transform 0.24s ease',
          pointerEvents: headerCollapsed ? 'none' : 'auto',
        }}
      >
        {data && (
          <div className="marche-stats-row">
            {statsCards.map((s, i) => {
              const node = stats?.[s.key]
              const count =
                node && typeof node === 'object'
                  ? Number(node.count) || 0
                  : Number(stats?.[s.legacy]) || 0
              const vs =
                node && typeof node === 'object'
                  ? Number(node.vs_semaine_precedente) || 0
                  : 0
              const arrow = vs > 0 ? '▲' : vs < 0 ? '▼' : '—'
              const isBaisses = s.key === 'baisses_7j'
              const deltaColor = isBaisses
                ? vs > 0
                  ? '#f87171'
                  : vs < 0
                    ? '#4ade80'
                    : 'rgba(255,255,255,0.35)'
                : vs > 0
                  ? '#4ade80'
                  : vs < 0
                    ? '#f87171'
                    : 'rgba(255,255,255,0.35)'
              return (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: '1.35rem 1.1rem',
                  textAlign: 'center',
                  borderRight: i < 2 ? '1px solid var(--border)' : 'none',
                }}
              >
                <div style={{ fontSize: '1.9rem', fontWeight: 300, color: '#f8fafc', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
                  {Number(count).toLocaleString('fr-CH')}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontVariant: 'small-caps',
                  }}
                >
                  {s.lbl}
                </div>
                <div style={{ marginTop: 4, fontSize: 10, color: deltaColor, fontWeight: 700 }}>
                  {arrow} {vs > 0 ? '+' : ''}{vs}% vs sem. précédente
                </div>
              </div>
            )})}
          </div>
        )}

        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0, alignItems: 'stretch' }}>
          <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
            {[
              { key: 'opportunites', label: 'Opportunités', priority: true },
              { key: 'baisses', label: 'Baisses de prix', priority: true },
              { key: 'vendus', label: 'Vendus récents', priority: false },
              { key: 'nouvelles', label: 'Nouvelles annonces', priority: false },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background:
                    t.priority && tab === t.key
                      ? 'linear-gradient(180deg, rgba(0,30,80,0.18), rgba(0,30,80,0.06))'
                      : t.priority
                        ? 'rgba(0,30,80,0.07)'
                        : 'transparent',
                  border: 'none',
                  borderBottom: tab === t.key ? '2px solid #bfdbfe' : '2px solid transparent',
                  color: tab === t.key ? '#bfdbfe' : t.priority ? 'rgba(191,219,254,0.86)' : 'var(--muted)',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: t.priority ? 700 : 600,
                  cursor: 'pointer',
                }}
              >
                {t.label}
                {t.key === 'opportunites' && opportunites.length > 0 && (
                  <span style={{ marginLeft: 6, background: 'rgba(245,158,11,0.18)', color: '#f59e0b', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 }}>
                    {opportunites.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: '0.65rem 1rem',
            borderBottom: '1px solid var(--border)',
            fontSize: 12,
            color: 'rgba(255,255,255,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div>
            Données mises à jour il y a {heuresDepuisMaj}h · {Number(statsLive?.nouvelles_24h || 0).toLocaleString('fr-CH')} nouvelles annonces aujourd&apos;hui ·{' '}
            {Number(statsLive?.opportunites || 0).toLocaleString('fr-CH')} opportunités détectées
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {activeMarcheFiltersCount > 0 ? (
              <button
                type="button"
                onClick={resetMarcheFilters}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: 'rgba(255,255,255,0.03)',
                  color: 'var(--text-secondary)',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Reset
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setShowMarcheFilters(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid rgba(126,166,255,0.45)',
                background: 'rgba(0,30,80,0.18)',
                color: '#dbeafe',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <SlidersHorizontal size={14} />
              Filtres{activeMarcheFiltersCount > 0 ? ` (${activeMarcheFiltersCount})` : ''}
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
            flexWrap: 'wrap',
            rowGap: 8,
            minWidth: 0,
          }}
        >
          {tab === 'baisses' && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                marginTop: 0,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                  fontVariant: 'small-caps',
                }}
              >
                Vue baisses
              </span>
              <button
                type="button"
                onClick={() => setMarcheBaissesTriMode('toutes')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: marcheBaissesTriMode === 'toutes' ? '1px solid rgba(126,166,255,0.45)' : '1px solid var(--border)',
                  background: marcheBaissesTriMode === 'toutes' ? 'rgba(0,30,80,0.22)' : 'transparent',
                  color: marcheBaissesTriMode === 'toutes' ? '#e2e8f0' : 'var(--muted)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Toutes
              </button>
              <button
                type="button"
                onClick={() => setMarcheBaissesTriMode('recurrentes')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: marcheBaissesTriMode === 'recurrentes' ? '1px solid rgba(126,166,255,0.45)' : '1px solid var(--border)',
                  background: marcheBaissesTriMode === 'recurrentes' ? 'rgba(0,30,80,0.22)' : 'transparent',
                  color: marcheBaissesTriMode === 'recurrentes' ? '#e2e8f0' : 'var(--muted)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Baisses récurrentes (≥ 2)
              </button>
              <button
                type="button"
                onClick={() => setMarcheBaissesTriMode('sup5')}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: marcheBaissesTriMode === 'sup5' ? '1px solid rgba(126,166,255,0.45)' : '1px solid var(--border)',
                  background: marcheBaissesTriMode === 'sup5' ? 'rgba(0,30,80,0.22)' : 'transparent',
                  color: marcheBaissesTriMode === 'sup5' ? '#e2e8f0' : 'var(--muted)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                &gt; 5%
              </button>
              {marcheBaissesTriMode === 'recurrentes' ? (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4, flex: '1 1 200px' }}>
                  Uniquement les annonces avec au moins deux mouvements à la baisse dans l'historique des prix.
                </span>
              ) : marcheBaissesTriMode === 'sup5' ? (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.4, flex: '1 1 200px' }}>
                  Uniquement les annonces dont la dernière baisse dépasse 5% de l&apos;ancien prix.
                </span>
              ) : null}
            </div>
          )}
          {tab === 'baisses' && modeleFiltreActif !== 'tous' && !loading && (
            <span style={{ fontSize: 11, color: 'var(--muted)', width: '100%', lineHeight: 1.35 }}>
              Toutes les baisses détectées pour ce modèle (annonces actives), jusqu'à 600 entrées.
            </span>
          )}
        </div>
      </div>

      <div
        data-marche-scroll
        ref={marcheScrollRef}
        onScroll={handleMarcheScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'clamp(1rem, 2.2vw, 2rem)',
          display: 'block',
        }}
      >
        {marcheRefreshing ? (
          <div
            style={{
              marginBottom: 10,
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid rgba(0,30,80,0.35)',
              background: 'rgba(0,30,80,0.14)',
              color: 'var(--text-secondary)',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Mise à jour des listes marché?
          </div>
        ) : null}
        {tab === 'opportunites' && (
          <div style={{ marginBottom: 2 }}>
            <div
              style={{
                border: '1px solid rgba(245,158,11,0.25)',
                background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.04))',
                borderRadius: 12,
                padding: '10px 12px',
                color: 'rgba(255,255,255,0.72)',
                fontSize: 13,
              }}
            >
              {opportunites.length} opportunité{opportunites.length > 1 ? 's' : ''} détectée{opportunites.length > 1 ? 's' : ''}
              {economieMoyenne > 0 && ` ? En moyenne ${economieMoyenne}% sous la cotation moteur`}
            </div>
          </div>
        )}
        {!(tab === 'opportunites' && loadingOpps) && marcheVehicleRows.length > 0 && (
          <div style={{ minWidth: 0 }}>
            <VehicleRowList
              variant={
                tab === 'baisses' ? 'baisse' : tab === 'vendus' ? 'vendu' : tab === 'nouvelles' ? 'nouvelle' : 'opportunite'
              }
              rowHeightPx={VEHICLE_TABLE_ROW_HEIGHT_PX}
              rows={marcheVehicleRowsDisplayed}
              showActionColumn={false}
              showMarcheDelay
              stickyHeader={false}
              sortColumn={marcheSortColumn}
              sortDesc={marcheSortDesc}
              onSort={handleMarcheSort}
            />
          </div>
        )}
        {!marcheHeavyListsExpanded &&
          ((tab === 'vendus' && Array.isArray(data?.vendus) && data.vendus.length >= MARCHE_LIST_LITE) ||
            (tab === 'nouvelles' && Array.isArray(data?.nouvelles) && data.nouvelles.length >= MARCHE_LIST_LITE) ||
            (tab === 'opportunites' && !loadingOpps && opportunites.length >= MARCHE_LIST_LITE)) && (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.03)',
              color: 'rgba(226,232,240,0.65)',
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            Liste courte (20 annonces) pour un chargement plus rapide. Utilisez les filtres (marque, canton, modèle, recherche ou prix) pour charger jusqu'à {MARCHE_LIST_FULL} annonces.
          </div>
        )}
        {tab === 'opportunites' && !loadingOpps && marcheVehicleRows.length > 0 && hasMoreOpps && marcheHeavyListsExpanded && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={handleLoadMoreOpps}
              disabled={loadingMoreOpps}
              style={{
                border: '1px solid rgba(0,30,80,0.35)',
                background: 'rgba(0,30,80,0.14)',
                color: '#dbeafe',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 12,
                fontWeight: 600,
                cursor: loadingMoreOpps ? 'wait' : 'pointer',
              }}
            >
              {loadingMoreOpps ? 'Chargement...' : `Charger ${OPPORTUNITES_PAGE_SIZE} de plus`}
            </button>
          </div>
        )}

        {tab === 'opportunites' && loadingOpps && (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem', fontSize: 14 }}>
            Chargement des opportunités...
          </div>
        )}

        {tab === 'opportunites' && !loadingOpps && marcheVehicleRows.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem', fontSize: 14, lineHeight: 1.6 }}>
            Aucune opportunité détectée{modeleFiltreActif !== 'tous' ? ` pour « ${modeleFiltreActif} »` : ''}.<br />
            Critères : prix &gt; 10% sous la cotation moteur, confiance &ge; 40%. Mise à jour quotidienne.
          </div>
        )}

        {tab !== 'opportunites' && data && displayList.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem', fontSize: 14, lineHeight: 1.55 }}>
            Aucun résultat pour ce filtre
            {tab === 'baisses' && marcheBaissesTriMode === 'recurrentes' ? (
              <span style={{ display: 'block', marginTop: 10, fontSize: 13 }}>
                Aucune annonce avec au moins deux baisses cumulées ici · repassez en vue « Toutes » ou choisissez un autre modèle.
              </span>
            ) : tab === 'baisses' && marcheBaissesTriMode === 'sup5' ? (
              <span style={{ display: 'block', marginTop: 10, fontSize: 13 }}>
                Aucune annonce avec une dernière baisse supérieure à 5% ici · repassez en vue « Toutes » ou choisissez un autre modèle.
              </span>
            ) : null}
          </div>
        )}
      </div>
      {showMarcheFilters ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Filtres marché"
          onClick={() => setShowMarcheFilters(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 4550,
            background: 'rgba(10,12,14,0.55)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 680,
              maxWidth: '100%',
              borderRadius: 10,
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              padding: 14,
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <input
                type="text"
                value={modeleRecherche}
                onChange={(e) => setModeleRecherche(e.target.value)}
                placeholder="Recherche modèle"
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}
              />
              <select
                value={marqueFiltre}
                onChange={(e) => setMarqueFiltre(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}
              >
                {marqueOptions.map((m) => (
                  <option key={m} value={m} style={{ background: '#1a1a1a', color: '#fff' }}>
                    {m === 'toutes' ? 'Toutes les marques' : String(m).charAt(0).toUpperCase() + String(m).slice(1)}
                  </option>
                ))}
              </select>
              <select
                value={modelesLoading ? 'tous' : modeleFiltreActif}
                onChange={(e) => setFiltre(e.target.value)}
                disabled={modelesLoading}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13, opacity: modelesLoading ? 0.7 : 1 }}
              >
                {modelesLoading ? (
                  <option value="tous" style={{ background: '#1a1a1a', color: '#fff' }}>
                    Chargement des modèles...
                  </option>
                ) : (
                  modeleOptions.map((m) => (
                    <option key={m} value={m} style={{ background: '#1a1a1a', color: '#fff' }}>
                      {formatModeleOptionLabel(m)}
                    </option>
                  ))
                )}
              </select>
              <select
                value={cantonFiltre}
                onChange={(e) => setCantonFiltre(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}
              >
                {cantonOptions.map((c) => (
                  <option key={c} value={c} style={{ background: '#1a1a1a', color: '#fff' }}>
                    {c === 'tous' ? 'Tous les cantons' : c}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                step="100"
                value={prixMinInput}
                onChange={(e) => {
                  setPriceFilterTouched(true)
                  setPrixMinInput(e.target.value)
                }}
                placeholder="Prix min"
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}
              />
              <input
                type="number"
                min="0"
                step="100"
                value={prixMaxInput}
                onChange={(e) => {
                  setPriceFilterTouched(true)
                  setPrixMaxInput(e.target.value)
                }}
                placeholder="Prix max"
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button
                type="button"
                onClick={resetMarcheFilters}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.03)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={() => setShowMarcheFilters(false)}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid rgba(126,166,255,0.45)', background: 'rgba(0,30,80,0.2)', color: '#dbeafe', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {marchePanel?.record ? (
        <MarcheVehiclePanel
          token={token}
          record={marchePanel.record}
          variant={marchePanel.variant}
          onClose={() => setMarchePanel(null)}
        />
      ) : null}
      {marcheFicheModele ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Fiche modèle marché"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 4600,
            background: 'rgba(10,12,14,0.55)',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'stretch',
          }}
          onClick={() => setMarcheFicheModele(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 480,
              maxWidth: '100vw',
              height: '100%',
              overflow: 'auto',
              background: 'var(--surface)',
              borderLeft: '1px solid var(--border)',
              padding: '1.25rem 1.35rem',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    marginBottom: 6,
                    fontVariant: 'small-caps',
                  }}
                >
                  Fiche marché
                </div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: 'var(--text)', lineHeight: 1.25 }}>
                  {formatModeleOptionLabel(marcheFicheModele.modelKey)}
                </h3>
                <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Synthèse des annonces actives et du mouvement des prix pour ce slug modèle (filtre marques du compte).
                </p>
              </div>
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => setMarcheFicheModele(null)}
                className="drawer-close-btn"
                style={{
                  flexShrink: 0,
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--text)',
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <X size={18} strokeWidth={1.75} aria-hidden />
              </button>
            </div>
            {marcheFicheModele.loading ? (
              <p style={{ color: 'var(--muted)', fontSize: 14 }}>Chargement de la fiche…</p>
            ) : marcheFicheModele.error ? (
              <p style={{ color: '#fca5a5', fontSize: 14 }}>{marcheFicheModele.error}</p>
            ) : marcheFicheModele.data ? (
              (() => {
                const d = marcheFicheModele.data
                const cells = [
                  { k: 'Annonces actives', v: d.annonces_actives != null ? String(d.annonces_actives) : '—' },
                  { k: 'Prix affiché (médiane)', v: d.prix_median != null ? `${Number(d.prix_median).toLocaleString('fr-CH')} CHF` : '—' },
                  { k: 'Fourchette prix', v: [d.prix_min, d.prix_max].every((x) => x != null) ? `${Number(d.prix_min).toLocaleString('fr-CH')} – ${Number(d.prix_max).toLocaleString('fr-CH')} CHF` : '—' },
                  { k: 'Km (médiane)', v: d.km_median != null ? `${Number(d.km_median).toLocaleString('fr-CH')} km` : '—' },
                  { k: 'Baisses de prix (90 j)', v: d.baisses_prix_90j != null ? String(d.baisses_prix_90j) : '—' },
                  { k: 'Annonces ≥ 2 baisses', v: d.annonces_baisse_recurrente != null ? String(d.annonces_baisse_recurrente) : '—' },
                  { k: 'Vendus (30 j)', v: d.vendus_30j != null ? String(d.vendus_30j) : '—' },
                  { k: 'Annonces cotées', v: d.annonces_cotees != null ? String(d.annonces_cotees) : '—' },
                  {
                    k: 'Écart % moyen (cote)',
                    v: d.ecart_pct_moyen != null ? `${Number(d.ecart_pct_moyen) > 0 ? '+' : ''}${Number(d.ecart_pct_moyen).toLocaleString('fr-CH', { maximumFractionDigits: 1 })} %` : '—',
                  },
                  {
                    k: 'Valeur cotation (médiane)',
                    v: d.valeur_cotation_median != null ? `${Number(d.valeur_cotation_median).toLocaleString('fr-CH')} CHF` : '—',
                  },
                  {
                    k: 'Fourchette cote (médiane)',
                    v:
                      d.fourchette_basse_median != null && d.fourchette_haute_median != null
                        ? `${Number(d.fourchette_basse_median).toLocaleString('fr-CH')} – ${Number(d.fourchette_haute_median).toLocaleString('fr-CH')} CHF`
                        : '—',
                  },
                  { k: 'Opportunités détectées', v: d.opportunites_actives != null ? String(d.opportunites_actives) : '—' },
                ]
                return (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '10px 14px',
                      marginTop: 8,
                    }}
                  >
                    {cells.map((c) => (
                      <div
                        key={c.k}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 6,
                          border: '1px solid var(--border)',
                          background: 'rgba(255,255,255,0.02)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'var(--text-tertiary)',
                            marginBottom: 6,
                            fontVariant: 'small-caps',
                          }}
                        >
                          {c.k}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 300, color: 'var(--text)' }}>{c.v}</div>
                      </div>
                    ))}
                  </div>
                )
              })()
            ) : (
              <p style={{ color: 'var(--muted)', fontSize: 14 }}>Aucune donnée.</p>
            )}
          </div>
        </div>
      ) : null}
      {showBackTop && (
        <button type="button" className="back-top-btn" onClick={scrollMarcheTop}>
          Remonter
        </button>
      )}
    </div>
  )
}

const SITUATION_CONCURRENCE_PRIO = {
  critique: 1,
  sature: 2,
  expose: 3,
  dans_le_marche: 4,
  avantage: 5,
  exclusivite: 6,
}

function stockUrgencyFromAnnonce(a) {
  const ec = Number(a?.ecart_pct)
  const j = Number(a?.jours_en_ligne)
  if (Number.isFinite(ec) && ec >= 10) return 'surcote'
  if (Number.isFinite(j) && j > 90) return 'dort'
  if (Number.isFinite(ec) && ec <= -10) return 'souscote'
  return 'neutral'
}

/** Bordure ligne concurrence alignée sur les urgences stock (rouge / ambre / bleu). */
function concurrenceUrgencyFromSituation(situation) {
  if (situation === 'critique') return 'surcote'
  if (situation === 'expose') return 'dort'
  if (situation === 'sature') return 'dort'
  if (situation === 'exclusivite') return 'souscote'
  return 'neutral'
}

function marcheUrgencyFromRow(row) {
  const ec = Number(row?.ecart_pct ?? row?.ecart_vs_mediane)
  const j = Number(row?.jours_en_ligne)
  if (Number.isFinite(ec) && ec > 8) return 'surcote'
  if (Number.isFinite(j) && j > 90) return 'dort'
  if (Number.isFinite(ec) && ec < -8) return 'souscote'
  return 'neutral'
}

function StockPage({
  token,
  user = null,
  prefs,
  settingsVersion = 0,
  onCoterMoto = null,
  onOpenReglages = null,
  pendingOpenRequest = null,
  onPendingOpenHandled = null,
}) {
  const [data, setData] = useState(null)
  const [depreciations, setDepreciations] = useState({})
  const [loading, setLoading] = useState(true)
  const [stockView, setStockView] = useState('pricing')
  const [filtre, setFiltre] = useState('tous')
  const [stockSortColumn, setStockSortColumn] = useState('jours')
  const [stockSortDesc, setStockSortDesc] = useState(true)
  const [stockPanelAnnonce, setStockPanelAnnonce] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [showBackTop, setShowBackTop] = useState(false)
  const [concurrenceParMoto, setConcurrenceParMoto] = useState({})
  const [concurrentsModelKey, setConcurrentsModelKey] = useState('')
  const [concurrentsCategorie, setConcurrentsCategorie] = useState('all')
  const [concurrentModalSellerId, setConcurrentModalSellerId] = useState(null)
  const [concurrentModalContext, setConcurrentModalContext] = useState(null)
  const [concurrentModalData, setConcurrentModalData] = useState(null)
  const [concurrentModalLoading, setConcurrentModalLoading] = useState(false)
  /** Ligne concurrence développée (détail sous le tableau, même logique que l'ancienne carte). */
  const [concurrenceExpandedKey, setConcurrenceExpandedKey] = useState(null)
  const [concurrenceSortColumn, setConcurrenceSortColumn] = useState('jours')
  const [concurrenceSortDesc, setConcurrenceSortDesc] = useState(true)
  const stockScrollRef = useRef(null)
  const depreciationRequestedRef = useRef(new Set())
  const stockHydratedRef = useRef(false)
  const stockCacheKey = useMemo(() => buildPageCacheKey('stock', token, 'default', user?.id || ''), [token, user?.id])

  // Bouton retour système (mobile) : ferme le détail concurrent dépliée plutôt
  // que de quitter l'app. Les panneaux drawer (StockVehiclePanel,
  // ConcurrenceDetailPanel) sont déjà gérés dans leurs propres composants.
  useBackToClose(Boolean(concurrenceExpandedKey), () => setConcurrenceExpandedKey(null))

  useEffect(() => {
    stockHydratedRef.current = false
    if (!token) return
    const cached = readPageCache(stockCacheKey, 1000 * 60 * 30)
    if (!cached) return
    // PERF FIX 2026-04 : hydrater Mon Stock depuis cache navigateur après reload.
    // Note: pour invalider ce cache après un fix backend, bumper PAGE_CACHE_VERSION
    // (la clé localStorage change → cache miss garanti → re-fetch frais).
    if (cached.data && typeof cached.data === 'object') {
      setData(cached.data)
      setLoading(false)
      stockHydratedRef.current = true
    }
    if (cached.concurrenceParMoto && typeof cached.concurrenceParMoto === 'object') {
      setConcurrenceParMoto(cached.concurrenceParMoto)
    }
  }, [token, stockCacheKey])

  useEffect(() => {
    if (!token || !data || data.erreur || data.error) return
    writePageCache(stockCacheKey, {
      data,
      concurrenceParMoto,
    })
  }, [token, stockCacheKey, data, concurrenceParMoto])

  const loadConcurrence = useCallback(() => {
    if (!token) return
    fetchWithTimeout(`${BACKEND}/api/concurrents/mon-stock`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((rows) => {
        if (!Array.isArray(rows)) {
          setConcurrenceParMoto({})
          return
        }
        const map = {}
        rows.forEach((row) => {
          const id = row?.ma_moto?.id_source
          if (id) map[String(id)] = row
        })
        setConcurrenceParMoto(map)
      })
      .catch(() => setConcurrenceParMoto({}))
  }, [token])

  const loadStock = useCallback((force = false) => {
    if (!token) {
      setLoading(false)
      return
    }
    if (force) setRefreshing(true)
    const qs = new URLSearchParams()
    if (STOCK_DEV_LIMIT > 0) qs.set('limit', String(STOCK_DEV_LIMIT))
    if (STOCK_DEV_FAST_MODE) qs.set('fast', '1')
    if (force) qs.set('refresh', '1')
    // Lancer la concurrence en parallèle avec mon-stock (le backend la calcule
    // directement depuis la DB, pas besoin d'attendre la réponse mon-stock).
    loadConcurrence()
    fetchWithTimeout(
      `${BACKEND}/api/mon-stock?${qs.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
      90000
    )
      .then(async (r) => {
        let body = null
        try {
          body = await r.json()
        } catch {
          throw new Error(`Réponse invalide API mon-stock (${r.status})`)
        }
        if (!r.ok) {
          return {
            ...(body && typeof body === 'object' ? body : {}),
            error:
              body?.error ||
              body?.erreur ||
              `Erreur API mon-stock (${r.status})`,
          }
        }
        return body
      })
      .then((d) => {
        setData(d)
        setLoading(false)
      })
      .catch(() => {
        setData({
          error:
            "Impossible de charger Mon stock pour le moment (connexion/API). Réessayez dans quelques secondes.",
        })
        setLoading(false)
      })
      .finally(() => {
        setRefreshing(false)
      })
  }, [token, loadConcurrence])

  const onStockScroll = useCallback((e) => {
    setShowBackTop(e.currentTarget.scrollTop > 420)
  }, [])

  const scrollStockTop = useCallback(() => {
    stockScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const ouvrirAnnonce = useCallback((url) => {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  useEffect(() => {
    if (!stockHydratedRef.current) setLoading(true)
    loadStock(false)
  }, [loadStock])

  useEffect(() => {
    if (!token || !settingsVersion) return
    loadStock(true)
  }, [settingsVersion, token, loadStock])

  useEffect(() => {
    const models = [...new Set((data?.annonces || []).map((a) => String(a?.model || '').trim()).filter(Boolean))]
    if (!token || models.length === 0) return
    const newModels = models.filter((m) => !depreciationRequestedRef.current.has(m))
    if (newModels.length === 0) return
    newModels.forEach((m) => depreciationRequestedRef.current.add(m))
    fetchWithTimeout(
      `${BACKEND}/api/depreciation/batch?models=${newModels.map(encodeURIComponent).join(',')}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.json())
      .then((batchData) => {
        if (!batchData || typeof batchData !== 'object') return
        setDepreciations((prev) => {
          const next = { ...prev }
          for (const model of newModels) {
            const depData = batchData[model]
            next[model] =
              depData && Number.isFinite(Number(depData.depreciation_par_jour))
                ? {
                    perDay: Number(depData.depreciation_par_jour),
                    nbVentes: Number(depData.nb_ventes) || 0,
                    source: depData.source || null,
                  }
                : null
          }
          return next
        })
      })
      .catch(() => {
        setDepreciations((prev) => {
          const next = { ...prev }
          for (const model of newModels) next[model] = null
          return next
        })
      })
  }, [data, token])

  const toutesAnnonces = useMemo(() => {
    if (!data || data.erreur || data.error || !Array.isArray(data.annonces)) return []
    return data.annonces
  }, [data])

  useEffect(() => {
    const req = pendingOpenRequest
    if (!req?.nonce) return
    // FIX 2026-05 : le dashboard peut demander de pré-appliquer un filtre
    // (ex. clic sur "Surcotés"). Le filtre s'applique avant l'ouverture
    // éventuelle d'une fiche.
    if (req.filter) {
      setFiltre(req.filter)
      setStockView('pricing')
    }
    if (!req.annonce) {
      onPendingOpenHandled?.()
      return
    }
    const id = String(req.annonce.id_source ?? '')
    if (!id) {
      onPendingOpenHandled?.()
      return
    }
    const fromList = toutesAnnonces.find((a) => String(a.id_source) === id)
    const annonce = fromList || req.annonce
    setStockPanelAnnonce(annonce)
    if (req.openPricing) setStockView('pricing')
    const t = window.setTimeout(() => {
      stockScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }, 60)
    onPendingOpenHandled?.()
    return () => clearTimeout(t)
  }, [pendingOpenRequest, toutesAnnonces, onPendingOpenHandled])

  // Véhicules neufs / quasi-neufs : volontairement exclus de la liste de cotation
  // (cote occasion non pertinente). On les compte à part pour le bandeau d'info.
  const isVehiculeNeuf = useCallback(
    (a) => a?.skip_cotation === true || String(a?.positionnement || '') === 'neuf',
    []
  )
  const nbVehiculesNeufs = useMemo(
    () => toutesAnnonces.filter(isVehiculeNeuf).length,
    [toutesAnnonces, isVehiculeNeuf]
  )
  // Annonces incomplètes (titre = juste la marque, modèle non renseigné, etc.)
  // Le concessionnaire les a saisies sans description : aucune cotation fiable
  // possible — on les met en évidence en rouge plutôt que de les afficher
  // comme « exclusivité » trompeuse.
  const nbAnnoncesIncompletes = useMemo(
    () => toutesAnnonces.filter((a) => getAnnonceCompletenessIssues(a).incomplete).length,
    [toutesAnnonces]
  )

  const annonces = useMemo(() => {
    // FIX 2026-05 : filtrer par `ecart_pct` numérique au lieu du texte
    // `positionnement`. Le backend écrit `'surcôté'` (avec ô) mais l'ancien
    // filtre cherchait `'surcoté'` (sans circonflexe) → 0 match alors que
    // le dashboard comptait bien les surcotes. On s'aligne désormais sur
    // les mêmes seuils que `computeStockHealthMetrics` (>=10 / <=-10 / |x|<10)
    // pour garantir que les compteurs et la liste filtrée sont cohérents.
    const ecart = (a) => {
      const v = Number(a?.ecart_pct)
      return Number.isFinite(v) ? v : null
    }
    const joursEnLigne = (a) =>
      a?.jours_en_ligne != null && Number.isFinite(Number(a.jours_en_ligne)) ? Number(a.jours_en_ligne) : null
    const isInconnu = (a) => String(a?.positionnement || '') === 'inconnu'
    return toutesAnnonces.filter((a) => {
      if (isVehiculeNeuf(a)) return false
      if (filtre === 'tous') return true
      const ec = ecart(a)
      if (filtre === 'surcote') return ec != null && ec >= 10 && !isInconnu(a)
      if (filtre === 'marche') return ec != null && Math.abs(ec) < 10 && !isInconnu(a)
      if (filtre === 'souscote') return ec != null && ec <= -10 && !isInconnu(a)
      if (filtre === 'plus90') {
        const jours = joursEnLigne(a)
        return jours != null && jours > 90
      }
      return false
    })
  }, [toutesAnnonces, filtre, isVehiculeNeuf])

  const metricsConcurrents = useMemo(() => {
    const vals = Object.values(concurrenceParMoto)
    let critiqueExpose = 0
    let exclusivites = 0
    let satures = 0
    vals.forEach((r) => {
      const s = r?.concurrence?.situation
      if (s === 'critique' || s === 'expose') critiqueExpose += 1
      if (s === 'exclusivite') exclusivites += 1
      if (s === 'sature') satures += 1
    })
    return { critiqueExpose, exclusivites, satures }
  }, [concurrenceParMoto])

  const concurrenceListeTriee = useMemo(() => {
    const list = Object.values(concurrenceParMoto)
    list.sort((a, b) => {
      const pa = SITUATION_CONCURRENCE_PRIO[a?.concurrence?.situation] ?? 99
      const pb = SITUATION_CONCURRENCE_PRIO[b?.concurrence?.situation] ?? 99
      if (pa !== pb) return pa - pb
      const na = Number(a?.concurrence?.nb_concurrents) || 0
      const nb = Number(b?.concurrence?.nb_concurrents) || 0
      return nb - na
    })
    if (!concurrentsModelKey) return list
    return list.filter((r) => String(r?.ma_moto?.model || '').trim().toLowerCase() === concurrentsModelKey)
  }, [concurrenceParMoto, concurrentsModelKey])

  const concurrenceListeFiltree = useMemo(() => {
    const base = concurrenceListeTriee
    if (concurrentsCategorie === 'all') return base
    if (concurrentsCategorie === 'critique_expose') {
      return base.filter((r) => {
        const s = r?.concurrence?.situation
        return s === 'critique' || s === 'expose'
      })
    }
    if (concurrentsCategorie === 'exclusivite') {
      return base.filter((r) => r?.concurrence?.situation === 'exclusivite')
    }
    if (concurrentsCategorie === 'sature') {
      return base.filter((r) => r?.concurrence?.situation === 'sature')
    }
    if (concurrentsCategorie === 'avantage') {
      return base.filter((r) => r?.concurrence?.situation === 'avantage')
    }
    return base
  }, [concurrenceListeTriee, concurrentsCategorie])

  const handleConcurrenceSort = useCallback((colKey) => {
    if (concurrenceSortColumn === colKey) {
      setConcurrenceSortDesc((d) => !d)
      return
    }
    setConcurrenceSortColumn(colKey)
    setConcurrenceSortDesc(colKey !== 'model')
  }, [concurrenceSortColumn])

  const concurrenceListeSorted = useMemo(() => {
    const list = [...concurrenceListeFiltree]
    const dir = concurrenceSortDesc ? -1 : 1
    const modelKey = (r) => String(r?.ma_moto?.model || r?.ma_moto?.titre || '').toLowerCase()
    const nbConc = (r) => Number(r?.concurrence?.nb_concurrents)
    const prixConc = (r) => Number(r?.ma_moto?.prix_chf)
    const ecartConc = (r) => Number(r?.concurrence?.ecart_vs_median_pct)
    list.sort((a, b) => {
      if (concurrenceSortColumn === 'model') {
        const c = modelKey(a).localeCompare(modelKey(b), 'fr')
        return concurrenceSortDesc ? -c : c
      }
      if (concurrenceSortColumn === 'jours') {
        const av = nbConc(a)
        const bv = nbConc(b)
        if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0
        if (!Number.isFinite(av)) return 1
        if (!Number.isFinite(bv)) return -1
        return (av - bv) * dir
      }
      if (concurrenceSortColumn === 'prix') {
        const av = prixConc(a)
        const bv = prixConc(b)
        if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0
        if (!Number.isFinite(av)) return 1
        if (!Number.isFinite(bv)) return -1
        return (av - bv) * dir
      }
      if (concurrenceSortColumn === 'ecart') {
        const av = ecartConc(a)
        const bv = ecartConc(b)
        if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0
        if (!Number.isFinite(av)) return 1
        if (!Number.isFinite(bv)) return -1
        return (av - bv) * dir
      }
      return 0
    })
    return list
  }, [concurrenceListeFiltree, concurrenceSortColumn, concurrenceSortDesc])

  useEffect(() => {
    if (stockView !== 'concurrence') setConcurrenceExpandedKey(null)
  }, [stockView])

  useEffect(() => {
    if (!concurrenceExpandedKey) return
    const still = concurrenceListeFiltree.some((r) => String(r?.ma_moto?.id_source) === concurrenceExpandedKey)
    if (!still) setConcurrenceExpandedKey(null)
  }, [concurrenceListeFiltree, concurrenceExpandedKey])

  const concurrenceVehicleRows = useMemo(() => {
    return concurrenceListeSorted
      .map((row, idx) => {
        const mm = row.ma_moto
        const c = row.concurrence
        if (!mm || !c) return null
        const id = String(mm.id_source ?? `idx-${idx}`)
        const coterOk = anneeKmOkPourCotationStock(mm)
        const ecartPct = Number(c.ecart_vs_median_pct)
        const priceSuspect = mm.prix_suspect === true
        const completeness = getAnnonceCompletenessIssues(mm)
        const isIncomplete = completeness.incomplete
        const metaParts = [
          mm.first_registration_year,
          mm.km != null ? `${Number(mm.km).toLocaleString('fr-CH')} km` : null,
          [mm.canton, mm.ville].filter(Boolean).join(' · ') || null,
        ].filter(Boolean)
        // Périmètre concurrentiel : déterminé côté backend (canton → rayon → suisse).
        // Couleur + pastille + glyphe dans le badge → état lisible au coup d'œil
        // sans avoir besoin de relire le libellé.
        const sourceGeo = c.source_geo === 'rayon' || c.source_geo === 'suisse' ? c.source_geo : 'canton'
        const scopeWord =
          sourceGeo === 'canton' ? 'cantonale' : sourceGeo === 'rayon' ? 'régionale' : 'Suisse'
        const sitBase = (() => {
          if (c.situation === 'exclusivite') {
            if (sourceGeo === 'canton') return 'Exclusivité cantonale'
            if (sourceGeo === 'rayon') return 'Exclusivité régionale'
            return 'Exclusivité Suisse'
          }
          if (c.situation === 'sature') {
            if (sourceGeo === 'canton') return 'Saturation cantonale'
            if (sourceGeo === 'rayon') return 'Saturation régionale'
            return 'Saturation Suisse'
          }
          if (c.situation === 'critique') return `Pression forte · ${scopeWord}`
          if (c.situation === 'expose') return `Prix exposé · ${scopeWord}`
          if (c.situation === 'avantage') return `Avantage prix · ${scopeWord}`
          return `Dans le marché · ${scopeWord}`
        })()
        const sitShort = isIncomplete
          ? shortIncompleteLabel()
          : priceSuspect
            ? 'Prix à vérifier'
            : sitBase
        const actionPill = isIncomplete
          ? { type: 'none', label: '-' }
          : coterOk.ok
            ? { type: 'secondary', label: 'Coter ce véhicule' }
            : { type: 'none', label: '-' }
        const priceSubText = isIncomplete
          ? null // info déjà portée par le badge rouge à gauche, on évite la redondance
          : priceSuspect
            ? mm.prix_chf == null
              ? 'Prix non scrapé — à renseigner'
              : 'Prix scrapé suspect — à vérifier'
            : c.prix_median_concurrent != null && Number(c.prix_median_concurrent) > 0
              ? `Médiane ${Number(c.prix_median_concurrent).toLocaleString('fr-CH')} CHF`
              : null
        return {
          rowKey: id,
          daysSubStyle: 'badge',
          daysSubSituation: isIncomplete ? 'incomplete' : priceSuspect ? null : c.situation,
          daysSubScope: isIncomplete || priceSuspect ? null : sourceGeo,
          urgency: isIncomplete ? 'rouge' : priceSuspect ? 'orange' : concurrenceUrgencyFromSituation(c.situation),
          imageUrl: mm.image_principale_url || null,
          fallbackLetters: `${String(mm.make || '')[0] || ''}${String(mm.model || '')[0] || ''}`.trim() || '—',
          titleLine: formatVehicleDisplayTitle(mm.titre || `${mm.make || ''} ${mm.model || ''}`.trim()),
          metaLine: metaParts.length ? metaParts.join(' · ') : '—',
          daysMain: isIncomplete
            ? '—'
            : priceSuspect
              ? '—'
              : `${c.nb_concurrents ?? 0} concurrent${Number(c.nb_concurrents) === 1 ? '' : 's'}`,
          daysSub: sitShort,
          priceMain: mm.prix_chf != null ? `${Number(mm.prix_chf).toLocaleString('fr-CH')} CHF` : '—',
          priceSub: priceSubText,
          ecartPct: isIncomplete ? null : Number.isFinite(ecartPct) ? ecartPct : null,
          showPosition: !isIncomplete && !priceSuspect && c.situation !== 'exclusivite' && Number.isFinite(ecartPct),
          showAction: true,
          actionPill,
          onRowClick: () => {
            setConcurrenceExpandedKey((prev) => (prev === id ? null : id))
          },
          onPillClick: () => {
            if (!coterOk.ok) return
            onCoterMoto?.({
              id_source: mm.id_source,
              make: mm.make || 'yamaha',
              model: mm.model,
              annee: coterOk.annee,
              km: coterOk.km,
              titre: mm.titre,
              version: mm.version,
            })
          },
        }
      })
      .filter(Boolean)
  }, [concurrenceListeSorted, onCoterMoto])

  const selectedConcurrenceRow = useMemo(() => {
    if (!concurrenceExpandedKey) return null
    return concurrenceListeFiltree.find((r) => String(r?.ma_moto?.id_source) === concurrenceExpandedKey) || null
  }, [concurrenceListeFiltree, concurrenceExpandedKey])

  useEffect(() => {
    if (!token || !concurrentModalSellerId) {
      setConcurrentModalData(null)
      return
    }
    setConcurrentModalLoading(true)
    const params = new URLSearchParams()
    const modelFocus = String(concurrentModalContext?.model || '').trim().toLowerCase()
    if (modelFocus) params.set('model', modelFocus)
    fetchWithTimeout(`${BACKEND}/api/concurrents/fiche/${concurrentModalSellerId}${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setConcurrentModalData(d))
      .catch(() => setConcurrentModalData({ error: true }))
      .finally(() => setConcurrentModalLoading(false))
  }, [token, concurrentModalSellerId, concurrentModalContext])

  const handleStockSort = useCallback((colKey) => {
    if (stockSortColumn === colKey) {
      setStockSortDesc((d) => !d)
      return
    }
    setStockSortColumn(colKey)
    setStockSortDesc(colKey !== 'model')
  }, [stockSortColumn])


  const annoncesTriees = useMemo(() => {
    const list = [...annonces]
    const dir = stockSortDesc ? -1 : 1
    const joursVal = (x) =>
      x.jours_en_ligne != null && Number.isFinite(Number(x.jours_en_ligne)) ? Number(x.jours_en_ligne) : null
    const ecartVal = (x) =>
      x.ecart_pct != null && Number.isFinite(Number(x.ecart_pct)) ? Number(x.ecart_pct) : null
    const prixVal = (x) => (x.prix != null && Number.isFinite(Number(x.prix)) ? Number(x.prix) : null)
    const modelKey = (x) => String(x.model || x.titre || '').toLowerCase()
    list.sort((a, b) => {
      if (stockSortColumn === 'jours') {
        const av = joursVal(a)
        const bv = joursVal(b)
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        return (av - bv) * dir
      }
      if (stockSortColumn === 'ecart') {
        const av = ecartVal(a)
        const bv = ecartVal(b)
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        return (av - bv) * dir
      }
      if (stockSortColumn === 'prix') {
        const av = prixVal(a)
        const bv = prixVal(b)
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        return (av - bv) * dir
      }
      if (stockSortColumn === 'model') {
        const cmp = modelKey(a).localeCompare(modelKey(b), 'fr')
        return stockSortDesc ? -cmp : cmp
      }
      return 0
    })
    return list
  }, [annonces, stockSortColumn, stockSortDesc])

  useEffect(() => {
    if (!stockPanelAnnonce && !concurrenceExpandedKey) return undefined
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      if (stockPanelAnnonce) setStockPanelAnnonce(null)
      if (concurrenceExpandedKey) setConcurrenceExpandedKey(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [stockPanelAnnonce, concurrenceExpandedKey])

  const filtres = [
    { key: 'tous', label: 'Tous' },
    { key: 'surcote', label: 'Surcotées' },
    { key: 'marche', label: 'Dans le marché' },
    { key: 'souscote', label: 'Sous-cotées' },
    { key: 'plus90', label: '+90j' },
  ]

  const filtresConcurrence = [
    { key: 'all', label: 'Tous' },
    { key: 'critique_expose', label: 'Critiques / exposées' },
    { key: 'exclusivite', label: 'Exclusivités' },
    { key: 'sature', label: 'Saturées' },
    { key: 'avantage', label: 'Avantage' },
  ]

  const seuilStockLong = Number(prefs?.seuil_stock_long) || DEFAULT_PREFS.seuil_stock_long

  const stockQueueActions = useMemo(() => {
    const health = computeStockHealthMetrics(toutesAnnonces, seuilStockLong)
    return buildStockHealthActions(
      health,
      seuilStockLong,
      {
        resolveAnnonce: (id) => toutesAnnonces.find((a) => String(a.id_source) === String(id)),
        onBaisserPrix: (annonce) => {
          setStockPanelAnnonce(annonce)
          setStockView('pricing')
        },
        onVoirFiche: (annonce) => setStockPanelAnnonce(annonce),
      },
      concurrenceParMoto
    )
  }, [toutesAnnonces, seuilStockLong, concurrenceParMoto])

  function genererRecommandation(moto) {
    const { ecart_pct, jours_en_ligne, jours_estimes_vente, score_liquidite, prix, valeur_marche, positionnement } = moto

    if (ecart_pct > 15 && valeur_marche != null && prix != null) {
      const prixCible = Math.round((valeur_marche * 1.02) / 100) * 100
      const baisse = prix - prixCible
      return {
        action: 'Baisser le prix',
        detail: `Baisser de ${baisse.toLocaleString()} CHF → ${prixCible.toLocaleString()} CHF pour vendre ~2x plus vite`,
        urgence: 'rouge',
      }
    }

    if (jours_en_ligne > seuilStockLong && ecart_pct > 5 && valeur_marche != null) {
      const prixCible = Math.round((valeur_marche * 0.98) / 100) * 100
      return {
        action: 'Réviser le prix',
        detail: `${jours_en_ligne}j en stock · passer à ${prixCible.toLocaleString()} CHF pour accélérer la vente`,
        urgence: 'orange',
      }
    }

    if (ecart_pct < -10 && score_liquidite >= 6) {
      const prixVenteRapide = valeur_marche != null ? Math.round((valeur_marche * 0.99) / 100) * 100 : null
      return {
        action: 'Prix attractif',
        detail: prixVenteRapide
          ? `Véhicule bien placé pour vendre vite. Zone de vente conseillée : ${prixVenteRapide.toLocaleString()} CHF`
          : 'Véhicule bien positionné · bonne liquidité',
        urgence: 'vert',
      }
    }

    if (jours_en_ligne > 60 && positionnement === 'dans le marché') {
      return {
        action: 'Mettre en avant',
        detail: `${jours_en_ligne}j en stock — booster la visibilité ou offrir une garantie`,
        urgence: 'orange',
      }
    }

    if (ecart_pct <= 0 && score_liquidite >= 7) {
      return {
        action: 'Prix optimal',
        detail: `Bonne liquidité · vente estimée ~${jours_estimes_vente || '—'} jours`,
        urgence: 'vert',
      }
    }

    return null
  }

  /* eslint-disable react-hooks/exhaustive-deps -- genererRecommandation : closure stable via seuilStockLong */
  const stockVehicleRows = useMemo(() => {
    return annoncesTriees.map((a, i) => {
      const cardId = String(a.id_source || `${a.titre}-${i}`)
      const priceSuspect = a.prix_suspect === true
      const completeness = getAnnonceCompletenessIssues(a)
      const isIncomplete = completeness.incomplete
      const joursEnLigneNum =
        a.jours_en_ligne != null && Number.isFinite(Number(a.jours_en_ligne)) ? Number(a.jours_en_ligne) : null
      let daysSub = null
      if (joursEnLigneNum != null && joursEnLigneNum > 90 && a.valeur_marche) {
        const fallbackDep = (a.valeur_marche * 0.004) / 30
        const depMeta = depreciations[a.model]
        const depModele = Number(depMeta?.perDay)
        const depNbVentes = Number(depMeta?.nbVentes) || 0
        const depMaxPct = (a.valeur_marche * 0.018) / 30
        const depHardCap = 8
        let depBrut = Number.isFinite(depModele) && depModele > 0 ? depModele : fallbackDep
        if (a.confiance === 'faible') depBrut *= 0.8
        else if (a.confiance === 'moyenne') depBrut *= 0.9
        if (depNbVentes > 0 && depNbVentes < 10) depBrut *= 0.85
        if (joursEnLigneNum < 15) depBrut *= 0.7
        const depParJour = Math.min(depBrut, depMaxPct, depHardCap)
        const depreciationParJour = Math.max(0, Math.round(depParJour))
        if (depreciationParJour > 0) daysSub = `~${depreciationParJour} CHF/j`
      }
      const metaParts = [
        a.annee,
        a.km != null ? `${Number(a.km).toLocaleString('fr-CH')} km` : null,
        a.canton || a.ville,
      ].filter(Boolean)
      const isSkipNeuf = a.skip_cotation === true || a.positionnement === 'neuf'
      const priceSubText = isIncomplete
        ? null // info déjà portée par le badge rouge à gauche, on évite la redondance
        : priceSuspect
          ? a.prix == null
            ? 'Prix non scrapé — à renseigner'
            : 'Prix scrapé suspect — à vérifier'
          : isSkipNeuf
            ? 'Neuf · comparer au tarif catalogue'
            : a.valeur_marche != null && Number.isFinite(Number(a.valeur_marche))
              ? `Marché ${Number(a.valeur_marche).toLocaleString('fr-CH')} CHF`
              : null
      const positionSubText = isIncomplete
        ? null
        : priceSuspect
          ? null
          : isSkipNeuf
            ? 'Pas de cote occasion (km < 500 ou millésime récent)'
            : cotationReferenceLabel(a)
      const daysSubFinal = isIncomplete ? shortIncompleteLabel() : daysSub
      return {
        rowKey: cardId,
        sourceAnnonce: a,
        urgency: isIncomplete ? 'rouge' : priceSuspect ? 'orange' : isSkipNeuf ? 'neutral' : stockUrgencyFromAnnonce(a),
        imageUrl: a.image_url,
        fallbackLetters: `${String(a.make || '')[0] || ''}${String(a.model || '')[0] || ''}`.trim() || '—',
        titleLine: a.titre,
        metaLine: metaParts.length ? metaParts.join(' · ') : '—',
        daysMain: joursEnLigneNum != null ? `${joursEnLigneNum} j` : '—',
        daysSub: daysSubFinal,
        daysSubStyle: isIncomplete ? 'badge' : undefined,
        daysSubSituation: isIncomplete ? 'incomplete' : undefined,
        priceMain: a.prix != null ? `${Number(a.prix).toLocaleString('fr-CH')} CHF` : '—',
        priceSub: priceSubText,
        ecartPct: isIncomplete || priceSuspect || isSkipNeuf ? null : a.ecart_pct,
        positionSub: positionSubText,
        showPosition: !isIncomplete && !priceSuspect && !isSkipNeuf,
        onRowClick: () => setStockPanelAnnonce(a),
      }
    })
  }, [annoncesTriees, depreciations, seuilStockLong])
  /* eslint-enable react-hooks/exhaustive-deps */

  if (loading)
    return (
      <div className="stock-loading" role="status" aria-live="polite" aria-busy="true">
        <div className="stock-loading-ring" aria-hidden />
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f4f4f5', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Analyse du stock en cours
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
            Cotation et comparaison marché pour chaque annonce — merci de patienter.
          </div>
          <div className="stock-loading-bar stock-loading-bar--indeterminate" aria-hidden />
        </div>
      </div>
    )
  if (!data || data.erreur || data.error)
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}>
        {data?.erreur || data?.error || 'Aucun stock lié à ce compte.'}
      </div>
    )

  return (
    <div
      className="stock-page-root"
      ref={stockScrollRef}
      onScroll={onStockScroll}
      style={{ flex: 1, overflow: 'auto', padding: 'clamp(1rem, 2.2vw, 2rem)', position: 'relative' }}
    >
      {refreshing && (
        <div className="stock-refresh-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="stock-loading-ring" aria-hidden />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#e5e5e5' }}>Recalcul du stock…</span>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Les chiffres se mettent à jour</span>
        </div>
      )}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
          Analyse de stock
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <p style={{ color: 'var(--muted)', fontSize: 14 }}>{data.nb_annonces} véhicules analysés</p>
          <button
            type="button"
            onClick={() => loadStock(true)}
            disabled={refreshing}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'rgba(255,255,255,0.03)',
              color: '#bfdbfe',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {refreshing ? 'Recalcul...' : 'Recalculer'}
          </button>
        </div>
      </div>
      {(() => {
        const {
          surcotes,
          souscotes,
          dansMarche: dansMarch,
          stockLong,
          score,
          scoreColor,
          scoreLabel,
        } = computeStockHealthMetrics(toutesAnnonces, seuilStockLong)

        return (
          <div
            className="stock-summary-card"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: '1.5rem',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 32,
            }}
          >
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 4 }}>
                <div style={{ fontSize: 52, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{score}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.45)', lineHeight: 1.2 }}>/100</div>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>SANTÉ STOCK (score sur 100)</div>
            </div>
            <div style={{ width: 1, height: 60, background: 'var(--border)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: scoreColor, marginBottom: 8 }}>{scoreLabel}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>
                Le détail des priorités figure dans la file d&apos;actions sous cette carte.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
              {[
                { val: dansMarch.length, label: 'Dans le marché', color: '#4ade80' },
                { val: surcotes.length, label: 'Surcotées', color: '#f87171' },
                { val: souscotes.length, label: 'Sous-cotées', color: '#60efbc' },
                { val: stockLong.length, label: `+${seuilStockLong} jours`, color: '#fbbf24' },
              ].map((s, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )
      })()}
      <div style={{ marginBottom: 24 }}>
        <ActionQueue
          sectionId="stock-optimize-queue"
          idPrefix="stock-aq"
          title="Stock à optimiser"
          actions={stockQueueActions}
          maxVisible={5}
          listLabel="Priorités stock à traiter"
        />
      </div>
      {(() => {
        // Bloc unifié des contrôles : Vue / Modèle (concurrence) / Filtre.
        // Chaque rangée est séparée par un trait fin et préfixée d'un label
        // de rôle pour repérer instantanément ce qu'on est en train de régler.
        const rowBase = {
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 18,
          padding: '10px 14px',
        }
        const labelStyle = {
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--text-tertiary)',
          minWidth: 56,
          flex: '0 0 auto',
        }
        const tabBtn = (active) => ({
          padding: '6px 0',
          border: 'none',
          borderBottom: active ? '2px solid #001E50' : '2px solid transparent',
          background: 'transparent',
          color: active ? 'var(--text)' : 'var(--text-secondary)',
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        })
        const showModelRow = stockView === 'concurrence' && concurrentsModelKey
        const showFilterRow = stockView === 'pricing' || stockView === 'concurrence'
        return (
          <div
            className="stock-controls"
            style={{
              border: '1px solid var(--border)',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: 8,
              marginBottom: '1.25rem',
              overflow: 'hidden',
            }}
          >
            <div
              className="stock-view-tabs"
              style={{
                ...rowBase,
                borderBottom: showModelRow || showFilterRow ? '1px solid var(--border)' : 'none',
              }}
            >
              <span style={labelStyle}>Vue</span>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', flex: 1, minWidth: 0 }}>
                {[
                  { key: 'pricing', label: 'Positionnement prix' },
                  { key: 'concurrence', label: 'Concurrence', badge: metricsConcurrents.critiqueExpose },
                ].map((t) => (
                  <button key={t.key} type="button" onClick={() => setStockView(t.key)} style={tabBtn(stockView === t.key)}>
                    {t.label}
                    {t.badge > 0 ? (
                      <span
                        style={{
                          minWidth: 18,
                          height: 18,
                          lineHeight: '18px',
                          borderRadius: 4,
                          border: '1px solid rgba(163,45,45,0.35)',
                          background: 'rgba(163,45,45,0.12)',
                          color: '#e7a39a',
                          fontSize: 10,
                          fontWeight: 700,
                          textAlign: 'center',
                          padding: '0 5px',
                        }}
                      >
                        {t.badge}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>

            {showModelRow ? (
              <div
                style={{
                  ...rowBase,
                  background: 'rgba(0,30,80,0.05)',
                  borderBottom: showFilterRow ? '1px solid var(--border)' : 'none',
                }}
              >
                <span style={labelStyle}>Modèle</span>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 999,
                      border: '1px solid rgba(0,30,80,0.35)',
                      background: 'rgba(0,30,80,0.18)',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text)',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {concurrentsModelKey}
                  </span>
                  <button
                    type="button"
                    onClick={() => setConcurrentsModelKey('')}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      background: 'transparent',
                      color: 'var(--text-secondary)',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                    title="Réinitialiser le filtre modèle"
                  >
                    × Tous les modèles
                  </button>
                </div>
              </div>
            ) : null}

            {stockView === 'pricing' ? (
              <div className="stock-filter-tabs" style={rowBase}>
                <span style={labelStyle}>Filtre</span>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  {filtres.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFiltre(f.key)}
                      style={tabBtn(filtre === f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {stockView === 'concurrence' ? (
              <div style={rowBase}>
                <span style={labelStyle}>Filtre</span>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  {filtresConcurrence.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setConcurrentsCategorie(f.key)}
                      style={tabBtn(concurrentsCategorie === f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )
      })()}
      {stockView === 'concurrence' && concurrenceListeFiltree.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '2rem', textAlign: 'center', marginBottom: 24 }}>
          Aucune donnée de concurrence pour le moment (ou aucun véhicule ne correspond au filtre).
        </div>
      )}
      {stockView === 'concurrence' && concurrenceListeFiltree.length > 0 && (
        <div style={{ marginBottom: 28, minWidth: 0 }}>
          <VehicleRowList
            variant="concurrence"
            rowHeightPx={VEHICLE_TABLE_ROW_HEIGHT_PX}
            rows={concurrenceVehicleRows}
            showActionColumn
            stickyHeader={false}
            sortColumn={concurrenceSortColumn}
            sortDesc={concurrenceSortDesc}
            onSort={handleConcurrenceSort}
          />
        </div>
      )}
      {selectedConcurrenceRow && stockView === 'concurrence' && (
        <ConcurrenceDetailPanel
          row={selectedConcurrenceRow}
          coterStock={anneeKmOkPourCotationStock(selectedConcurrenceRow.ma_moto)}
          onClose={() => setConcurrenceExpandedKey(null)}
          onCoterMoto={onCoterMoto}
          onOpenMonAnnonce={(url) => ouvrirAnnonce(url)}
          onOpenFicheConcurrent={({ sellerId, context }) => {
            setConcurrenceExpandedKey(null)
            setConcurrentModalContext(context)
            setConcurrentModalSellerId(sellerId)
          }}
          onOpenReglages={
            onOpenReglages
              ? () => {
                  setConcurrenceExpandedKey(null)
                  onOpenReglages()
                }
              : undefined
          }
        />
      )}
      {stockView === 'pricing' && nbAnnoncesIncompletes > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '10px 14px',
            marginBottom: 12,
            border: '1px solid rgba(220,38,38,0.45)',
            background: 'rgba(220,38,38,0.08)',
            borderRadius: 8,
            color: '#fecaca',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <span
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'rgba(220,38,38,0.25)',
              color: '#fecaca',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {nbAnnoncesIncompletes}
          </span>
          <span>
            <strong style={{ color: '#fee2e2' }}>
              {nbAnnoncesIncompletes === 1 ? 'annonce incomplète' : 'annonces incomplètes'}
            </strong>{' '}
            détectée{nbAnnoncesIncompletes > 1 ? 's' : ''} dans votre stock (titre = juste la marque, modèle non renseigné…). Aucune cotation ni comparaison fiable possible — corrigez la fiche sur MotoScout pour qu'elle soit analysée.
          </span>
        </div>
      )}
      {stockView === 'pricing' && nbVehiculesNeufs > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            marginBottom: 16,
            border: '1px solid rgba(148,163,184,0.18)',
            background: 'rgba(148,163,184,0.06)',
            borderRadius: 8,
            color: 'var(--text-secondary)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <span
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'rgba(96,165,250,0.18)',
              color: '#93c5fd',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {nbVehiculesNeufs}
          </span>
          <span>
            <strong style={{ color: 'var(--text)' }}>
              {nbVehiculesNeufs === 1 ? 'véhicule neuf' : 'véhicules neufs'} masqué{nbVehiculesNeufs > 1 ? 's' : ''}
            </strong>{' '}
            de la cotation occasion (km &lt; 500 ou millésime récent). Comparer au tarif catalogue concessionnaire.
          </span>
        </div>
      )}
      {stockView === 'pricing' && (
        <div style={{ marginBottom: 28, minWidth: 0 }}>
          <VehicleRowList
            variant="stock"
            rowHeightPx={VEHICLE_TABLE_ROW_HEIGHT_PX}
            sortColumn={stockSortColumn}
            sortDesc={stockSortDesc}
            onSort={handleStockSort}
            rows={stockVehicleRows}
            showActionColumn={false}
            stickyHeader={false}
          />
        </div>
      )}
      {stockPanelAnnonce && (
        <StockVehiclePanel
          token={token}
          annonce={stockPanelAnnonce}
          depreciations={depreciations}
          reco={genererRecommandation(stockPanelAnnonce)}
          concurrenceRow={concurrenceParMoto[String(stockPanelAnnonce.id_source || '')]}
          onClose={() => setStockPanelAnnonce(null)}
          onNavigateConcurrence={(modelKey) => {
            setStockPanelAnnonce(null)
            setStockView('concurrence')
            setConcurrentsModelKey(modelKey)
            stockScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
          }}
          onCoterMoto={onCoterMoto}
          coterStock={anneeKmOkPourCotationStock({
            ...stockPanelAnnonce,
            first_registration_year: stockPanelAnnonce.annee,
          })}
        />
      )}
      {concurrentModalSellerId != null && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '24px 12px',
            overflow: 'auto',
          }}
          onClick={() => {
            setConcurrentModalSellerId(null)
            setConcurrentModalContext(null)
            setConcurrentModalData(null)
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: 720,
              marginTop: 24,
              borderRadius: 16,
              border: '1px solid var(--border)',
              background: '#14141f',
              padding: '1.25rem 1.35rem',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
          >
            <button
              type="button"
              aria-label="Fermer"
              onClick={() => {
                setConcurrentModalSellerId(null)
                setConcurrentModalContext(null)
                setConcurrentModalData(null)
              }}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                width: 32,
                height: 32,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'rgba(255,255,255,0.06)',
                color: '#e5e5e5',
                fontSize: 18,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              <X size={18} strokeWidth={1.75} aria-hidden />
            </button>
            <h3 style={{ margin: '0 40px 12px 0', fontSize: 18, fontWeight: 800, color: '#fff' }}>Fiche concurrent</h3>
            {concurrentModalLoading && (
              <div style={{ color: 'var(--muted)', fontSize: 14, padding: '1rem 0' }}>Chargement…</div>
            )}
            {!concurrentModalLoading && concurrentModalData?.error && (
              <div style={{ color: '#f87171', fontSize: 14 }}>Impossible de charger la fiche.</div>
            )}
            {!concurrentModalLoading && concurrentModalData && !concurrentModalData.error && (
              <>
                {!!String(concurrentModalData.model_focus || concurrentModalContext?.model || '').trim() && (
                  <div
                    style={{
                      marginBottom: 10,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid rgba(0,30,80,0.35)',
                      background: 'rgba(0,30,80,0.12)',
                      fontSize: 12,
                      color: '#dbeafe',
                    }}
                  >
                    Fiche centrée sur le modèle :{' '}
                    <strong style={{ color: '#fff' }}>
                      {String(concurrentModalData.model_focus || concurrentModalContext?.model || '').toUpperCase()}
                    </strong>
                  </div>
                )}
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 16, lineHeight: 1.5 }}>
                  <strong style={{ color: '#fff' }}>{concurrentModalData.ville || 'Ville inconnue'}</strong>
                  {' · '}
                  stock actif <strong style={{ color: '#bfdbfe' }}>{concurrentModalData.nb_annonces_actives ?? 0}</strong>
                  {' · '}
                  vendus 30j <strong style={{ color: '#bfdbfe' }}>{concurrentModalData.nb_vendus_30j ?? 0}</strong>
                  {' · '}
                  délai vente moyen{' '}
                  <strong style={{ color: '#bfdbfe' }}>
                    {concurrentModalData.delai_vente_moyen != null ? `${concurrentModalData.delai_vente_moyen} j` : '—'}
                  </strong>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase' }}>
                  Modèles en commun avec votre stock
                </div>
                <div style={{ overflowX: 'auto', marginBottom: 18 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: 'var(--muted)', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Modèle</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Leur prix</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Votre prix</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Écart</th>
                        <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(concurrentModalData.modeles_en_commun || []).map((m, i) => {
                        const leurPrix = Number(m?.leur_moto?.prix_chf)
                        const monPrix = Number(m?.ma_moto?.prix_chf)
                        const verdict = m.avantage === 'moi' ? 'Moi' : 'Concurrent'
                        const rowColor = m.avantage === 'moi' ? '#4ade80' : '#f87171'
                        return (
                          <tr key={i} style={{ color: '#e5e5e5' }}>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{m.model}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                              {Number.isFinite(leurPrix) ? `${leurPrix.toLocaleString('fr-CH')} CHF` : '—'}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                              {Number.isFinite(monPrix) ? `${monPrix.toLocaleString('fr-CH')} CHF` : '—'}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                              {m.ecart_pct != null ? `${m.ecart_pct > 0 ? '+' : ''}${m.ecart_pct}%` : '—'}
                            </td>
                            <td style={{ padding: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: 800, color: rowColor }}>
                              {verdict}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {Array.isArray(concurrentModalData.modeles_en_commun) && concurrentModalData.modeles_en_commun[0]?.leur_moto && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid rgba(248,113,113,0.3)',
                      background: 'rgba(248,113,113,0.08)',
                      fontSize: 12,
                      color: '#e5e5e5',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#fca5a5', marginBottom: 4 }}>
                      Concurrent le moins cher (modèle ciblé)
                    </div>
                    <div style={{ lineHeight: 1.45 }}>
                      <strong style={{ color: '#fff' }}>
                        {concurrentModalData.modeles_en_commun[0]?.leur_moto?.titre || 'Annonce concurrente'}
                      </strong>
                      {' · '}
                      {concurrentModalData.modeles_en_commun[0]?.leur_moto?.prix_chf != null
                        ? `${Number(concurrentModalData.modeles_en_commun[0].leur_moto.prix_chf).toLocaleString('fr-CH')} CHF`
                        : '—'}
                      {' · '}
                      {concurrentModalData.modeles_en_commun[0]?.leur_moto?.km != null
                        ? `${Number(concurrentModalData.modeles_en_commun[0].leur_moto.km).toLocaleString('fr-CH')} km`
                        : '—'}
                      {' · '}
                      {concurrentModalData.modeles_en_commun[0]?.leur_moto?.first_registration_year ?? '—'}
                    </div>
                    {concurrentModalData.modeles_en_commun[0]?.leur_moto?.url && (
                      <a
                        href={concurrentModalData.modeles_en_commun[0].leur_moto.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'inline-block', marginTop: 6, color: '#fca5a5', fontWeight: 700, textDecoration: 'underline' }}
                      >
                        Voir cette annonce
                      </a>
                    )}
                  </div>
                )}
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase' }}>
                  Leurs baisses de prix (14 derniers jours)
                </div>
                {(!concurrentModalData.baisses_14j || concurrentModalData.baisses_14j.length === 0) && (
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>Aucune baisse récente détectée</div>
                )}
                {concurrentModalData.baisses_14j && concurrentModalData.baisses_14j.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    {concurrentModalData.baisses_14j.length >= 3 && (
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#fbbf24',
                          marginBottom: 8,
                          padding: '8px 10px',
                          borderRadius: 8,
                          background: 'rgba(245,158,11,0.12)',
                          border: '1px solid rgba(245,158,11,0.35)',
                        }}
                      >
                        ⚠ Liquidation possible en cours
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflow: 'auto' }}>
                      {concurrentModalData.baisses_14j.map((b, i) => {
                        const d = b.created_at ? new Date(b.created_at).toLocaleDateString('fr-CH') : '—'
                        const delta = Number(b.delta)
                        return (
                          <div key={i} style={{ fontSize: 12, color: '#e5e5e5', lineHeight: 1.45 }}>
                            {b.titre ? `${b.titre} · ` : ''}
                            {b.ancien_prix != null ? `${Number(b.ancien_prix).toLocaleString('fr-CH')}` : '—'} CHF →{' '}
                            {b.nouveau_prix != null ? `${Number(b.nouveau_prix).toLocaleString('fr-CH')}` : '—'} CHF ·{' '}
                            <span style={{ color: '#f87171', fontWeight: 700 }}>
                              {Number.isFinite(delta) ? `${delta.toLocaleString('fr-CH')}` : '—'} CHF
                            </span>
                            {' · '}
                            {d}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase' }}>
                  Leur stock complet ({(concurrentModalData.stock_complet || []).length} motos)
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(concurrentModalData.stock_complet || []).map((s, i) => (
                    <div
                      key={s.id_source || i}
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: '#e5e5e5', flex: 1, minWidth: 140 }}>{s.titre}</span>
                      <span style={{ color: '#fff', fontWeight: 700 }}>{s.prix_chf != null ? `${Number(s.prix_chf).toLocaleString('fr-CH')} CHF` : '—'}</span>
                      <span style={{ color: 'var(--muted)' }}>
                        {s.km != null ? `${Number(s.km).toLocaleString('fr-CH')} km` : '—'} · {s.first_registration_year ?? '—'}
                      </span>
                      {s.url && (
                        <a href={s.url} target="_blank" rel="noreferrer" style={{ color: '#bfdbfe', fontWeight: 700, fontSize: 11 }}>
                          Voir sur MotoScout24
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showBackTop && (
        <button type="button" className="back-top-btn" onClick={scrollStockTop}>
          Remonter
        </button>
      )}
    </div>
  )
}

export default function App() {
  const [page, setPage] = useState('landing')
  // FIX 2026-05 : l'onglet Dashboard est désactivé de la nav (cf. handleLogin et
  // chat-tabs ci-dessous). On l'a remplacé par Mon Stock comme vue d'entrée
  // par défaut. Le composant DashboardToday reste compilé mais inaccessible —
  // pour le réactiver, ré-ajouter les boutons dans la nav.
  const [chatTab, setChatTab] = useState('stock') // 'cotation' | 'marche' | 'stock' | 'leasing' | 'analyse'
  const [marcheInitialTab, setMarcheInitialTab] = useState('baisses')
  /** Ouvre la fiche Synthèse modèle au prochain affichage de l'onglet Marché (ex. depuis la cotation). */
  const [marchePendingFiche, setMarchePendingFiche] = useState(null)
  /** Ouverture Mon stock depuis le dashboard (fiche + option onglet prix). */
  const [stockPendingOpen, setStockPendingOpen] = useState(null)
  const [dashboardPanelMounted, setDashboardPanelMounted] = useState(true)
  const [marchePanelMounted, setMarchePanelMounted] = useState(false)
  /** Garde Mon Stock monté après la 1re visite : pas de re-fetch à chaque clic d'onglet. */
  const [stockPanelMounted, setStockPanelMounted] = useState(false)
  const [leasingPanelMounted, setLeasingPanelMounted] = useState(false)
  const [analysePanelMounted, setAnalysePanelMounted] = useState(false)
  const [token, setToken] = useState(() => localStorage.getItem('radar_token'))
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('radar_user')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  })
  const [authChecked, setAuthChecked] = useState(false)
  const [loginEntryMode, setLoginEntryMode] = useState('login')
  const [showSettings, setShowSettings] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false)
  )
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)
  const [settingsVersion, setSettingsVersion] = useState(0)
  const [cotationPrefillRequest, setCotationPrefillRequest] = useState(null)
  const [leasingPrefillRequest, setLeasingPrefillRequest] = useState(null)
  const pathName = typeof window !== 'undefined' ? window.location.pathname : '/'
  const publicLeasingMatch = pathName.match(/^\/p\/leasing\/([^/]+)$/)

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = (e) => {
      setIsMobileViewport(e.matches)
      if (!e.matches) setMobileNavOpen(false)
    }
    setIsMobileViewport(mq.matches)
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
    mq.addListener(onChange)
    return () => mq.removeListener(onChange)
  }, [])

  const goPlatform = useCallback(() => {
    setLoginEntryMode('login')
    if (token) setPage('chat')
    else setPage('login')
  }, [token])

  const goRegister = useCallback(() => {
    setLoginEntryMode('register')
    setPage('login')
  }, [])

  useEffect(() => {
    if (!token) {
      setUser(null)
      setPrefs(DEFAULT_PREFS)
      setAuthChecked(true)
      return
    }
    setAuthChecked(false)
    fetchWithTimeout(`${BACKEND}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }, 15000)
      .then((r) => r.json())
      .then((u) => {
        if (u && u.id) {
          setUser(u)
          localStorage.setItem('radar_user', JSON.stringify(u))
        } else {
          localStorage.removeItem('radar_token')
          localStorage.removeItem('radar_user')
          setToken(null)
          setUser(null)
        }
      })
      .catch(() => {
        localStorage.removeItem('radar_token')
        localStorage.removeItem('radar_user')
        setToken(null)
        setUser(null)
      })
      .finally(() => setAuthChecked(true))
  }, [token])

  useEffect(() => {
    if (!token) return
    fetchWithTimeout(`${BACKEND}/api/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        setPrefs({
          ...DEFAULT_PREFS,
          ...(data && typeof data === 'object' ? data : {}),
        })
      })
      .catch(() => {})
  }, [token])

  useEffect(() => {
    if (chatTab === 'stock') setStockPanelMounted(true)
    if (chatTab === 'dashboard') setDashboardPanelMounted(true)
    if (chatTab === 'marche') setMarchePanelMounted(true)
    if (chatTab === 'leasing') setLeasingPanelMounted(true)
    if (chatTab === 'analyse') setAnalysePanelMounted(true)
  }, [chatTab])

  useEffect(() => {
    if (page !== 'landing') return undefined
    const root = document.querySelector('.landing')
    if (!root) return undefined
    const nodes = root.querySelectorAll('.landing-reveal')
    if (!nodes.length) return undefined
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('landing-reveal--in')
            io.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    )
    nodes.forEach((node) => io.observe(node))
    return () => io.disconnect()
  }, [page])

  useEffect(() => {
    if (!token) {
      setStockPanelMounted(false)
      setMarchePanelMounted(false)
      setLeasingPanelMounted(false)
      setAnalysePanelMounted(false)
      setDashboardPanelMounted(true)
      setStockPendingOpen(null)
    }
  }, [token])

  useEffect(() => {
    const onStorage = () => {
      setToken(localStorage.getItem('radar_token'))
      try {
        const raw = localStorage.getItem('radar_user')
        setUser(raw ? JSON.parse(raw) : null)
      } catch {
        setUser(null)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('radar_token')
    localStorage.removeItem('radar_user')
    setToken(null)
    setUser(null)
    setShowSettings(false)
    setPage('landing')
  }

  const handleLogin = (u, t) => {
    setUser(u)
    setToken(t)
    setChatTab('stock')
    setPage('chat')
  }

  const switchChatTab = useCallback((nextTab) => {
    if (LOCKED_CHAT_TABS.has(nextTab)) return
    setChatTab(nextTab)
    setMobileNavOpen(false)
    if (window.location.pathname === '/leasing') {
      window.history.replaceState({}, '', '/')
    }
  }, [])

  const clearStockPendingOpen = useCallback(() => {
    setStockPendingOpen(null)
  }, [])

  /** @param {object} [annonce] @param {{ openPricing?: boolean, stockFilter?: 'tous'|'surcote'|'marche'|'souscote'|'plus90' }} [opts] */
  const handleOpenStockFromDashboard = useCallback(
    (annonce, opts = {}) => {
      const requestedFilter = String(opts?.stockFilter || '').trim()
      const filterValid =
        requestedFilter === 'tous' ||
        requestedFilter === 'surcote' ||
        requestedFilter === 'marche' ||
        requestedFilter === 'souscote' ||
        requestedFilter === 'plus90'
      const validAnnonce =
        annonce &&
        typeof annonce === 'object' &&
        annonce.id_source != null &&
        String(annonce.id_source) !== ''
      // FIX 2026-05 : on passe le filtre via pendingOpenRequest (StockPage owne
      // son state filtre), sinon `setFiltre` est undefined dans App.
      if (filterValid || validAnnonce) {
        setStockPendingOpen({
          nonce: Date.now(),
          annonce: validAnnonce ? annonce : null,
          openPricing: Boolean(opts.openPricing),
          filter: filterValid ? requestedFilter : null,
        })
      }
      switchChatTab('stock')
    },
    [switchChatTab]
  )

  const openMarcheFicheForModel = useCallback((modelKey, initialTab = 'baisses') => {
    const k = String(modelKey || '')
      .trim()
      .toLowerCase()
    if (!k) return
    setMarcheInitialTab(initialTab || 'baisses')
    setMarchePendingFiche({ modelKey: k, initialTab: initialTab || 'baisses', nonce: Date.now() })
    switchChatTab('marche')
  }, [switchChatTab])

  const handleCoterDepuisStock = useCallback((moto) => {
    if (!moto) return
    const make = String(moto.make || moto.marque || moto.brand || 'yamaha').trim().toLowerCase()
    const model = String(moto.model || moto.modele || moto.titre || '').trim()
    const annee = Number(moto.annee)
    const km = Number(moto.km)
    if (!make || !model || !Number.isFinite(annee) || !Number.isFinite(km)) return
    const titre = moto.titre != null ? String(moto.titre).trim() : ''
    const version = moto.version != null ? String(moto.version).trim() : ''
    setCotationPrefillRequest({
      id: `${Date.now()}-${moto.id_source || model}`,
      make,
      model,
      annee,
      km,
      titre,
      version,
      garantie: moto.garantie === true,
      had_accident: moto.had_accident === true,
    })
    setChatTab('cotation')
  }, [])

  useEffect(() => {
    if (!authChecked) return
    if (page === 'chat' && (!token || !user)) setPage('login')
  }, [authChecked, page, token, user])

  useEffect(() => {
    if (!token || !authChecked) return
    if (window.location.pathname === '/leasing') {
      setPage('chat')
      window.history.replaceState({}, '', '/')
    }
  }, [token, authChecked])

  useEffect(() => {
    if (LOCKED_CHAT_TABS.has(chatTab)) setChatTab('stock')
  }, [chatTab])

  if (publicLeasingMatch) {
    return <PublicLeasingProposalPage proposalId={publicLeasingMatch[1]} />
  }

  if (page === 'login')
    return (
      <div>
        <style>{style}</style>
        <LoginPage
          key={loginEntryMode}
          initialMode={loginEntryMode}
          onLogin={handleLogin}
          onBack={() => setPage('landing')}
        />
      </div>
    )

  if (page === 'chat' && (!authChecked || !token || !user))
    return (
      <div className="chat-page">
        <style>{style}</style>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: 'var(--muted)' }}>
          Chargement...
        </div>
      </div>
    )

  if (page === 'chat')
    return (
      <div className="chat-page">
        <style>{style}</style>
        <div className="chat-shell">
          <div className="chat-main">
            {isMobileViewport && (
              <div className="mobile-dashboard-header">
                <a
                  href="/"
                  className="mobile-dashboard-brand"
                  aria-label="RadarAuto"
                  onClick={(e) => {
                    e.preventDefault()
                    switchChatTab('stock')
                  }}
                >
                  <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="RadarAuto" />
                </a>
                <button type="button" className="mobile-dashboard-menu-btn" aria-label="Ouvrir le menu des onglets" onClick={() => setMobileNavOpen(true)}>
                  <Menu size={18} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden />
                  Onglets
                </button>
              </div>
            )}
            <div className="chat-nav">
              <a
                href="/"
                className="chat-nav-brand"
                aria-label="RadarAuto"
                onClick={(e) => {
                  e.preventDefault()
                  switchChatTab('stock')
                }}
              >
                <img src={`${process.env.PUBLIC_URL}/logo-icon.png`} alt="RadarAuto" />
              </a>
              <div className="chat-tabs">
                <button type="button" className={`chat-tab chat-tab--home ${chatTab === 'stock' ? 'active' : ''}`} onClick={() => switchChatTab('stock')}>
                  <Package size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden />
                  Mon Stock
                </button>
                <button type="button" className={`chat-tab ${chatTab === 'cotation' ? 'active' : ''}`} onClick={() => switchChatTab('cotation')}>
                  <CircleDollarSign size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden />
                  Cotation
                </button>
                <button type="button" className={`chat-tab ${chatTab === 'marche' ? 'active' : ''}`} onClick={() => switchChatTab('marche')}>
                  <Store size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden />
                  Marché
                </button>
                <button
                  type="button"
                  className="chat-tab chat-tab--locked"
                  disabled
                  aria-disabled="true"
                  title="Bientôt disponible — simulateur leasing"
                >
                  <Calculator size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden />
                  <span style={{ whiteSpace: 'nowrap' }}>Leasing</span>
                  <Lock size={16} strokeWidth={2.25} style={{ marginLeft: 8, verticalAlign: 'text-bottom', opacity: 0.85, flexShrink: 0 }} aria-hidden />
                </button>
                <button
                  type="button"
                  className="chat-tab chat-tab--locked"
                  disabled
                  aria-disabled="true"
                  title="Bientôt disponible — analyse stratégique marché"
                >
                  <LineChart size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden />
                  <span style={{ whiteSpace: 'nowrap' }}>Analyse</span>
                  <Lock size={16} strokeWidth={2.25} style={{ marginLeft: 8, verticalAlign: 'text-bottom', opacity: 0.85, flexShrink: 0 }} aria-hidden />
                </button>
              </div>
              <div className="chat-nav-right">
                {user && (
                  <span className="chat-user-badge" style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                    <span>{user.nom || user.email}</span>
                    {user.concession ? (
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>· {user.concession}</span>
                    ) : null}
                  </span>
                )}
                <button type="button" className="settings-nav-btn" onClick={() => setShowSettings(true)}>
                  <span className="settings-nav-btn__glyph" aria-hidden>
                    <Settings size={15} strokeWidth={2} />
                  </span>
                  Réglages
                </button>
                <button type="button" onClick={handleLogout} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--muted)', fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>
                  Déconnexion
                </button>
                <div className="online">
                  <div className="online-dot" />
                  En ligne
                </div>
              </div>
            </div>
            <div className="chat-body">
              <div
                style={{
                  display: chatTab === 'cotation' ? 'flex' : 'none',
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  flexDirection: 'column',
                }}
              >
                <CotationPage
                  token={token}
                  prefs={prefs}
                  user={user}
                  prefillRequest={cotationPrefillRequest}
                  onPrefillHandled={() => setCotationPrefillRequest(null)}
                  onOpenMarcheFicheModel={openMarcheFicheForModel}
                />
              </div>
              {dashboardPanelMounted && (
                <div
                  style={{
                    display: chatTab === 'dashboard' ? 'flex' : 'none',
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    flexDirection: 'column',
                  }}
                  aria-hidden={chatTab !== 'dashboard'}
                >
                  <DashboardToday
                    token={token}
                    user={user}
                    onOpenStock={handleOpenStockFromDashboard}
                    onOpenMarche={() => {
                      setMarcheInitialTab('baisses')
                      setChatTab('marche')
                    }}
                    onOpenOpportunites={() => {
                      setMarcheInitialTab('opportunites')
                      setChatTab('marche')
                    }}
                  />
                </div>
              )}
              {marchePanelMounted && (
                <div
                  style={{
                    display: chatTab === 'marche' ? 'flex' : 'none',
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    flexDirection: 'column',
                  }}
                  aria-hidden={chatTab !== 'marche'}
                >
                  <MarchePage
                    token={token}
                    user={user}
                    initialTab={marcheInitialTab}
                    onInitialTabConsumed={() => setMarcheInitialTab('')}
                    pendingFicheRequest={marchePendingFiche}
                    onPendingFicheConsumed={() => setMarchePendingFiche(null)}
                  />
                </div>
              )}
              {stockPanelMounted && (
                <div
                  style={{
                    display: chatTab === 'stock' ? 'flex' : 'none',
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    flexDirection: 'column',
                  }}
                  aria-hidden={chatTab !== 'stock'}
                >
                  <StockPage
                    token={token}
                    user={user}
                    prefs={prefs}
                    settingsVersion={settingsVersion}
                    onCoterMoto={handleCoterDepuisStock}
                    onOpenReglages={() => setShowSettings(true)}
                    pendingOpenRequest={stockPendingOpen}
                    onPendingOpenHandled={clearStockPendingOpen}
                  />
                </div>
              )}
              {leasingPanelMounted && (
                <div
                  style={{
                    display: chatTab === 'leasing' ? 'flex' : 'none',
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    flexDirection: 'column',
                  }}
                  aria-hidden={chatTab !== 'leasing'}
                >
                  <LeasingPage
                    token={token}
                    user={user}
                    prefs={prefs}
                    prefillRequest={leasingPrefillRequest}
                    onPrefillHandled={() => setLeasingPrefillRequest(null)}
                  />
                </div>
              )}
              {analysePanelMounted && (
                <div
                  style={{
                    display: chatTab === 'analyse' ? 'flex' : 'none',
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflow: 'hidden',
                    flexDirection: 'column',
                  }}
                  aria-hidden={chatTab !== 'analyse'}
                >
                  <AnalyseTab token={token} user={user} />
                </div>
              )}
            </div>
            {isMobileViewport && mobileNavOpen && (
              <div className="mobile-nav-sheet" role="presentation" onClick={() => setMobileNavOpen(false)}>
                <div className="mobile-nav-sheet__panel" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Navigation
                    </div>
                    <button
                      type="button"
                      className="mobile-nav-action"
                      style={{ minHeight: 32, padding: '4px 10px' }}
                      onClick={() => setMobileNavOpen(false)}
                    >
                      Fermer
                    </button>
                  </div>
                  <div className="mobile-nav-section-title">Vues principales</div>
                  <div className="mobile-nav-sheet__tabs mobile-nav-sheet__tabs--primary">
                    <button type="button" className={`mobile-nav-tab ${chatTab === 'stock' ? 'active' : ''}`} onClick={() => switchChatTab('stock')}>
                      <Package size={14} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} aria-hidden />
                      Mon Stock
                    </button>
                    <button type="button" className={`mobile-nav-tab ${chatTab === 'cotation' ? 'active' : ''}`} onClick={() => switchChatTab('cotation')}>
                      <CircleDollarSign size={14} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} aria-hidden />
                      Cotation
                    </button>
                    <button type="button" className={`mobile-nav-tab ${chatTab === 'marche' ? 'active' : ''}`} onClick={() => switchChatTab('marche')}>
                      <Store size={14} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} aria-hidden />
                      Marché
                    </button>
                    <button
                      type="button"
                      className="mobile-nav-tab mobile-nav-tab--locked"
                      disabled
                      aria-disabled="true"
                      title="Bientôt disponible — simulateur leasing"
                    >
                      Leasing
                      <Lock size={16} strokeWidth={2.25} style={{ marginLeft: 8, verticalAlign: 'text-bottom', opacity: 0.85, flexShrink: 0 }} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="mobile-nav-tab mobile-nav-tab--locked"
                      disabled
                      aria-disabled="true"
                      title="Bientôt disponible — analyse stratégique marché"
                    >
                      <LineChart size={14} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} aria-hidden />
                      <span style={{ whiteSpace: 'nowrap' }}>Analyse</span>
                      <Lock size={16} strokeWidth={2.25} style={{ marginLeft: 8, verticalAlign: 'text-bottom', opacity: 0.85, flexShrink: 0 }} aria-hidden />
                    </button>
                  </div>
                  <div className="mobile-nav-section-title">Outils</div>
                  <div className="mobile-nav-sheet__actions">
                    <button
                      type="button"
                      className="mobile-nav-action"
                      onClick={() => {
                        setMobileNavOpen(false)
                        setShowSettings(true)
                      }}
                    >
                      Réglages
                    </button>
                    <button type="button" className="mobile-nav-action" onClick={handleLogout}>
                      Déconnexion
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showSettings && (
              <div className="settings-backdrop" role="presentation" onClick={() => setShowSettings(false)}>
                <div
                  className="settings-sheet"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="settings-sheet-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="settings-sheet__head">
                    <div className="settings-sheet__head-row">
                      <div>
                        <div id="settings-sheet-title" className="settings-sheet__title">
                          Réglages concession
                        </div>
                        <p className="settings-sheet__subtitle">
                          Ces paramètres affectent les calculs de marge, de reprise et la détection de concurrence sur
                          Mon stock.
                        </p>
                      </div>
                      <button type="button" className="settings-close" onClick={() => setShowSettings(false)} aria-label="Fermer">
                        <X size={20} strokeWidth={2} aria-hidden />
                      </button>
                    </div>
                  </div>

                  <div className="settings-sheet__body">
                    <section className="settings-section" aria-labelledby="settings-label-recond">
                      <h2 id="settings-label-recond" className="settings-section__label">
                        Frais de reconditionnement
                      </h2>
                      {[
                        { label: 'Véhicule < 15 000 km', key: 'frais_recond_bas' },
                        { label: 'Véhicule 15 000 – 30 000 km', key: 'frais_recond_moyen' },
                        { label: 'Véhicule > 30 000 km', key: 'frais_recond_haut' },
                      ].map((field) => (
                        <div key={field.key} className="settings-field">
                          <span className="settings-field__text">{field.label}</span>
                          <div className="settings-input-wrap">
                            <input
                              type="number"
                              className="settings-input"
                              value={prefs[field.key]}
                              onChange={(e) => setPrefs((p) => ({ ...p, [field.key]: parseInt(e.target.value, 10) || 0 }))}
                            />
                            <span className="settings-input-suffix">CHF</span>
                          </div>
                        </div>
                      ))}
                    </section>

                    <section className="settings-section" aria-labelledby="settings-label-marges">
                      <h2 id="settings-label-marges" className="settings-section__label">
                        Marges concessionnaire
                      </h2>
                      {[
                        { label: 'Marge cible (reprise standard)', key: 'marge_cible_pct' },
                        { label: 'Marge minimum acceptée', key: 'marge_min_pct' },
                      ].map((field) => (
                        <div key={field.key} className="settings-field">
                          <span className="settings-field__text">{field.label}</span>
                          <div className="settings-input-wrap">
                            <input
                              type="number"
                              className="settings-input settings-input--narrow"
                              value={prefs[field.key]}
                              onChange={(e) => setPrefs((p) => ({ ...p, [field.key]: parseInt(e.target.value, 10) || 0 }))}
                            />
                            <span className="settings-input-suffix">%</span>
                          </div>
                        </div>
                      ))}
                    </section>

                    <section className="settings-section" aria-labelledby="settings-label-stock">
                      <h2 id="settings-label-stock" className="settings-section__label">
                        Alertes stock
                      </h2>
                      <div className="settings-field">
                        <span className="settings-field__text">Seuil « stock long » (jours)</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            className="settings-input settings-input--narrow"
                            value={prefs.seuil_stock_long}
                            onChange={(e) => setPrefs((p) => ({ ...p, seuil_stock_long: parseInt(e.target.value, 10) || 0 }))}
                          />
                          <span className="settings-input-suffix">j</span>
                        </div>
                      </div>
                    </section>

                    <section className="settings-section" aria-labelledby="settings-label-conc">
                      <h2 id="settings-label-conc" className="settings-section__label">
                        Intelligence concurrentielle
                      </h2>
                      <div className="settings-field">
                        <span className="settings-field__text">Rayon après le canton</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            className="settings-input settings-input--mid"
                            value={prefs.rayon_concurrence_km}
                            onChange={(e) =>
                              setPrefs((p) => ({ ...p, rayon_concurrence_km: parseInt(e.target.value, 10) || 0 }))
                            }
                          />
                          <span className="settings-input-suffix">km</span>
                        </div>
                      </div>
                      <p className="settings-hint">
                        Priorité aux concurrents du même canton, puis extension au rayon configuré.
                      </p>
                    </section>

                    <section className="settings-section" aria-labelledby="settings-label-leasing">
                      <h2 id="settings-label-leasing" className="settings-section__label">
                        Leasing
                      </h2>
                      <div className="settings-field">
                        <span className="settings-field__text">Taux nominal par défaut</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            step="0.1"
                            className="settings-input settings-input--narrow"
                            value={prefs.leasing_taux_defaut}
                            onChange={(e) => setPrefs((p) => ({ ...p, leasing_taux_defaut: Number(e.target.value) || 0 }))}
                          />
                          <span className="settings-input-suffix">%</span>
                        </div>
                      </div>
                      <div className="settings-field">
                        <span className="settings-field__text">Plafond LCC en vigueur</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            step="0.1"
                            className="settings-input settings-input--narrow"
                            value={prefs.leasing_plafond_lcc_pct}
                            disabled={user?.plan !== 'admin'}
                            onChange={(e) => setPrefs((p) => ({ ...p, leasing_plafond_lcc_pct: Number(e.target.value) || 0 }))}
                          />
                          <span className="settings-input-suffix">%</span>
                        </div>
                      </div>
                      <p className="settings-hint">Dernière mise à jour plafond LCC : {prefs.leasing_plafond_lcc_updated_at || '—'}</p>
                      <div className="settings-field">
                        <span className="settings-field__text">Durée par défaut</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            className="settings-input settings-input--narrow"
                            value={prefs.leasing_duree_defaut_mois}
                            onChange={(e) => setPrefs((p) => ({ ...p, leasing_duree_defaut_mois: parseInt(e.target.value, 10) || 36 }))}
                          />
                          <span className="settings-input-suffix">mois</span>
                        </div>
                      </div>
                      <div className="settings-field">
                        <span className="settings-field__text">Km annuels par défaut</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            className="settings-input settings-input--mid"
                            value={prefs.leasing_km_annuels_defaut}
                            onChange={(e) => setPrefs((p) => ({ ...p, leasing_km_annuels_defaut: parseInt(e.target.value, 10) || 10000 }))}
                          />
                          <span className="settings-input-suffix">km</span>
                        </div>
                      </div>
                      <div className="settings-field">
                        <span className="settings-field__text">Acompte par défaut</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            step="0.5"
                            className="settings-input settings-input--narrow"
                            value={prefs.leasing_acompte_defaut_pct}
                            onChange={(e) => setPrefs((p) => ({ ...p, leasing_acompte_defaut_pct: Number(e.target.value) || 20 }))}
                          />
                          <span className="settings-input-suffix">%</span>
                        </div>
                      </div>
                      <div className="settings-field">
                        <span className="settings-field__text">Commission estimée (leasing)</span>
                        <div className="settings-input-wrap">
                          <input
                            type="number"
                            step="0.1"
                            className="settings-input settings-input--narrow"
                            value={prefs.leasing_commission_pct ?? 0}
                            onChange={(e) => setPrefs((p) => ({ ...p, leasing_commission_pct: Number(e.target.value) || 0 }))}
                          />
                          <span className="settings-input-suffix">%</span>
                        </div>
                      </div>
                      <p className="settings-hint">Affichée sur la page Leasing (bloc concessionnaire) si la valeur est supérieure à 0 · indicatif sur la base des intérêts simulés.</p>
                    </section>

                  </div>

                  <div className="settings-sheet__foot">
                    <button
                      type="button"
                      className="settings-save"
                      onClick={async () => {
                        await fetchWithTimeout(`${BACKEND}/api/preferences`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                          },
                          body: JSON.stringify(prefs),
                        })
                        setSettingsVersion((v) => v + 1)
                        setShowSettings(false)
                      }}
                    >
                      Sauvegarder les réglages
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )

  return (
    <>
      <style>{style}</style>
      <LandingPage onConnect={goPlatform} onRequestAccess={goRegister} />
    </>
  )
}





